import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import DashboardPA from './screens/DashboardPA';
import CreateCohortWizard from './screens/CreateCohortWizard';
import { TIME_SLOTS, DAYS_OF_WEEK, getDayOfWeek, getWeekLabel, slotsMatch, formatSlotForApi } from './utils/sessionUtils';
import SessionForm from './screens/SessionForm';
import SessionDetail from './screens/SessionDetail';
import AttendanceOverview from './screens/AttendanceOverview';
import ManageAssignmentsModal from './screens/ManageAssignmentsModal';
import CohortDetail from './screens/CohortDetail';
import SchoolsList from './screens/SchoolsList';
import SchoolView from './screens/SchoolView';
import YearLevelBadge from './components/YearLevelBadge';
import ResourcesViewer from './screens/ResourcesViewer';
import { filterResourceIdsByCouncil } from './utils/resourceFilters';
// eslint-disable-next-line no-unused-vars
import { DashboardSPOC, MyCohortsSPOC, NominationsSPOC } from './screens/ScreensSPOC';
import Year2Portal from './screens/Year2/Year2Portal';
import Year2DisabledMessage from './screens/Year2/Year2DisabledMessage';
import Year2NominationsSPOC from './screens/Year2/Year2NominationsSPOC';
import Year2SessionView from './screens/Year2/Year2SessionView';
import EsgSquadFormation from './screens/Esg/EsgSquadFormation';
import EsgEvaluation from './screens/Esg/EsgEvaluation';
import EsgRatings from './screens/Esg/EsgRatings';
import EsgSupportInbox from './screens/Esg/EsgSupportInbox';
import EsgCertificates from './screens/Esg/EsgCertificates';
import EsgTags from './screens/Esg/EsgTags';
import EsgSquadDetail from './screens/Esg/EsgSquadDetail';
import EsgSquadsList from './screens/Esg/EsgSquadsList';
import { loadYear2Discovery as fetchYear2Discovery } from './api/year2Discovery';
import {
  councilNominationsComplete,
  councilNumberFromSession,
  hoursUntilSession,
  isCouncilNominationEditable,
  isCouncilSession,
  isOrientationSession,
} from './utils/year2Nominations';
import * as mentorApi from './api/mentorApi';
import * as mentorRequestLog from './api/mentorRequestLog';
import { APPROVALS_EVAL_QUERY_KEY, YEAR2_APPROVALS_QUERY_KEY, clearAllQueryCache, invalidateQuery, seedMentorQuery, useMentorQuery } from './api/mentorQuery';
import ApiActivityLog from './screens/ApiActivityLog';
import ReportsInsights from './screens/ReportsInsights';
import ActionItemsStrip from './components/ActionItemsStrip';
import ProfileModal from './components/ProfileModal';
import { mapStudentFromApi, resolveStudent, resolveStudents, rosterHasStudents, rosterKey } from './utils/studentResolution';
import { enrichSessionSchoolName } from './utils/sessionSchoolName';
import { shouldAutoRefreshPeopleRoster } from './utils/peopleRosterTtl';
import { withSchoolBranchContext } from './utils/schoolBranchOverrides';
import {
  buildReportsFromEvaluations,
  fetchEvaluationsReports,
  fetchWorkspaceBootStreaming,
  markWorkspaceFetchLoading,
  mergeReopenResponsesIntoReports,
  syncWorkspaceCohort,
  syncWorkspaceRoster,
  syncWorkspaceSession,
} from './workspace/actions/loadWorkspace';
import { isActiveSessionStatus } from './workspace/bootContract';
import { mergeCohortsForDisplay, mergeSessionsForDisplay } from './workspace/bootFallbacks';
import { buildBootTelemetryPayload, emitBootTelemetry } from './workspace/bootTelemetry';
import {
  projectAuditorStatusFromSession,
  buildEvaluationChallengerSlots,
  evaluationChallengersAreEmpty,
  resolveNominationOutcomeForCohort,
} from './workspace/projectSession';
import { useWorkspace } from './workspace/WorkspaceContext';
import {
  filterAuditorPastSessions,
  filterAuditorUpcomingSessions,
} from './workspace/auditorSessions';
import {
  buildAuditorSessionStatusesMap,
  buildSchoolCohortsMap,
  buildSessionRolesMap,
  buildSessionRoomAssignmentsMap,
  getAllRosterStudents,
  getAllSessions,
  getSessionRolesFromMap,
  getStudentsForSchoolBranch,
  rosterHasSchoolBranch,
  getSessionById,
} from './workspace/selectors';
import { WORKSPACE_ACTIONS } from './workspace/workspaceReducer';

function titleCaseWord(value) {
  if (!value) return '';
  return String(value)
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function normalizeYear2QueueItem(item) {
  const type = item.assessment_type || item.type || 'squad';
  const status = item.status === 'submitted' ? 'pending' : (item.status || 'pending');
  const roleLabel = type === 'squad' ? 'Squad' : type === 'deputy' ? 'Co-chair' : 'Chair';
  return {
    ...item,
    uiId: `year2:${type}:${item.id}`,
    status,
    type,
    roleLabel,
    title: type === 'squad'
      ? (item.squad_name || `Squad ${item.squad_id || ''}`.trim())
      : (item.student_name || `Student ${item.student_id || ''}`.trim()),
    sessionName: [item.session_type || item.sessionType, item.squad_name, item.council_number ? `Council ${item.council_number}` : null]
      .filter(Boolean)
      .join(' · '),
  };
}

function normalizeYear2StudentLogItem(item) {
  const role = item.role || item.assessment_role || item.assessment_type || 'squad';
  const label = role === 'deputy' ? 'Year 2 Co-chair' : role === 'chair' ? 'Year 2 Chair' : 'Year 2 Squad';
  return {
    ...item,
    id: `year2-${item.id}`,
    role,
    roleBadge: item.squad_name && role === 'squad' ? `${label}: ${item.squad_name}` : label,
    sessionType: item.session_type || item.sessionType || (item.council_number ? `Year 2 Council ${item.council_number}` : 'Year 2'),
    approvedAt: item.approved_at || item.approvedAt,
    approvedByName: item.approved_by_name || item.approvedByName,
    auditorName: item.auditor_name || item.auditorName,
    rating: item.rating,
    comment: item.feedback_note || item.comment || item.evidence_note || '',
    positiveKeywords: item.strengths_json || item.positiveKeywords || [],
    negativeKeywords: item.coaching_json || item.negativeKeywords || [],
    isRevision: item.is_revision ?? item.isRevision,
    revisionNumber: item.revision_number ?? item.revisionNumber,
  };
}

// Clear any stale resource cache left over from the previous cache-first implementation.
localStorage.removeItem('mentorResourcesCache');
localStorage.removeItem('mentorStorageVersion');

function formatApiError(err, actionLabel) {
  if (Number(err?.status) === 429) {
    return `${actionLabel} was rate-limited (HTTP 429). Wait about a minute, then refresh once — avoid rapid reloads.`;
  }
  const networkFailure = !err?.status && /failed to fetch|networkerror|load failed|network request failed/i.test(String(err?.message || ''));
  if (networkFailure) {
    const target = String(err?.baseUrl || '').includes('mentor-api') ? 'mentor API' : 'IFSPD API';
    return `${actionLabel} could not reach the ${target}. Wait a few seconds and refresh once.`;
  }
  const statusPart = err?.status ? `HTTP ${err.status}` : 'HTTP unknown';
  const endpointPart = err?.endpoint ? ` ${err.endpoint}` : '';
  const responseMessage = err?.data?.message || err?.message || 'API error';
  const validationDetails = err?.data?.errors ? ` | details=${JSON.stringify(err.data.errors)}` : '';
  return `${actionLabel} failed${endpointPart} (${statusPart}): ${responseMessage}${validationDetails}`;
}

// ids are the URL slug under #/esg/{id} (e.g. #/esg/squad-formation) - not
// flat top-level tab ids, unlike every other nav item's id.
const ESG_SQUAD_SCREENS = [
  { id: 'squads', label: 'ESG Squads' },
  { id: 'squad-formation', label: 'New Creation' },
];
const ESG_EVALUATION_SCREEN = { id: 'evaluation', label: 'Evaluation' };
const ESG_RATINGS_SCREEN = { id: 'ratings', label: 'Ratings' };
// Council-related messages from students (T7): a PA and the squad's assigned auditors answer them (not SPOCs).
const ESG_SUPPORT_SCREEN = { id: 'support', label: 'Support Inbox' };
// Certificates are approved by a PA, the squad's own SPOC or an assigned auditor.
const ESG_CERTIFICATES_SCREEN = { id: 'certificates', label: 'Certificates' };
// Auditors only evaluate (they cannot manage squads). An evaluation is final once submitted: there is no approval screen.
const ESG_SUBMENU = [...ESG_SQUAD_SCREENS, ESG_EVALUATION_SCREEN, ESG_RATINGS_SCREEN, ESG_CERTIFICATES_SCREEN];
const ESG_PA_SUBMENU = [...ESG_SQUAD_SCREENS, ESG_EVALUATION_SCREEN, ESG_RATINGS_SCREEN, ESG_CERTIFICATES_SCREEN, ESG_SUPPORT_SCREEN, { id: 'tags', label: 'ESG Options' }];
// An auditor's ESG Squads lists only the squads assigned to them.
const ESG_AUDITOR_SUBMENU = [ESG_SQUAD_SCREENS[0], ESG_EVALUATION_SCREEN, ESG_RATINGS_SCREEN, ESG_CERTIFICATES_SCREEN, ESG_SUPPORT_SCREEN];
const ESG_DEFAULT_SCREEN = { program_administrator: 'squad-formation', spoc: 'squad-formation', auditor: 'evaluation' };
// 'squad' is the detail page (#/esg/squad/{squadId}) - reachable from a link, not listed in the menu.
const ESG_ALLOWED_SCREENS = {
  program_administrator: [...ESG_PA_SUBMENU.map((c) => c.id), 'squad'],
  spoc: [...ESG_SUBMENU.map((c) => c.id), 'squad'],
  auditor: [...ESG_AUDITOR_SUBMENU.map((c) => c.id), 'squad'],
};

const NAVIGATION_CONFIG = {
  program_administrator: [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'schools', label: 'Schools', icon: '🏫' },
    { id: 'cohorts', label: 'Cohorts', icon: '👥' },
    { id: 'sessions', label: 'Sessions', icon: '📅' },
    { id: 'attendance', label: 'Attendance', icon: '🗓️' },
    { id: 'auditors', label: 'Auditors', icon: '🔍' },
    { id: 'approvals', label: 'Year 1 Evaluation Approvals', icon: '✓' },
    { id: 'year2_approvals', label: 'Year 2 Evaluation Approvals', icon: '✓' },
    { id: 'year2', label: 'Year 2 Portal', icon: '🏛' },
    { id: 'esg', label: 'ESG Specialist', icon: '🌍', children: ESG_PA_SUBMENU },
    { id: 'api_activity_log', label: 'API Activity Log', icon: '📡' },
    { id: 'reports_insights', label: 'Reports & Insights', icon: '📈' },
    { id: 'resources', label: 'Learning Space', icon: '📚' },
  ],
  // SPOC intentionally excluded from 'reports_insights' — SPOCs are school staff, outside
  // IFSPD's own ecosystem, and are never a scored role in Reports & Insights. Do not add here.
  spoc: [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'my_cohorts', label: 'My Cohorts', icon: '👥' },
    { id: 'attendance', label: 'Attendance', icon: '🗓️' },
    { id: 'nominations', label: 'Year 1 Role Nominations', icon: '📝' },
    { id: 'year2_nominations', label: 'Year 2 Role Nominations', icon: '📝' },
    { id: 'year2', label: 'Year 2 Portal', icon: '🏛' },
    { id: 'esg', label: 'ESG Specialist', icon: '🌍', children: ESG_SUBMENU },
    { id: 'resources', label: 'Learning Space', icon: '📚' }
  ],
  counselor: [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'sessions', label: 'Sessions', icon: '📅' },
    { id: 'attendance', label: 'Attendance', icon: '🗓️' },
    { id: 'approvals', label: 'Year 1 Evaluation Approvals', icon: '✓' },
    { id: 'year2_approvals', label: 'Year 2 Evaluation Approvals', icon: '✓' },
    { id: 'reports_insights', label: 'Reports & Insights', icon: '📈' },
    { id: 'resources', label: 'Learning Space', icon: '📚' }
  ],
  auditor: [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'my_sessions_y1', label: 'Year 1 Sessions', icon: '📅' },
    { id: 'my_sessions_y2', label: 'Year 2 Sessions', icon: '📅' },
    { id: 'attendance', label: 'Attendance', icon: '🗓️' },
    { id: 'nominations', label: 'Year 1 Role Nominations', icon: '📝' },
    { id: 'year2_nominations', label: 'Year 2 Role Nominations', icon: '📝' },
    { id: 'availability', label: 'Availability', icon: '⏳' },
    { id: 'esg', label: 'ESG Specialist', icon: '🌍', children: ESG_AUDITOR_SUBMENU },
    { id: 'reports_insights', label: 'Reports & Insights', icon: '📈' },
    { id: 'resources', label: 'Learning Space', icon: '📚' }
  ]
};

const ROLE_KEYWORDS = {
  chair: {
    strengths: ['Leadership', 'Facilitative', 'Respectful', 'Inclusive', 'Diplomatic', 'Process-Oriented', 'Time-Conscious', 'Decisive', 'Conflict Resolution', 'Composed'],
    coaching: ['Harsh', 'Disorganised', 'Dominating', 'Exclusive', 'Impatient', 'Distracted', 'Unprepared', 'Indecisive', 'Reactive', 'Inconsistent']
  },
  presenter: {
    strengths: ['Confident', 'Articulate', 'Followed IRAC', 'Persuasive', 'Structured', 'Insightful', 'Engaging', 'Prepared', 'Composed', 'Influential'],
    coaching: ['Hesitant', 'Unclear', 'Superficial', 'Unconvincing', 'Disorganised', "Didn't follow IRAC", 'Monotonous', 'Underprepared', 'Nervous', 'Passive']
  },
  mentor: {
    strengths: ['Supportive', 'Empathetic', 'Encouraging', 'Observant', 'Constructive', 'Thoughtful', 'Insightful', 'Collaborative', 'Respectful', 'Improvement-Oriented'],
    coaching: ['Listening', 'Empathy', 'Specificity', 'Observation', 'Confidence', 'Structure', 'Depth', 'Diplomacy', 'Objectivity', 'Harsh']
  },
  challenger: {
    strengths: ['Analytical', 'Inquisitive', 'Insightful', 'Perceptive', 'Critical', 'Diplomatic', 'Precise', 'Courageous', 'Engaged', 'Reflective'],
    coaching: ['Listening', 'Confidence', 'Evidence', 'Depth', 'Precision', 'Relevance', 'Diplomacy', 'Spontaneity', 'Critical Thinking', 'Question Framing']
  }
};

function App() {
  // Authentication & Session States
  const parseHash = () => {
    const h = window.location.hash.replace(/^#\/?/, '');
    const parts = h.split('/');
    const rawTab = parts[0] === 'year2-approvals' ? 'year2_approvals' : (parts[0] || 'dashboard');
    // cohortId/detailTab aliases kept for existing callers that destructure those names
    const entityId = parts[1] || null;
    const subView = parts[2] || null;
    const subEntityId = parts[3] || null;
    return { tab: rawTab, cohortId: entityId, detailTab: subView, entityId, subView, subEntityId };
  };
  const [authState, setAuthState] = useState(() => sessionStorage.getItem('mentor_auth') || 'logged_out');
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('mentor_user')); } catch { return null; }
  });
  const [activeTab, setActiveTab] = useState(() => {
    const stored = sessionStorage.getItem('mentor_auth');
    return stored === 'logged_in' ? parseHash().tab : 'dashboard';
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileOverlay, setProfileOverlay] = useState(null);
  // Sidebar nav groups with sub-items (currently just 'esg') - which are expanded.
  const [expandedNavGroups, setExpandedNavGroups] = useState(() => new Set());
  // Which ESG sub-screen is active (#/esg/{screen}) - activeTab stays 'esg'
  // for the whole group, this tracks the specific one, same pattern as
  // selectedCohort/selectedSession for other grouped/detail routes.
  const [esgActiveScreen, setEsgActiveScreen] = useState('squad-formation');
  // Squad opened on the detail page (#/esg/squad/{squadId}).
  const [esgSquadId, setEsgSquadId] = useState(() => {
    const parts = window.location.hash.replace(/^#\/?/, '').split('/');
    return parts[0] === 'esg' && parts[1] === 'squad' && Number(parts[2]) > 0 ? Number(parts[2]) : null;
  });
  // A screen the role can't open (stale hash, or the default before a role loads) falls back to that role's landing screen.
  const esgScreen = (ESG_ALLOWED_SCREENS[currentUser?.role] || []).includes(esgActiveScreen)
    ? esgActiveScreen
    : (ESG_DEFAULT_SCREEN[currentUser?.role] || 'squad-formation');

  // Input states for Login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // True from the click on Authenticate until the API has answered, so the button cannot be pressed twice.
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // SSO handoff landing: portahead redirects here (/sso?code=...) after a
  // successful mentor login there, so this app never shows its own login
  // form for that flow. 'pending' while the code is being redeemed,
  // 'error' if redemption fails, null otherwise (normal app flow).
  const [ssoState, setSsoState] = useState(() => (window.location.pathname === '/sso' ? 'pending' : null));
  const [ssoErrorMsg, setSsoErrorMsg] = useState('');
  // React 18 StrictMode double-invokes effects in development. Reading the
  // code and stripping it from the URL is a one-time side effect -- the
  // second invocation would find the URL already stripped by the first and
  // wrongly report a missing code. This ref makes the body run exactly once.
  const ssoHasRunRef = useRef(false);

  const { state: workspaceState, dispatch: workspaceDispatch } = useWorkspace();

  const cohortsFetch = workspaceState.fetch.cohorts;
  const sessionsFetch = workspaceState.fetch.sessions;
  const evaluationsFetch = workspaceState.fetch.evaluations;

  const schoolCohortsMap = useMemo(
    () => buildSchoolCohortsMap(workspaceState),
    [workspaceState]
  );

  const [reports, setReports] = useState([]);
  const [year2ApprovalItems, setYear2ApprovalItems] = useState([]);

  // Live API data — schools and resources loaded from Ritika's API after login
  const [liveSchools, setLiveSchools] = useState([]);
  const liveStudents = useMemo(
    () => getAllRosterStudents(workspaceState),
    [workspaceState]
  );

  const allCohortsForDisplay = useMemo(() => {
    const merged = mergeCohortsForDisplay(schoolCohortsMap, [], cohortsFetch);
    return merged.map(cohort => {
      const school = liveSchools.find(item => String(item.id) === String(cohort.schoolId));
      return {
        ...cohort,
        schoolName: school?.school_name || school?.name || cohort.schoolName || '',
      };
    });
  }, [schoolCohortsMap, liveSchools, cohortsFetch]);

  const sessions = useMemo(() => {
    const workspaceSessions = getAllSessions(workspaceState);
    const base = mergeSessionsForDisplay([], sessionsFetch, workspaceSessions);
    return base.map(s => enrichSessionSchoolName(s, liveSchools, allCohortsForDisplay));
  }, [workspaceState, sessionsFetch, liveSchools, allCohortsForDisplay]);
  // Student Profile Drawer state
  const [selectedStudentForDrawer, setSelectedStudentForDrawer] = useState(null);
  const [studentDrawerEvaluations, setStudentDrawerEvaluations] = useState([]);
  const [loadingStudentDrawerEvaluations, setLoadingStudentDrawerEvaluations] = useState(false);
  const [liveBranches, setLiveBranches] = useState([]);
  const [liveResources, setLiveResources] = useState([]);
  const [resourceLoadState, setResourceLoadState] = useState('idle');
  const [liveCounselors, setLiveCounselors] = useState([]);
  const [liveAuditors, setLiveAuditors] = useState([]);
  const [liveTeachers, setLiveTeachers] = useState([]);
  const peopleRosterFetchedAtRef = useRef(0);
  const peopleRosterFetchInFlightRef = useRef(null);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const activeAuditors = liveAuditors;
  const activeCounselors = liveCounselors;
  const activeSpocs = liveTeachers;

  const loadLiveResources = useCallback(async () => {
    setResourceLoadState('loading');
    try {
      const items = await mentorApi.getAllResources();
      setLiveResources(mentorApi.normalizeResources(items));
      setResourceLoadState('ready');
      return items;
    } catch (err) {
      setResourceLoadState('error');
      console.warn('Resources unavailable for this role:', err);
      return [];
    }
  }, []);
  // eslint-disable-next-line no-unused-vars
  const [apiLoading, setApiLoading] = useState(false);

  // Auditor workflow state variables
  const [evaluatingSessionId, setEvaluatingSessionId] = useState(null);
  const [evaluatingCohortId, setEvaluatingCohortId] = useState('');
  // Setter-only cache for Year 2 auditor squads.
  const [, setYear2Squads] = useState([]);
  const auditorSessionStatuses = useMemo(
    () => buildAuditorSessionStatusesMap(workspaceState),
    [workspaceState]
  );
  const [sessionEvaluations, setSessionEvaluations] = useState({});
  const [myAvailability, setMyAvailability] = useState(() => {
    const initial = {};
    DAYS_OF_WEEK.forEach(day => {
      initial[day] = {};
      TIME_SLOTS.forEach(slot => {
        initial[day][slot] = false;
      });
    });
    return initial;
  });
  const [showWithdrawConfirmId, setShowWithdrawConfirmId] = useState(null);
  const [viewingPastEvaluationId, setViewingPastEvaluationId] = useState(null);
  const [auditorSessionsTab, setAuditorSessionsTab] = useState('upcoming');
  const [auditorDashboardFilter, setAuditorDashboardFilter] = useState('all');
  const [openDropdownIdx, setOpenDropdownIdx] = useState(null);

  // Approvals view states
  const [activeApprovalTab, setActiveApprovalTab] = useState('pending');
  const [expandedEvaluations, setExpandedEvaluations] = useState([]);
  const [rejectionInputId, setRejectionInputId] = useState(null);
  const [rejectionReasonText, setRejectionReasonText] = useState('');

  // Counselor workflow states
  const [counselorSessionsTab, setCounselorSessionsTab] = useState('today');
  const [selectedCounselorSessionId, setSelectedCounselorSessionId] = useState(null);
  const sessionRoomAssignments = useMemo(
    () => buildSessionRoomAssignmentsMap(workspaceState),
    [workspaceState]
  );
  const [sessionNoShows, setSessionNoShows] = useState({});
  const [showNoShowConfirm, setShowNoShowConfirm] = useState(null);

  // Approvals Queue Filters
  const [selectedCohortFilter, setSelectedCohortFilter] = useState('all');
  const [selectedSessionFilter, setSelectedSessionFilter] = useState('all');
  const [selectedAuditorFilter, setSelectedAuditorFilter] = useState('all');
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);

  // Schools module state — sourced from the API and kept only in-memory
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolSyncInfo, setSchoolSyncInfo] = useState({});
  const [schoolGroupsMap, setSchoolGroupsMap] = useState({});

  // Active Screen states for PA Workflow
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [cohortDetailTab, setCohortDetailTab] = useState('students');
  const [spocPickerOpen, setSpocPickerOpen] = useState(false);

  // Wizard creation states (null if not active)
  const [wizardState, setWizardState] = useState(null);
  const [wizardSearchText, setWizardSearchText] = useState('');
  const [highlightedStudentId, setHighlightedStudentId] = useState(null);
  
  // Session Form states (null if not active)
  const [sessionFormState, setSessionFormState] = useState(null);
  const [sessionFormSubmitting, setSessionFormSubmitting] = useState(false);
  const [sessionFormClashError, setSessionFormClashError] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showManageAssignments, setShowManageAssignments] = useState(false);
  const [auditorRosterSearch, setAuditorRosterSearch] = useState('');
  const [auditorRosterFilter, setAuditorRosterFilter] = useState('all');
  const [selectedAuditorRosterId, setSelectedAuditorRosterId] = useState(null);
  /** Fellowship Sessions list status filter: all | planned | live | completed | cancelled */
  const [sessionListStatusFilter, setSessionListStatusFilter] = useState('all');

  const handleOpenManageAssignments = () => {
    setShowManageAssignments(true);
  };

  // =========================================================
  // SPOC WORKFLOW STATE VARIABLES
  // =========================================================
  const sessionRoles = useMemo(
    () => buildSessionRolesMap(workspaceState),
    [workspaceState]
  );
  const [spocTasks, setSpocTasks] = useState([]);

  // Screen 3: Outcomes Form input states
  const [outcomeFormSessionId, setOutcomeFormSessionId] = useState(null);
  const [outcomeFormCohortId, setOutcomeFormCohortId] = useState('');
  const [outcomeChallenger1, setOutcomeChallenger1] = useState('');
  const [outcomeChallenger2, setOutcomeChallenger2] = useState('');
  const [outcomeChallenger3, setOutcomeChallenger3] = useState('');
  const [outcomeChallenger4, setOutcomeChallenger4] = useState('');
  const [show4thChallenger, setShow4thChallenger] = useState(false);
  const [outcomeBestPresenter, setOutcomeBestPresenter] = useState('');
  const [outcomeBestMentor, setOutcomeBestMentor] = useState('');
  const [outcomeBestChallenger, setOutcomeBestChallenger] = useState('');
  const [outcomeChairScore, setOutcomeChairScore] = useState('');

  // Screen 4: Nominations Form input states
  const [nominationsCohortId, setNominationsCohortId] = useState('');
  const [year2NominationsCohortId, setYear2NominationsCohortId] = useState('');
  const [year2Discovery, setYear2Discovery] = useState(null);
  const [year2DiscoveryError, setYear2DiscoveryError] = useState('');
  const [year2DiscoveryLoading, setYear2DiscoveryLoading] = useState(false);
  const [nomChairId, setNomChairId] = useState('');
  const [nomPresenter1, setNomPresenter1] = useState('');
  const [nomPresenter2, setNomPresenter2] = useState('');
  const [nomPresenter3, setNomPresenter3] = useState('');
  const [nomPresenter4, setNomPresenter4] = useState('');
  const [show4thPresenter, setShow4thPresenter] = useState(false);
  const [nomMentor1, setNomMentor1] = useState('');
  const [nomMentor2, setNomMentor2] = useState('');
  const [nomMentor3, setNomMentor3] = useState('');
  const [nomMentor4, setNomMentor4] = useState('');
  const [show4thMentor, setShow4thMentor] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [bootWarnings, setBootWarnings] = useState([]);
  const [bootPartial, setBootPartial] = useState(false);
  const [bootBannerDismissed, setBootBannerDismissed] = useState(false);
  const workspaceBootInFlightRef = useRef(null);
  const year2DiscoveryInFlightRef = useRef(null);

  const year2DiscoveryCohorts = useMemo(
    () => (year2Discovery?.cohortsBySchool
      ? Object.values(year2Discovery.cohortsBySchool).flat()
      : null),
    [year2Discovery]
  );
  const year2DiscoveryBootstrapPending = currentUser?.role === 'program_administrator' || currentUser?.role === 'spoc' || currentUser?.role === 'auditor'
    ? !year2Discovery && !year2DiscoveryError
    : false;

  // BUG-mentor-aud-availability-01: useMentorQuery for auditor availability.
  // Seeded from boot phase1 data; handles re-fetch on tab re-mount with 120s stale TTL.
  const availabilityQuery = useMentorQuery(
    ['mentor', 'auditor', 'availability'],
    () => mentorApi.getAuditorAvailability(),
    {
      enabled: currentUser?.role === 'auditor',
      staleTimeMs: 120_000,
      caller: 'tab:availability',
    }
  );

  // Sync useMentorQuery availability data into myAvailability grid state.
  // Query now returns a day×slot grid (Ritika day_id contract), not a flat slot list.
  useEffect(() => {
    if (!availabilityQuery.data) return;
    const grid = availabilityQuery.data;
    if (typeof grid !== 'object' || Array.isArray(grid)) return;
    setMyAvailability(grid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityQuery.data]);

  const loadsApprovalsQuery = currentUser?.role === 'program_administrator' || currentUser?.role === 'counselor';

  // BUG-mentor-counselor-approval-stale-01: shared evaluations cache for PA + counselor.
  // Seeded from boot; revalidates on Approvals tab open and every 60s while tab is active.
  const approvalsEvalQuery = useMentorQuery(
    APPROVALS_EVAL_QUERY_KEY,
    () => {
      mentorRequestLog.setCallerTag('tab:approvals-query');
      return mentorApi.getEvaluations();
    },
    {
      enabled: loadsApprovalsQuery,
      staleTimeMs: 60_000,
      caller: 'tab:approvals',
    }
  );

  const year2ApprovalsQuery = useMentorQuery(
    YEAR2_APPROVALS_QUERY_KEY,
    async () => {
      mentorRequestLog.setCallerTag('tab:year2-approvals-query');
      const [submitted, approved, rejected] = await Promise.all([
        mentorApi.getYear2EvaluationsQueue({ status: 'submitted' }),
        mentorApi.getYear2EvaluationsQueue({ status: 'approved' }),
        mentorApi.getYear2EvaluationsQueue({ status: 'rejected' }),
      ]);
      return [...submitted, ...approved, ...rejected].map(normalizeYear2QueueItem);
    },
    {
      enabled: loadsApprovalsQuery,
      staleTimeMs: 60_000,
      caller: 'tab:year2-approvals',
    }
  );

  // Sync query results into reports; approve/reject handlers optimistically update reports.
  useEffect(() => {
    if (!loadsApprovalsQuery || !approvalsEvalQuery.data) return;
    setReports(buildReportsFromEvaluations(approvalsEvalQuery.data, sessions));
  }, [loadsApprovalsQuery, approvalsEvalQuery.data, sessions]);

  useEffect(() => {
    if (!loadsApprovalsQuery || !year2ApprovalsQuery.data) return;
    setYear2ApprovalItems(year2ApprovalsQuery.data);
  }, [loadsApprovalsQuery, year2ApprovalsQuery.data]);

  useEffect(() => {
    if (activeTab !== 'approvals' || !loadsApprovalsQuery) return;

    mentorRequestLog.setCallerTag('tab:approvals');
    approvalsEvalQuery.refetch();

    const pollId = setInterval(() => {
      mentorRequestLog.setCallerTag('tab:approvals-poll');
      approvalsEvalQuery.refetch();
    }, 60_000);

    return () => clearInterval(pollId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loadsApprovalsQuery]);

  useEffect(() => {
    if (activeTab !== 'year2_approvals' || !loadsApprovalsQuery) return;

    mentorRequestLog.setCallerTag('tab:year2-approvals');
    year2ApprovalsQuery.refetch();

    const pollId = setInterval(() => {
      mentorRequestLog.setCallerTag('tab:year2-approvals-poll');
      year2ApprovalsQuery.refetch();
    }, 60_000);

    return () => clearInterval(pollId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loadsApprovalsQuery]);

  // Persist auth state to sessionStorage
  useEffect(() => {
    if (authState === 'logged_in') {
      sessionStorage.setItem('mentor_auth', 'logged_in');
    } else if (authState === 'logged_out') {
      sessionStorage.removeItem('mentor_auth');
      sessionStorage.removeItem('mentor_user');
    }
  }, [authState]);

  useEffect(() => {
    if (currentUser) {
      sessionStorage.setItem('mentor_user', JSON.stringify(currentUser));
      mentorApi.getMyProfile()
        .then(setProfileOverlay)
        .catch(() => setProfileOverlay(null));
    } else {
      setProfileOverlay(null);
    }
  }, [currentUser]);

  // Fetch counselors + auditors globally (school/branch restriction removed by Ritika 2026-06-10).
  // Cache: show last list immediately; auto-refetch on Auditors / Plan Session / Manage Assignments
  // if older than 5 minutes; hard-expire 24h; Refresh button always fetches (force).
  const fetchLiveCounselorsAndAuditors = useCallback(async (userRole = null) => {
    const role = userRole || currentUser?.role;
    if (peopleRosterFetchInFlightRef.current) {
      return peopleRosterFetchInFlightRef.current;
    }
    const run = (async () => {
      try {
        const [rawCounselors, rawAuditors] = await Promise.all([
          role === 'auditor' ? Promise.resolve([]) : mentorApi.getCounselors(),
          // Auditors cannot list other auditors — Ritika's API returns 403
          role === 'auditor' ? Promise.resolve([]) : mentorApi.getAuditors(),
        ]);
        setLiveCounselors(mentorApi.normalizeCounselors(rawCounselors));
        setLiveAuditors(mentorApi.normalizeAuditors(rawAuditors));
        peopleRosterFetchedAtRef.current = Date.now();
      } catch (err) {
        // Keep last cached roster; lazy refresh must not toast-spam on tab open.
        console.error('Failed to load counselors/auditors:', err);
      } finally {
        peopleRosterFetchInFlightRef.current = null;
      }
    })();
    peopleRosterFetchInFlightRef.current = run;
    return run;
  }, [currentUser?.role]);

  const ensurePeopleRosterFresh = useCallback((opts = {}) => {
    if (!shouldAutoRefreshPeopleRoster(peopleRosterFetchedAtRef.current, opts)) {
      return Promise.resolve();
    }
    return fetchLiveCounselorsAndAuditors();
  }, [fetchLiveCounselorsAndAuditors]);

  const loadMentorWorkspaceData = async (userOverride = null, { loadedRosterKeys = [] } = {}) => {
    if (workspaceBootInFlightRef.current) {
      return workspaceBootInFlightRef.current;
    }

    const user = userOverride || currentUser;
    const role = user?.role;
    const bootPromise = (async () => {
      const bootStartedAt = typeof performance !== 'undefined' && performance.now
        ? performance.now()
        : Date.now();
      try {
        setApiLoading(true);
        setBootBannerDismissed(false);
        const loadsEvaluations = role === 'program_administrator' || role === 'counselor' || role === 'auditor';
        markWorkspaceFetchLoading(workspaceDispatch, { loadsEvaluations });

        let phase1BootData = null;
        let phase2BootData = null;

        mentorRequestLog.setCallerTag('boot:phase1');
        await fetchWorkspaceBootStreaming({
          user,
          formatApiError,
          // ESG pages never use the Glosco school rosters; screens that need one load it on demand.
          skipRosterPrefetch: /^#\/?esg(\/|$)/.test(window.location.hash),
          // Rosters the login bootstrap already fetched (a scoped user's own school) are not fetched again.
          loadedRosterKeys,
          onPhase1: async (phase1) => {
            phase1BootData = phase1;
            setBootWarnings(phase1.bootWarnings || []);
            setBootPartial(Boolean(phase1.partial));
            if ((role === 'program_administrator' || role === 'counselor') && Array.isArray(phase1.evaluations)) {
              seedMentorQuery(APPROVALS_EVAL_QUERY_KEY, phase1.evaluations);
            }
            setReports(phase1.reportsNext || []);
            setApiLoading(false);

            // BUG-mentor-aud-availability-01: availability fetch moved to useMentorQuery
            // at the App level. The boot no longer calls getAuditorAvailability directly —
            // useMentorQuery handles it with cache-first, stale-while-revalidate semantics.
            // The query result is synced to myAvailability via the useEffect above.
            // This eliminates the duplicate fetch that happened when both boot and the
            // Availability tab's mount triggered the same endpoint.
          },
          onPhase2: (phase2) => {
            mentorRequestLog.setCallerTag('boot:phase2');
            phase2BootData = phase2;
            workspaceDispatch({
              type: WORKSPACE_ACTIONS.BOOT_ENTITIES,
              payload: {
                sessions: phase2.normalizedSessions || [],
                cohorts: phase2.normalizedCohortEntities || [],
                evaluations: phase2.normalizedEvaluations || [],
                rosters: phase2.rosters || {},
              },
            });
            setSchoolGroupsMap(phase2.groupsBySchool || {});
            setReports(phase2.reportsNext || []);
            setBootWarnings(phase2.bootWarnings || []);
            setBootPartial(Boolean(phase2.partial));

            const combinedBootData = {
              ...(phase1BootData || {}),
              ...(phase2BootData || {}),
              durationMs: phase2.durationMs ?? ((typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()) - bootStartedAt),
              bootWarnings: phase2.bootWarnings || phase1BootData?.bootWarnings || [],
            };
            emitBootTelemetry(buildBootTelemetryPayload(combinedBootData, {
              role,
              durationMs: combinedBootData.durationMs,
            }));
          },
        });
      } catch (err) {
        console.error('Failed to load mentor workspace data:', err);
        setBootWarnings([{ code: 'boot_apply', message: 'Workspace data could not be applied.', detail: err?.message || String(err) }]);
        setBootPartial(true);
        showToast('Workspace data could not be loaded. Please retry.');
      } finally {
        setApiLoading(false);
      }
    })();

    workspaceBootInFlightRef.current = bootPromise;
    try {
      return await bootPromise;
    } finally {
      if (workspaceBootInFlightRef.current === bootPromise) {
        workspaceBootInFlightRef.current = null;
      }
    }
  };

  useEffect(() => {
    mentorRequestLog.initSession();
    mentorRequestLog.restoreFromIndexedDB();
    mentorRequestLog.startFlushTimer();
    return () => mentorRequestLog.stopFlushTimer();
  }, []);

  useEffect(() => {
    if (authState !== 'logged_in' || !currentUser) {
      mentorRequestLog.setContext({ userRole: null, userId: null, activeTab: null });
      return;
    }
    mentorRequestLog.setContext({
      userRole: currentUser.role,
      userId: String(
        currentUser.apiUser?.user_id
        || currentUser.apiUser?.id
        || currentUser.email
        || ''
      ),
      activeTab,
    });
  }, [authState, currentUser, activeTab]);

  // On session restore (page refresh while logged in), re-seed data
  useEffect(() => {
    if (authState !== 'logged_in' || !currentUser) return;
    if (!mentorApi.isTokenValid()) {
      mentorApi.logout();
      sessionStorage.removeItem('mentor_auth');
      sessionStorage.removeItem('mentor_user');
      setAuthState('logged_out');
      setCurrentUser(null);
      setLiveSchools([]);
      setLiveBranches([]);
      return;
    }

    // Live session — re-fetch schools and counselors/auditors
    const isPA = currentUser.role === 'program_administrator';
    const isAuditor = currentUser.role === 'auditor';
    if (!isAuditor) {
      if (isPA || !currentUser.school_id) {
        mentorApi.getBranches()
          .then(setLiveBranches)
          .catch(err => showToast(formatApiError(err, 'Loading branches')));
        mentorApi.getSchools()
          .then(setLiveSchools)
          .catch(err => showToast(formatApiError(err, 'Loading schools')));
      } else {
        mentorApi.getSchools(currentUser.branch_id)
          .then(setLiveSchools)
          .catch(err => showToast(formatApiError(err, 'Loading schools')));
      }
    }
    mentorRequestLog.setCallerTag('session:restore');
    fetchLiveCounselorsAndAuditors();
    loadLiveResources();
    loadMentorWorkspaceData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs once on mount

  // A link that opens a screen (e.g. the "Reply in Mentor" button of an email) while nobody is signed in: remember it now,
  // so it survives a stale-session clean-up that rewrites the hash, and open it right after the login.
  useEffect(() => {
    if (authState === 'logged_in' || ssoState) return;
    const hash = window.location.hash;
    if (hash && hash !== '#/' && hash !== '#/dashboard' && hash.length > 2) sessionStorage.setItem('mentor_pending_hash', hash);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle welcome timeout redirect — restore pre-login deep link if present
  useEffect(() => {
    if (authState === 'welcoming') {
      const timer = setTimeout(() => {
        const pendingHash = sessionStorage.getItem('mentor_pending_hash');
        sessionStorage.removeItem('mentor_pending_hash');
        setAuthState('logged_in');
        setActiveTab('dashboard');
        window.location.hash = pendingHash || '#/dashboard';
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [authState]);

  // Map Ritika's API role string to our internal role key
  const mapApiRole = (apiRole) => {
    const roleMap = {
      program_administrator: { role: 'program_administrator', roleLabel: 'Program Administrator', badgeClass: 'pa' },
      pa: { role: 'program_administrator', roleLabel: 'Program Administrator', badgeClass: 'pa' },
      spoc: { role: 'spoc', roleLabel: 'SPOC', badgeClass: 'spoc' },
      teacher: { role: 'spoc', roleLabel: 'SPOC', badgeClass: 'spoc' },
      counselor: { role: 'counselor', roleLabel: 'Counselor', badgeClass: 'counselor' },
      counsellor: { role: 'counselor', roleLabel: 'Counselor', badgeClass: 'counselor' },
      auditor: { role: 'auditor', roleLabel: 'Auditor', badgeClass: 'auditor' },
    };
    return roleMap[apiRole?.toLowerCase()] || { role: 'program_administrator', roleLabel: 'Program Administrator', badgeClass: 'pa' };
  };

  // Shared post-authentication bootstrap: takes the merged claims+profile
  // `user` object returned by either mentorApi.login() (direct credential
  // login) or mentorApi.persistMentorSession() (SSO handoff from portahead)
  // and runs the same workspace bootstrap either way.
  const completeAuthenticatedSession = async (user) => {
    const roleInfo = mapApiRole(user.role);

    setCurrentUser({
      email: user.email,
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      role: roleInfo.role,
      roleLabel: roleInfo.roleLabel,
      badgeClass: roleInfo.badgeClass,
      apiUser: user,
    });
    setAuthState('welcoming');
    mentorRequestLog.initSession(true);
    mentorRequestLog.setContext({
      userRole: roleInfo.role,
      userId: String(user.user_id || user.id || user.email || ''),
      activeTab: 'dashboard',
    });
    mentorRequestLog.setCallerTag('login:bootstrap');

    // Rosters fetched below, so the workspace boot does not request them a second time.
    const loadedRosterKeys = [];

    // PA/admin always fetch all branches + schools regardless of JWT school_id claim
    const isPA = roleInfo.role === 'program_administrator';
    if (isPA || !user.school_id) {
      try {
        setApiLoading(true);
        const [branches, schools] = await Promise.all([
          mentorApi.getBranches(),
          mentorApi.getSchools(),
        ]);
        setLiveBranches(branches);
        setLiveSchools(schools);
      } catch (err) {
        console.error('Failed to load branches/schools:', err);
        showToast(formatApiError(err, 'Loading branches and schools'));
      } finally {
        setApiLoading(false);
      }
    } else {
      // Scoped user (SPOC/counselor/auditor) — fetch their specific school, students, and teachers
      try {
        setApiLoading(true);
        const [schools, students, teachers] = await Promise.all([
          mentorApi.getSchools(user.branch_id),
          mentorApi.getStudents(user.school_id, user.branch_id),
          mentorApi.getTeachers(user.school_id, user.branch_id),
        ]);
        setLiveSchools(schools);
        const scopedStudents = students.map(s => mapStudentFromApi(s, user.school_id, user.branch_id));
        syncWorkspaceRoster(workspaceDispatch, user.school_id, user.branch_id, scopedStudents);
        loadedRosterKeys.push(rosterKey(user.school_id, user.branch_id));
        if (Array.isArray(teachers) && teachers.length > 0) {
          setLiveTeachers(mentorApi.normalizeTeachers(teachers, user.school_id, user.branch_id));
        }
        // Auto-select the SPOC's school so cohort creation and school view work immediately
        const spocSchool = schools.find(s => String(s.id) === String(user.school_id));
        if (spocSchool) setSelectedSchool(spocSchool);
      } catch (err) {
        console.error('Failed to load scoped school data:', err);
        showToast(formatApiError(err, 'Loading scoped school data'));
      } finally {
        setApiLoading(false);
      }
    }

    // Fetch resource metadata. Ritika's /resources may deny some roles — fail silently.
    try {
      await loadLiveResources();
    } catch (err) {
      console.warn('Resources unavailable for this role:', err);
    }

    // Fetch counselors + auditors from saved school→branch associations
    const tempUser = {
      email: user.email,
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      role: roleInfo.role,
      roleLabel: roleInfo.roleLabel,
      badgeClass: roleInfo.badgeClass,
      apiUser: user,
    };
    fetchLiveCounselorsAndAuditors(tempUser.role);
    await loadMentorWorkspaceData(tempUser, { loadedRosterKeys });
  };

  // Redeem the SSO handoff code from portahead (/sso?code=...) on mount.
  useEffect(() => {
    if (ssoState !== 'pending') return;
    if (ssoHasRunRef.current) return;
    ssoHasRunRef.current = true;

    const code = new URLSearchParams(window.location.search).get('code');
    window.history.replaceState(null, '', '/');

    if (!code) {
      setSsoErrorMsg('Missing or invalid login link.');
      setSsoState('error');
      return;
    }

    (async () => {
      try {
        const user = await mentorApi.redeemSsoCode(code);
        await completeAuthenticatedSession(user);
        setSsoState(null);
      } catch (err) {
        setSsoErrorMsg(formatApiError(err, 'Login') || 'This login link has expired or was already used.');
        setSsoState('error');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssoState]);

  // Login handlers
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (isLoggingIn) return;
    if (!email || !password) {
      setErrorMsg('Please fill in both email and password.');
      return;
    }

    // Save any deep-link hash so it survives the welcoming timeout redirect
    const preLoginHash = window.location.hash;
    const isDeepLink = preLoginHash && preLoginHash !== '#/' && preLoginHash !== '#/dashboard' && preLoginHash.length > 2;
    if (isDeepLink) sessionStorage.setItem('mentor_pending_hash', preLoginHash);

    // Live API login
    setIsLoggingIn(true);
    try {
      setErrorMsg('');
      const user = await mentorApi.login(email, password);
      await completeAuthenticatedSession(user);
    } catch (err) {
      setErrorMsg(formatApiError(err, 'Login'));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    await mentorApi.logout();
    clearAllQueryCache();
    workspaceDispatch({ type: WORKSPACE_ACTIONS.RESET });
    setAuthState('logged_out');
    setCurrentUser(null);
    setEmail('');
    setPassword('');
    setActiveTab('dashboard');
    setSelectedCohort(null);
    setWizardState(null);
    setSessionFormState(null);
    setAuditorDashboardFilter('all');
    setLiveSchools([]);
    setLiveBranches([]);
    setYear2Discovery(null);
    setYear2DiscoveryError('');
    setYear2DiscoveryLoading(false);
    year2DiscoveryInFlightRef.current = null;
    setLiveResources([]);
    setLiveCounselors([]);
    setLiveAuditors([]);
    setLiveTeachers([]);
    setBootWarnings([]);
    setBootPartial(false);
    setBootBannerDismissed(false);
    setSelectedBranchId(null);
    window.location.hash = '#/dashboard';
  };

  const resolveSchoolBranchId = async (school) => {
    if (school.branch_id) return school.branch_id;
    if (selectedBranchId) return selectedBranchId;
    // Prefer branch already stored on a cohort for this school (works offline from Ritika branches).
    const fromCohort = (schoolCohortsMap[String(school.id)] || []).find(c => c.branchId)?.branchId;
    if (fromCohort) return fromCohort;
    try {
      const schoolBranches = await mentorApi.getBranches(school.id);
      if (schoolBranches.length >= 1) return schoolBranches[0].id;
    } catch (err) {
      console.warn(`Failed to resolve branch for school ${school.id}:`, err);
    }
    return null;
  };

  // Load students when a school is selected (branch_id required by mentor-api sync)
  const handleSelectSchoolAndLoadStudents = async (school) => {
    setSelectedSchool(school);
    window.location.hash = `#/schools/${school.id}`;
    const branchId = await resolveSchoolBranchId(school);
    if (!branchId) {
      showToast('Could not resolve branch for this school. Select a branch and try again.');
      return;
    }
    const schoolWithBranch = withSchoolBranchContext(
      { ...school, branch_id: branchId, branch_name: school.branch_name || school.branchName || null },
      { selectedSchool: school, branches: liveBranches }
    );
    setSelectedSchool(schoolWithBranch);
    try {
      // Teachers come from Ritika and can 500 independently — do not fail roster load.
      const [students, teachers, syncInfo] = await Promise.all([
        mentorApi.getStudents(school.id, branchId),
        mentorApi.getTeachers(school.id, branchId).catch(err => {
          console.warn(`Teachers load failed for school ${school.id}:`, err);
          showToast(formatApiError(err, `Loading teachers for school ${school.id}`));
          return [];
        }),
        mentorApi.getStudentSyncStatus(school.id, branchId).catch(() => null),
      ]);
      if (syncInfo) {
        setSchoolSyncInfo(prev => ({ ...prev, [String(school.id)]: syncInfo }));
      }
      const mapped = (students || []).map(s => mapStudentFromApi(s, school.id, branchId));
      syncWorkspaceRoster(workspaceDispatch, school.id, branchId, mapped);
      if (Array.isArray(teachers) && teachers.length > 0) {
        setLiveTeachers(prev => {
          const otherSchoolTeachers = prev.filter(t => t.schoolId !== String(school.id));
          return [...otherSchoolTeachers, ...mentorApi.normalizeTeachers(teachers, school.id, branchId)];
        });
      }
    } catch (err) {
      console.error('Failed to load students for school:', school.id, err);
      showToast(formatApiError(err, `Loading school ${school.id}`));
    }
  };

  const liveTeachersRef = useRef(liveTeachers);
  liveTeachersRef.current = liveTeachers;

  const ensureTeachersLoaded = useCallback(async (schoolId, branchId) => {
    if (!schoolId) return;
    const alreadyLoaded = liveTeachersRef.current.some(t => String(t.schoolId) === String(schoolId));
    if (alreadyLoaded) return;
    try {
      const teachers = await mentorApi.getTeachers(schoolId, branchId);
      if (Array.isArray(teachers) && teachers.length > 0) {
        setLiveTeachers(prev => {
          const others = prev.filter(t => String(t.schoolId) !== String(schoolId));
          return [...others, ...mentorApi.normalizeTeachers(teachers, schoolId, branchId)];
        });
      }
    } catch (err) {
      console.warn(`Failed to load teachers for school ${schoolId}:`, err);
    }
  }, []);

  // Track which school+branch teacher rosters we've already attempted to load (per mount).
  const loadedSchoolTeachersRef = useRef(new Set());

  // Auto-fetch students + teachers when a cohort is opened and that school's data isn't loaded yet
  // BUG-mentor-all-t1-tags-01: set T1 caller tag so Activity Log shows source of fetch.
  useEffect(() => {
    if (!selectedCohort) return;
    const cohortSchoolId = selectedCohort.schoolId;
    if (!cohortSchoolId) return;
    const school = liveSchools.find(s => String(s.id) === String(cohortSchoolId));
    let branchId = selectedCohort.branchId || school?.branch_id || null;
    if (!branchId) {
      branchId = null;
    }
    if (!branchId) {
      const foundStudent = liveStudents.find(s => String(s.schoolId) === String(cohortSchoolId) && s.branchId);
      if (foundStudent) branchId = foundStudent.branchId;
    }
    if (!branchId) {
      const foundTeacher = liveTeachers.find(t => String(t.schoolId) === String(cohortSchoolId) && t.branchId);
      if (foundTeacher) branchId = foundTeacher.branchId;
    }
    if (!branchId && currentUser?.apiUser?.branch_id && String(currentUser?.apiUser?.school_id) === String(cohortSchoolId)) {
      branchId = currentUser.apiUser.branch_id;
    }

    if (!branchId) {
      console.warn(`Auto-fetch skipped for cohort ${selectedCohort.name}: branchId could not be resolved for school ${cohortSchoolId}.`);
      return;
    }
    const studentsLoaded = rosterHasStudents(liveStudents, cohortSchoolId, branchId);
    const teachersLoaded = liveTeachers.some(t => String(t.schoolId) === String(cohortSchoolId));
    if (studentsLoaded && teachersLoaded) return;
    mentorRequestLog.setCallerTag('lazy:cohort-fetch');
    const fetches = [];
    if (!studentsLoaded) {
      fetches.push(
        mentorApi.getStudents(cohortSchoolId, branchId)
          .then(students => {
            const mapped = students.map(s => mapStudentFromApi(s, cohortSchoolId, branchId));
            syncWorkspaceRoster(workspaceDispatch, cohortSchoolId, branchId, mapped);
          })
      );
    }
    if (!teachersLoaded) {
      fetches.push(
        ensureTeachersLoaded(cohortSchoolId, branchId)
      );
    }
    Promise.all(fetches).catch(err => {
      console.error('Auto-fetch for cohort failed:', err);
      showToast(formatApiError(err, `Loading cohort ${selectedCohort.id} people`));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCohort, ensureTeachersLoaded]);

  // When branch is selected (PA branch-first navigation), set context for student loading
  // BUG-mentor-all-t1-tags-01: T1 caller tag for branch-first navigation.
  const handleSelectBranch = async (branchId) => {
    mentorRequestLog.setCallerTag('nav:select-branch');
    setSelectedBranchId(branchId);
    try {
      const schools = await mentorApi.getSchools(branchId);
      setLiveSchools(schools);
    } catch (err) {
      console.error('Failed to load schools for branch:', branchId, err);
      showToast(formatApiError(err, `Loading branch ${branchId} schools`));
    }
  };

  // Navigation handlers
  const handleNavigate = (tabId) => {
    mentorRequestLog.setCallerTag(`tab:${tabId}`);
    setSelectedCohort(null);
    setWizardState(null);
    setSessionFormState(null);
    setSelectedSession(null);
    setOutcomeFormSessionId(null);
    setOpenDropdownIdx(null);
    setEvaluatingSessionId(null);
    setViewingPastEvaluationId(null);
    setAuditorDashboardFilter('all');
    setSelectedCounselorSessionId(null);
    setOpenFilterDropdown(null);
    setSelectedCohortFilter('all');
    setSelectedSessionFilter('all');
    setSelectedAuditorFilter('all');
    setSelectedAuditorRosterId(null);
    setActiveTab(tabId);
    window.location.hash = `#/${tabId}`;
  };

  const handleBrandClick = () => {
    handleNavigate('dashboard');
  };

  // Navigate into a grouped nav item's sub-screen (only 'esg' has children
  // today) - #/{parentId}/{childId}, e.g. #/esg/squad-formation, rather than
  // the flat #/{tabId} every other nav item uses.
  const handleNavigateToChild = (parentId, childId) => {
    setActiveTab(parentId);
    if (parentId === 'esg') setEsgActiveScreen(childId);
    window.location.hash = `#/${parentId}/${childId}`;
  };

  // Hash-based routing — write hash from state; read hash on external changes (back/forward/paste)
  // eslint-disable-next-line no-unused-vars
  const navigate = (tabId, entityId = null, subView = null, subEntityId = null) => {
    let hash = `#/${tabId}`;
    if (entityId) hash += `/${entityId}`;
    if (subView) hash += `/${subView}`;
    if (subEntityId) hash += `/${subEntityId}`;
    window.location.hash = hash;
  };

  const openSchoolFromHash = useCallback(async (schoolId) => {
    const school = liveSchools.find(s => String(s.id) === String(schoolId));
    if (!school) return;
    // liveSchools rows never carry the PA's campus pick — always re-attach branch
    // from selection / localStorage UI pref so SchoolView + Cohort Builder can show it.
    // (Hash is only #/schools/:id; setting it after modal confirm re-enters this path.)
    const schoolWithBranch = withSchoolBranchContext(school, {
      selectedSchool,
      branches: liveBranches,
    });
    const branchId = schoolWithBranch.branch_id;
    const rosterLoaded = branchId && rosterHasStudents(liveStudents, schoolId, branchId);
    // BUG-mentor-pa-hash-overfetch-01: when boot already loaded rosters, skip the re-fetch
    // even if selectedSchool is null (first hash navigation). The old guard required both
    // rosterLoaded AND selectedSchool to match, causing a duplicate GET /students on every
    // initial hash-based school navigation after boot.
    if (rosterLoaded) {
      setSelectedSchool(schoolWithBranch);
      mentorRequestLog.setCallerTag('hash:school');
      return;
    }
    // Guard: same school already loaded with campus context — no-op
    if (
      selectedSchool &&
      String(selectedSchool.id) === String(schoolId) &&
      selectedSchool.branch_id
    ) {
      return;
    }
    mentorRequestLog.setCallerTag('hash:school');
    await handleSelectSchoolAndLoadStudents(schoolWithBranch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSchools, liveStudents, selectedSchool, liveBranches]);

  // Apply deep link on initial load when session is already restored.
  // Also reruns when sessions arrive after boot so cold-paste session/eval links resolve.
  useEffect(() => {
    if (authState !== 'logged_in') return;
    const { tab, detailTab, entityId, subView } = parseHash();
    const resolvedTab = (tab === 'cohorts' && currentUser?.role === 'spoc') ? 'my_cohorts' : tab;
    setActiveTab(resolvedTab);

    if (resolvedTab === 'schools' && entityId) {
      openSchoolFromHash(entityId);
    } else if ((resolvedTab === 'cohorts' || resolvedTab === 'my_cohorts') && entityId) {
      const found = allCohortsForDisplay.find(c => String(c.id) === String(entityId));
      if (found) {
        setSelectedCohort(found);
        if (detailTab) setCohortDetailTab(detailTab);
      }
    } else if (resolvedTab === 'sessions' && entityId) {
      if (currentUser?.role === 'counselor') {
        setSelectedCounselorSessionId(entityId);
      } else {
        const found = sessions.find(s => String(s.id) === String(entityId));
        if (found) {
          setSelectedSession(found);
        } else if (sessions.length > 0) {
          // sessions loaded but this ID not found — fetch directly
          mentorApi.getSession(entityId)
            .then(s => setSelectedSession(s))
            .catch(() => { showToast('Session not found or not accessible'); window.location.hash = '#/sessions'; });
        }
        // if sessions.length === 0, effect will rerun when sessions arrive
      }
    } else if ((resolvedTab === 'my_sessions_y1' || resolvedTab === 'my_sessions_y2') && entityId && subView === 'evaluate') {
      setEvaluatingSessionId(entityId);
    } else if ((resolvedTab === 'my_sessions_y1' || resolvedTab === 'my_sessions_y2') && entityId && subView === 'past') {
      setViewingPastEvaluationId(entityId);
    } else if (resolvedTab === 'nominations' && entityId && (currentUser?.role === 'spoc' || currentUser?.role === 'auditor')) {
      handleOpenOutcomeForm(entityId);
    } else if (resolvedTab === 'esg' && entityId) {
      setEsgActiveScreen(entityId === 'lists' ? 'squads' : entityId);
      setEsgSquadId(entityId === 'squad' && Number(subView) > 0 ? Number(subView) : null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSchools.length, sessions.length, authState]); // rerun when schools/sessions arrive after boot

  useEffect(() => {
    const onHashChange = () => {
      if (authState !== 'logged_in') return;
      const { tab, detailTab, entityId, subView } = parseHash();
      // remap cohorts → my_cohorts for SPOC role
      const resolvedTab = (tab === 'cohorts' && currentUser?.role === 'spoc') ? 'my_cohorts' : tab;
      setActiveTab(prev => prev === resolvedTab ? prev : resolvedTab);

      if (resolvedTab === 'schools' && entityId) {
        openSchoolFromHash(entityId);
      } else if (resolvedTab === 'schools') {
        setSelectedSchool(null);
      } else if (resolvedTab === 'cohorts' || resolvedTab === 'my_cohorts') {
        if (entityId) {
          setSelectedSchool(null);
          const found = allCohortsForDisplay.find(c => String(c.id) === String(entityId));
          if (found) {
            setSelectedCohort(found);
            if (detailTab) setCohortDetailTab(detailTab);
          }
        } else {
          setSelectedSchool(null);
          setSelectedCohort(null);
        }
      } else if (resolvedTab === 'sessions' && entityId) {
        setSelectedCohort(null);
        setWizardState(null);
        setSessionFormState(null);
        if (currentUser?.role === 'counselor') {
          setSelectedCounselorSessionId(entityId);
        } else {
          const found = sessions.find(s => String(s.id) === String(entityId));
          if (found) {
            setSelectedSession(found);
          } else {
            mentorApi.getSession(entityId)
              .then(s => setSelectedSession(s))
              .catch(() => { showToast('Session not found or not accessible'); window.location.hash = '#/sessions'; });
          }
        }
      } else if ((resolvedTab === 'my_sessions_y1' || resolvedTab === 'my_sessions_y2') && entityId && subView === 'evaluate') {
        setViewingPastEvaluationId(null);
        setEvaluatingSessionId(entityId);
      } else if ((resolvedTab === 'my_sessions_y1' || resolvedTab === 'my_sessions_y2') && entityId && subView === 'past') {
        setEvaluatingSessionId(null);
        setViewingPastEvaluationId(entityId);
      } else if (resolvedTab === 'nominations' && entityId && currentUser?.role === 'spoc') {
        handleOpenOutcomeForm(entityId);
      } else if (resolvedTab === 'esg' && entityId) {
        setEsgActiveScreen(entityId === 'lists' ? 'squads' : entityId);
        setEsgSquadId(entityId === 'squad' && Number(subView) > 0 ? Number(subView) : null);
      } else {
        setSelectedSchool(null);
        setSelectedCohort(null);
        setWizardState(null);
        setSessionFormState(null);
        setOutcomeFormSessionId(null);
        setEvaluatingSessionId(null);
        setViewingPastEvaluationId(null);
        setSelectedCounselorSessionId(null);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, allCohortsForDisplay, sessions, currentUser]);

  const navItems = currentUser ? NAVIGATION_CONFIG[currentUser.role] : [];
  const currentSpocId = currentUser?.role === 'spoc'
    ? String(
        currentUser.apiUser?.user_id
        || currentUser.apiUser?.id
        || currentUser.apiUser?.sub
        || liveTeachers.find(t => t.email.toLowerCase() === currentUser.email.toLowerCase())?.id
        || ''
      )
    : '';
  const currentAuditorId = currentUser?.role === 'auditor'
    ? String(
        liveAuditors.find(a => a.email?.toLowerCase() === currentUser.email?.toLowerCase())?.id
        || currentUser.apiUser?.user_id
        || currentUser.apiUser?.id
        || currentUser.apiUser?.sub
        || ''
      )
    : '';

  const refreshSessionAndProjections = useCallback(async (sessionId) => {
    try {
      const session = await mentorApi.getSession(sessionId);
      if (!session) return null;
      syncWorkspaceSession(workspaceDispatch, session);
      setSelectedSession(prev => (prev && String(prev.id) === String(sessionId) ? session : prev));
      return session;
    } catch (err) {
      console.error(`Failed to refresh session ${sessionId}:`, err);
      return null;
    }
  }, [workspaceDispatch]);

  const handleManageAssignmentsSaved = useCallback(async () => {
    if (!selectedSession?.id) return;
    await refreshSessionAndProjections(selectedSession.id);
    setShowManageAssignments(false);
  }, [selectedSession, refreshSessionAndProjections]);

  const refreshEvaluationsReports = useCallback(async (sessionsOverride) => {
    const snapshot = sessionsOverride || sessions;
    const role = currentUser?.role;
    if (role === 'program_administrator' || role === 'counselor') {
      mentorRequestLog.setCallerTag('tab:approvals-refresh');
      invalidateQuery(APPROVALS_EVAL_QUERY_KEY);
      const evaluations = await mentorApi.getEvaluations();
      seedMentorQuery(APPROVALS_EVAL_QUERY_KEY, evaluations);
      const reportsNext = buildReportsFromEvaluations(evaluations, snapshot);
      setReports(reportsNext);
      return reportsNext;
    }
    const reportsNext = await fetchEvaluationsReports(snapshot);
    setReports(reportsNext);
    return reportsNext;
  }, [sessions, currentUser?.role]);

  const buildSessionsSnapshotWithRefresh = useCallback((refreshedSession) => {
    if (!refreshedSession?.id) return sessions;
    const enriched = enrichSessionSchoolName(refreshedSession, liveSchools, allCohortsForDisplay);
    return sessions.map(s =>
      String(s.id) === String(enriched.id) ? enriched : s
    );
  }, [sessions, liveSchools, allCohortsForDisplay]);

  // Eager-load teacher rosters for any cohort list that has a SPOC, so names resolve on first paint.
  // BUG-mentor-pa-cohorts-overfetch-01: guard with T1 tag so Activity Log shows source of fetch.
  useEffect(() => {
    if (activeTab !== 'cohorts') return;
    if (selectedCohort || wizardState || sessionFormState) return;

    const schoolsToFetch = [];
    for (const c of allCohortsForDisplay) {
      if (!c.spocId) continue;
      const schoolId = c.schoolId;
      const branchId = c.branchId || null;
      if (!schoolId) continue;
      const key = `${String(schoolId)}:${String(branchId || '')}`;
      if (loadedSchoolTeachersRef.current.has(key)) continue;
      loadedSchoolTeachersRef.current.add(key);
      schoolsToFetch.push({ schoolId, branchId });
    }

    if (schoolsToFetch.length === 0) return;
    mentorRequestLog.setCallerTag('lazy:cohort-teachers');
    Promise.allSettled(
      schoolsToFetch.map(({ schoolId, branchId }) => ensureTeachersLoaded(schoolId, branchId))
    );
  }, [activeTab, selectedCohort, wizardState, sessionFormState, allCohortsForDisplay, ensureTeachersLoaded]);

  // Item 4: lazy-refresh auditor/counselor roster (5 min stale / 24h hard expire).
  useEffect(() => {
    const role = currentUser?.role;
    if (!role || role === 'auditor') return;
    const onAuditorsTab = activeTab === 'auditors' && !selectedCohort && !wizardState && !sessionFormState;
    const onSessionForm = !!sessionFormState;
    const onManageAssignments = !!showManageAssignments;
    if (!onAuditorsTab && !onSessionForm && !onManageAssignments) return;
    mentorRequestLog.setCallerTag('lazy:people-roster');
    ensurePeopleRosterFresh();
  }, [
    activeTab,
    selectedCohort,
    wizardState,
    sessionFormState,
    showManageAssignments,
    currentUser?.role,
    ensurePeopleRosterFresh,
  ]);

  // Auditor evaluation: eager-fetch nomination for the session if sessionRoles is missing it.
  // Covers the race where the eval form opens before loadMentorWorkspaceData finishes.
  // Guard: key existence in sessionRoles (not chairId truthiness) — a session with no chair
  // nominated writes chairId:'' which is falsy, causing an infinite fetch loop if we gated
  // on chairId alone.
  // BUG-mentor-all-t1-tags-01: set T1 caller tag so Activity Log shows source.
  useEffect(() => {
    if (!evaluatingSessionId || currentUser?.role !== 'auditor') return;
    mentorRequestLog.setCallerTag('lazy:auditor-eval-prefetch');
    const existingSession = getSessionById(workspaceState, evaluatingSessionId);
    if (existingSession) {
      const schoolId = existingSession.schoolId;
      const branchId = existingSession.branchId;
      if (schoolId) {
        const rosterLoaded = rosterHasSchoolBranch(workspaceState, schoolId, branchId);
        if (rosterLoaded) return;
      }
    }
    refreshSessionAndProjections(evaluatingSessionId).then(session => {
      if (!session) return;
      // Prefetch students — auditors have no school_id in JWT so workspace boot may skip them.
      const schoolId = session.schoolId;
      const branchId = session.branchId;
      if (!schoolId) return;
      const rosterLoaded = rosterHasSchoolBranch(workspaceState, schoolId, branchId);
      if (rosterLoaded) return;
      mentorApi.getStudents(schoolId, branchId).then(students => {
        const mapped = students.map(s => mapStudentFromApi(s, schoolId, branchId));
        syncWorkspaceRoster(workspaceDispatch, schoolId, branchId, mapped);
      }).catch(err => console.warn('Failed to prefetch students for auditor eval:', err));
    }).catch(err => console.warn('Failed to refresh session for auditor eval:', err));
  }, [evaluatingSessionId, currentUser, refreshSessionAndProjections, workspaceDispatch, workspaceState]);

  // Derived Dashboard Stats
  const dashboardStats = useMemo(() => {
    const listCohortCount = cohortsFetch.status === 'success' ? cohortsFetch.data.length : 0;
    const enrichedActive = sessions.filter(s => isActiveSessionStatus(s.status)).length;
    const listActive = sessionsFetch.status === 'success'
      ? sessionsFetch.data.filter(s => isActiveSessionStatus(s.status)).length
      : 0;

    return {
      cohortsCount: Math.max(allCohortsForDisplay.length, listCohortCount),
      activeSessionsCount: Math.max(enrichedActive, listActive),
      pendingApprovalsCount: reports.filter(r => r.status === 'pending').length,
      auditorsCount: activeAuditors.length
    };
  }, [allCohortsForDisplay, sessions, reports, activeAuditors, cohortsFetch, sessionsFetch]);

  // Grouped active upcoming sessions (for PA dashboard summary)
  const groupedSessionsActive = useMemo(() => {
    const active = sessions.filter(s => s.status === 'Planned' || s.status === 'Live');
    const sorted = [...active].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

    const groups = {};
    sorted.forEach(session => {
      const label = getWeekLabel(session.scheduledAt);
      if (!groups[label]) {
        groups[label] = [];
      }
      groups[label].push(session);
    });
    return groups;
  }, [sessions]);

  // Grouped sessions for auditor My Sessions (upcoming and past tabs)
  const groupedAuditorUpcoming = useMemo(() => {
    const list = filterAuditorUpcomingSessions(sessions, currentAuditorId, auditorSessionStatuses);
    const sorted = [...list].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    const groups = {};
    sorted.forEach(s => {
      const label = getWeekLabel(s.scheduledAt);
      if (!groups[label]) groups[label] = [];
      groups[label].push(s);
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, currentAuditorId, auditorSessionStatuses, currentUser]);

  const groupedAuditorPast = useMemo(() => {
    const list = filterAuditorPastSessions(sessions, currentAuditorId, auditorSessionStatuses);
    const sorted = [...list].sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));
    const groups = {};
    sorted.forEach(s => {
      const label = getWeekLabel(s.scheduledAt);
      if (!groups[label]) groups[label] = [];
      groups[label].push(s);
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, currentAuditorId, auditorSessionStatuses, currentUser]);

  // Grouped all sessions grouped by week (for /sessions list view), with status filter
  const groupedSessionsAll = useMemo(() => {
    const statusKey = String(sessionListStatusFilter || 'all').toLowerCase();
    const filtered = sessions.filter(session => {
      if (statusKey === 'all') return true;
      return String(session.status || '').toLowerCase() === statusKey;
    });
    const sorted = [...filtered].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

    const groups = {};
    sorted.forEach(session => {
      const label = getWeekLabel(session.scheduledAt);
      if (!groups[label]) {
        groups[label] = [];
      }
      groups[label].push(session);
    });
    return groups;
  }, [sessions, sessionListStatusFilter]);

  // -------------------------------------------------------------
  // COHORT CREATION WIZARD FUNCTIONS
  // -------------------------------------------------------------
  const handleStartCohortWizard = () => {
    setSelectedCohort(null);
    setSessionFormState(null);
    setWizardState({
      step: 1,
      schoolId: '',
      levelYear: 'Year 1',
      studentIds: [],
      name: '',
      spocId: '',
      counselorIds: [],
      auditorAssignments: [] // array of { auditorId, isStandby }
    });
    setWizardSearchText('');
    setHighlightedStudentId(null);
  };

  const handleCancelCohortWizard = () => {
    setWizardState(null);
    setHighlightedStudentId(null);
  };

  const handleWizardSchoolSelect = (schoolId) => {
    setWizardState(prev => {
      const selectedSchool = liveSchools.find(s => s.id === schoolId);
      const schoolName = selectedSchool ? (selectedSchool.school_name || selectedSchool.name || '') : '';
      return {
        ...prev,
        schoolId,
        name: schoolName ? `${schoolName} - Glosco Cohort` : '',
        studentIds: [], // clear any previously selected students when school changes
        step: 2
      };
    });
    setWizardSearchText('');       // clear stale search text from previous school
    setHighlightedStudentId(null); // clear stale highlight
  };

  const isStudentAssignedToAnyCohort = (studentId) => {
    return allCohortsForDisplay.some(c => c.studentIds.includes(studentId));
  };

  const getAssignedCohortName = (studentId) => {
    const assigned = allCohortsForDisplay.find(c => c.studentIds.includes(studentId));
    return assigned ? assigned.name : null;
  };

  const handleAddStudent = (studentId) => {
    if (isStudentAssignedToAnyCohort(studentId)) return;
    setWizardState(prev => {
      if (!prev) return prev;
      if (prev.studentIds.includes(studentId)) return prev;
      return {
        ...prev,
        studentIds: [...prev.studentIds, studentId]
      };
    });
    setHighlightedStudentId(null);
  };

  const handleRemoveStudent = (studentId) => {
    setWizardState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        studentIds: prev.studentIds.filter(id => id !== studentId)
      };
    });
  };

  const handleWizardToggleAuditorSelect = (auditorId) => {
    setWizardState(prev => {
      const isAssigned = prev.auditorAssignments.some(a => a.auditorId === auditorId);
      const auditorAssignments = isAssigned
        ? prev.auditorAssignments.filter(a => a.auditorId !== auditorId)
        : [...prev.auditorAssignments, { auditorId, isStandby: false }];
      return { ...prev, auditorAssignments };
    });
  };

  const handleWizardToggleAuditorStandby = (auditorId) => {
    setWizardState(prev => {
      const auditorAssignments = prev.auditorAssignments.map(a => {
        if (a.auditorId === auditorId) {
          return { ...a, isStandby: !a.isStandby };
        }
        return a;
      });
      return { ...prev, auditorAssignments };
    });
  };

  const handleWizardToggleCounselor = (counselorId) => {
    setWizardState(prev => {
      const isSelected = prev.counselorIds.includes(counselorId);
      const counselorIds = isSelected
        ? prev.counselorIds.filter(id => id !== counselorId)
        : [...prev.counselorIds, counselorId];
      return { ...prev, counselorIds };
    });
  };

  const handleCreateCohortSubmit = async () => {
    const { schoolId, studentIds, name, spocId, counselorIds, auditorAssignments } = wizardState;
    if (!name || !spocId) return;

    const targetSchool = liveSchools.find(s => s.id === schoolId);
    const resolvedBranchId = targetSchool?.branch_id || selectedBranchId || null;

    const payload = {
      name,
      school_id: Number(schoolId),
      branch_id: resolvedBranchId ? Number(resolvedBranchId) : null,
      spoc_id: Number(spocId),
      level_year: wizardState.levelYear,
      counselor_ids: counselorIds.map(id => Number(id)),
      auditor_assignments: auditorAssignments.map(a => ({
        user_id: Number(a.auditorId),
        is_standby: Boolean(a.isStandby),
      })),
      student_ids: studentIds.map(id => Number(id)),
    };

    let newCohort = null;
    try {
      newCohort = await mentorApi.createCohort(payload);
    } catch (err) {
      console.error('Failed to create cohort in mentor API:', err);
      showToast(formatApiError(err, 'Cohort create'));
      return;
    }

    const cohortRecord = newCohort;

    syncWorkspaceCohort(workspaceDispatch, cohortRecord);
    setWizardState(null);
    setSelectedCohort(cohortRecord); // Redirect to detail page
    setCohortDetailTab('students');
    showToast('Cohort saved to mentor API.');
  };

  const handleAssignSpoc = async (cohortId, spocId, spocName) => {
    const updated = { spocId, spocName };
    try {
      await mentorApi.updateCohort(cohortId, { spoc_id: Number(spocId) });
    } catch (err) {
      console.error('Failed to update cohort SPOC via mentor API:', err);
      showToast(formatApiError(err, 'Cohort SPOC update'));
      return;
    }

    const cohortToUpdate = selectedCohort || allCohortsForDisplay.find(c => String(c.id) === String(cohortId));
    if (cohortToUpdate) {
      syncWorkspaceCohort(workspaceDispatch, { ...cohortToUpdate, ...updated });
    }
    setSelectedCohort(prev => prev ? { ...prev, ...updated } : prev);
    setSpocPickerOpen(false);
    showToast('SPOC assignment updated.');
  };

  const handleUnassignSpoc = async (cohortId) => {
    const hasPlannedSessions = sessions.some(s => String(s.cohortId) === String(cohortId));
    const warning = hasPlannedSessions ? "\n\nWARNING: This cohort has planned sessions; unassigning the SPOC won't delete them." : "";
    if (!window.confirm(`Are you sure you want to unassign the SPOC for this cohort?${warning}`)) {
      return;
    }

    const updated = { spocId: null, spocName: null };
    try {
      await mentorApi.updateCohort(cohortId, { spoc_id: null });
    } catch (err) {
      console.error('Failed to unassign cohort SPOC via mentor API:', err);
      showToast(formatApiError(err, 'Cohort SPOC unassign'));
      return;
    }

    const cohortToUpdate = selectedCohort || allCohortsForDisplay.find(c => String(c.id) === String(cohortId));
    if (cohortToUpdate) {
      syncWorkspaceCohort(workspaceDispatch, { ...cohortToUpdate, ...updated });
    }
    setSelectedCohort(prev => prev ? { ...prev, ...updated } : prev);
    setSpocPickerOpen(false);
    showToast('SPOC unassigned.');
  };

  const handleSchoolCohortsReplace = async (nextCohorts) => {
    if (!selectedSchool) return;
    const schoolId = String(selectedSchool.id);
    const previous = schoolCohortsMap[schoolId] || [];
    const nextById = new Map(nextCohorts.map(c => [String(c.id), c]));
    const synced = [];
    const errors = [];
    const isPersistedCohortId = (id) => /^\d+$/.test(String(id || ''));

    const schoolName = selectedSchool.school_name || selectedSchool.name || 'Unknown School';
    const branchId = selectedSchool.branch_id || selectedBranchId || null;

    // Only school-roster IDs may be written. When the roster is loaded, drop foreign
    // IDs client-side so legacy contamination cannot 422 the whole Save & Close.
    // (API PUT also strips foreign IDs; create still hard-rejects them.)
    const schoolRosterIds = new Set(
      (liveStudents || [])
        .filter(s => String(s.schoolId) === schoolId)
        .map(s => String(s.id))
    );
    const filterStudentIdsForSchool = (ids) => {
      const numeric = (ids || []).map(id => Number(id)).filter(id => !Number.isNaN(id));
      if (schoolRosterIds.size === 0) return numeric;
      return numeric.filter(id => schoolRosterIds.has(String(id)));
    };
    let strippedForeignCount = 0;

    // Deletes first — do not let update/sync of remaining dirty cohorts block removal.
    for (const previousCohort of previous) {
      if (isPersistedCohortId(previousCohort.id) && !nextById.has(String(previousCohort.id))) {
        try {
          await mentorApi.deleteCohort(previousCohort.id);
          workspaceDispatch({ type: WORKSPACE_ACTIONS.REMOVE_COHORT, payload: previousCohort.id });
        } catch (err) {
          console.error('Failed to delete cohort via mentor API:', err);
          errors.push(formatApiError(err, `Delete ${previousCohort.name || previousCohort.id}`));
        }
      }
    }

    for (const cohort of nextCohorts) {
      const payload = {
        name: cohort.name,
        school_id: Number(cohort.schoolId || selectedSchool.id),
        branch_id: cohort.branchId ? Number(cohort.branchId) : (branchId ? Number(branchId) : null),
        level_year: cohort.levelYear ?? null,
        counselor_ids: (cohort.counselorIds || []).map(id => Number(id)),
        auditor_assignments: (cohort.auditorAssignments || []).map(a => ({
          user_id: Number(a.auditorId),
          is_standby: Boolean(a.isStandby),
        })),
      };
      if (cohort.spocId) {
        payload.spoc_id = Number(cohort.spocId);
      }

      const hasPersistedId = isPersistedCohortId(cohort.id);
      const rawStudentIds = (cohort.studentIds || []).map(id => Number(id)).filter(id => !Number.isNaN(id));
      const nextStudentIds = filterStudentIdsForSchool(rawStudentIds);
      strippedForeignCount += Math.max(0, rawStudentIds.length - nextStudentIds.length);
      let cohortRecord = null;

      if (hasPersistedId) {
        try {
          await mentorApi.updateCohort(cohort.id, payload);
          cohortRecord = await mentorApi.syncCohortStudents(cohort.id, nextStudentIds);
        } catch (err) {
          console.error('Failed to update cohort via mentor API:', err);
          errors.push(formatApiError(err, `Update ${cohort.name || cohort.id}`));
          // Keep prior workspace shape so one bad cohort does not wipe the rest of the save.
          synced.push({
            ...cohort,
            schoolId: String(cohort.schoolId || selectedSchool.id),
            schoolName: cohort.schoolName || schoolName,
            studentIds: nextStudentIds.map(String),
          });
          continue;
        }
      } else {
        try {
          // Create without student_ids when filtered empty — avoids create hard-422 on foreign IDs.
          const created = await mentorApi.createCohort({
            ...payload,
            ...(nextStudentIds.length > 0 ? { student_ids: nextStudentIds } : {}),
          });
          cohortRecord = nextStudentIds.length > 0
            ? await mentorApi.syncCohortStudents(created.id, nextStudentIds)
            : created;
        } catch (err) {
          console.error('Failed to create cohort via mentor API:', err);
          errors.push(formatApiError(err, `Create ${cohort.name || 'cohort'}`));
          continue;
        }
      }

      if (!cohortRecord) {
        cohortRecord = {
          ...cohort,
          id: String(cohort.id || `cohort-${Date.now()}`),
          schoolId: String(cohort.schoolId || selectedSchool.id),
          schoolName,
          branchId: payload.branch_id ? String(payload.branch_id) : '',
          spocId: payload.spoc_id ? String(payload.spoc_id) : '',
          spocName: cohort.spocName || null,
          levelYear: payload.level_year,
          status: titleCaseWord(cohort.status) || 'Active',
          studentIds: nextStudentIds.map(String),
          counselorIds: (cohort.counselorIds || []).map(String),
          auditorAssignments: (cohort.auditorAssignments || []).map(a => ({ ...a, auditorId: String(a.auditorId) })),
        };
      }

      synced.push({
        ...cohortRecord,
        schoolName: cohortRecord.schoolName || schoolName,
        studentIds: (cohortRecord.studentIds || nextStudentIds.map(String)).map(String),
      });
    }

    synced.forEach(cohort => syncWorkspaceCohort(workspaceDispatch, cohort));
    if (selectedCohort && String(selectedCohort.schoolId) === schoolId) {
      const updatedSelected = synced.find(c => String(c.id) === String(selectedCohort.id));
      if (updatedSelected) {
        setSelectedCohort(updatedSelected);
      } else if (!nextById.has(String(selectedCohort.id))) {
        setSelectedCohort(synced[0] || null);
      } else if (synced.length > 0) {
        setSelectedCohort(synced[0]);
      } else {
        setSelectedCohort(null);
      }
    }

    if (strippedForeignCount > 0) {
      showToast(`Removed ${strippedForeignCount} student(s) that do not belong to this school.`);
    }
    if (errors.length > 0) {
      showToast(errors[0] + (errors.length > 1 ? ` (+${errors.length - 1} more)` : ''));
    } else if (strippedForeignCount === 0) {
      showToast('Cohorts saved.');
    }
  };

  const handleSchoolGroupSave = async (groupData, isEdit) => {
    if (!selectedSchool) return;
    const schoolId = String(selectedSchool.id);

    const payload = {
      name: groupData.name,
      school_id: Number(schoolId),
      cohort_ids: (groupData.cohortIds || []).map(Number),
    };

    let savedGroup = null;
    try {
      if (isEdit && groupData.id) {
        savedGroup = await mentorApi.updateGroup(groupData.id, payload);
      } else {
        savedGroup = await mentorApi.createGroup(payload);
      }
    } catch (err) {
      console.error('Failed to persist group via mentor API:', err);
      showToast(formatApiError(err, 'Group save'));
      return;
    }

    const groupRecord = {
      id: String(savedGroup?.id || groupData.id),
      name: savedGroup?.name || groupData.name,
      schoolId,
      schoolName: savedGroup?.schoolName || selectedSchool.school_name || selectedSchool.name || '',
      cohortIds: (savedGroup?.cohortIds || groupData.cohortIds || []).map(String),
      cohorts: savedGroup?.cohorts || [],
      year: savedGroup?.cohorts?.[0]?.levelYear ?? groupData.year ?? null,
    };

    setSchoolGroupsMap(prev => {
      const existing = prev[schoolId] || [];
      const updated = isEdit
        ? existing.map(g => String(g.id) === String(groupRecord.id) ? groupRecord : g)
        : [...existing.filter(g => String(g.id) !== String(groupRecord.id)), groupRecord];
      return { ...prev, [schoolId]: updated };
    });

    showToast(isEdit ? 'Group updated.' : 'Group created.');
  };

  // Filter students by selected school and search criteria for Left Panel, excluding already selected ones in right panel
  const filteredLeftStudents = useMemo(() => {
    if (!wizardState || !wizardState.schoolId) return [];
    return liveStudents.filter(student => {
      // Must belong to the selected school
      if (student.schoolId !== wizardState.schoolId) return false;
      // Exclude students that are currently in the right panel (selected)
      if (wizardState.studentIds.includes(student.id)) return false;

      // Filter by search text (real-time)
      const search = wizardSearchText.toLowerCase();
      return student.name.toLowerCase().includes(search) || 
             student.grade.toLowerCase().includes(search);
    });
  }, [wizardState, wizardSearchText, liveStudents]);

  // Selected students for the Right Panel
  const selectedStudents = useMemo(() => {
    if (!wizardState || !wizardState.studentIds) return [];
    return resolveStudents(wizardState.studentIds, liveStudents);
  }, [wizardState, liveStudents]);

  // -------------------------------------------------------------
  // SESSION FORM FUNCTIONS & CLASH HARD-BLOCK VALIDATION
  // -------------------------------------------------------------

  // Returns IDs of resources that match a given sessionType + cohort levelYear.
  // Delegates to the shared filter helper also used by ResourcesViewer.
  const activeResources = liveResources;
  const getAutoResources = useCallback(
    (sessionType, cohortLevelYear) => filterResourceIdsByCouncil(activeResources, sessionType, cohortLevelYear),
    [activeResources]
  );

  // Resource loading can finish after the session form opens. Populate the
  // canonical resources when they arrive without overwriting an explicit list.
  useEffect(() => {
    if (!sessionFormState || resourceLoadState !== 'ready' || (sessionFormState.resources || []).length > 0) return;
    const autoResources = getAutoResources(sessionFormState.sessionType, sessionFormState.levelYear);
    if (autoResources.length === 0) return;
    setSessionFormState(prev => {
      if (!prev || (prev.resources || []).length > 0) return prev;
      return { ...prev, resources: autoResources };
    });
  }, [getAutoResources, resourceLoadState, sessionFormState]);

  const handleOpenStudentDrawer = useCallback(async (student) => {
    if (!student) return;
    setSelectedStudentForDrawer(student);
    setLoadingStudentDrawerEvaluations(true);
    setStudentDrawerEvaluations([]);
    try {
      const studentId = String(student.id);
      const isYear2Student = allCohortsForDisplay.some((cohort) =>
        cohort.levelYear === 'Year 2'
        && (String(cohort.id) === String(student.cohortId)
          || (cohort.studentIds || []).map(String).includes(studentId))
      );
      const [year1Log, year2Log] = await Promise.all([
        mentorApi.getStudentEvaluationLog({ student_id: student.id }),
        isYear2Student ? mentorApi.getYear2StudentEvaluationLog({ student_id: student.id }) : Promise.resolve([]),
      ]);
      const merged = [
        ...year1Log,
        ...year2Log.map(normalizeYear2StudentLogItem),
      ].sort((a, b) => new Date(b.approvedAt || b.approved_at || b.submittedAt || 0) - new Date(a.approvedAt || a.approved_at || a.submittedAt || 0));
      setStudentDrawerEvaluations(merged);
    } catch (err) {
      console.error('Failed to load student evaluations:', err);
    } finally {
      setLoadingStudentDrawerEvaluations(false);
    }
  }, [allCohortsForDisplay]);

  const handleStartSessionForm = (cohortId, schoolId) => {
    setSelectedCohort(null);
    setWizardState(null);

    let nextType = '';
    let nextNumber = 1;

    if (cohortId) {
      const cohortSessions = sessions.filter(s => s.cohortId === cohortId);
      const activeSessions = cohortSessions.filter(s => s.status !== 'Cancelled');
      const orientationCompleted = activeSessions.some(
        s => s.sessionType === 'Orientation' && s.status === 'Completed'
      );
      nextType = 'Orientation';

      if (orientationCompleted) {
        const councilNumbers = activeSessions
          .filter(s => s.sessionType.startsWith('Council') && s.status !== 'Cancelled')
          .map(s => parseInt(s.sessionType.split(' ')[1]) || 0);

        const maxCouncil = councilNumbers.length > 0 ? Math.max(...councilNumbers) : 0;
        nextNumber = maxCouncil + 1;
        nextType = `Council ${nextNumber}`;
      }
    }

    const cohort = allCohortsForDisplay.find(c => c.id === cohortId);
    // levelYear lives on the group, not the cohort — look it up via schoolGroupsMap
    const allGroups = Object.values(schoolGroupsMap).flat();
    const parentGroup = allGroups.find(g => g.cohortIds && g.cohortIds.includes(cohortId));
    const resolvedLevel = (cohort && cohort.levelYear) ?? (parentGroup && parentGroup.year) ?? null;
    const autoRes = cohort && nextType ? getAutoResources(nextType, resolvedLevel) : [];

    setSessionFormState({
      targetType: 'cohort',
      cohortId: cohortId || '',
      groupId: '',
      schoolId: cohort ? String(cohort.schoolId || '') : (schoolId ? String(schoolId) : ''),
      levelYear: resolvedLevel,
      sessionType: nextType,
      sessionNumber: nextNumber,
      scheduledAt: '',
      meetingLink: '',
      comments: '',
      resources: autoRes,
      customLinks: [],
      timeSlot: '',
      auditors: [],
      counselorId: '',
      lockContext: Boolean(cohortId),
    });
    setSessionFormClashError('');
  };

  const handleCancelSessionForm = () => {
    setSessionFormState(null);
    setSessionFormSubmitting(false);
    setSessionFormClashError('');
  };

  const handleOpenSessionDetail = (session) => {
    setSelectedSession(session);
    window.location.hash = `#/sessions/${session.id}`;
    const cohort = allCohortsForDisplay.find(c => c.id === session.cohortId);
    if (cohort?.schoolId) {
      ensureTeachersLoaded(cohort.schoolId, cohort.branchId || null);
    }
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const handleCloseSessionDetail = () => {
    setSelectedSession(null);
    window.location.hash = '#/sessions';
  };

  const handleStartSessionEdit = (session) => {
    const isGroup = !!session.groupId;
    const allGroups = Object.values(schoolGroupsMap).flat();
    const group = isGroup ? allGroups.find(g => g.id === session.groupId) : null;
    const cohort = !isGroup ? allCohortsForDisplay.find(c => c.id === session.cohortId) : null;
    const parentGroup = (!isGroup && cohort) ? allGroups.find(g => g.cohortIds && g.cohortIds.includes(session.cohortId)) : null;
    const schoolId = group ? String(group.schoolId || '') : (cohort ? String(cohort.schoolId || '') : '');
    const levelYear = (cohort && cohort.levelYear) ?? (parentGroup && parentGroup.year) ?? null;

    setSessionFormState({
      editingSessionId: session.id,
      targetType: isGroup ? 'group' : 'cohort',
      cohortId: session.cohortId || '',
      groupId: session.groupId || '',
      schoolId,
      levelYear,
      sessionType: session.sessionType,
      sessionNumber: session.sessionNumber,
      scheduledAt: session.scheduledAt ? session.scheduledAt.split(/[T ]/)[0] : '',
      meetingLink: session.meetingLink || '',
      comments: session.comments || '',
      resources: (session.resources || []).length > 0
        ? session.resources.map(String)
        : getAutoResources(session.sessionType, levelYear),
      customLinks: session.customLinks || [],
      timeSlot: session.timeSlot || '',
      auditors: [...new Set((session.auditors || []).map(String))],
      counselorId: session.counselorId || '',
      lockContext: true,
    });
    setSelectedSession(null);
    setSessionFormClashError('');
  };

  const handleUpdateCohortLevelYear = async (cohortId, levelYear) => {
    const nextLevelYear = levelYear || null;

    try {
      const updatedCohort = await mentorApi.updateCohort(cohortId, { level_year: nextLevelYear });
      if (updatedCohort) {
        syncWorkspaceCohort(workspaceDispatch, updatedCohort);
        setSelectedCohort(prev => {
          if (!prev || String(prev.id) !== String(cohortId)) return prev;
          return { ...prev, ...updatedCohort };
        });
      }
      showToast('Programme year updated.');
    } catch (err) {
      console.error('Failed to update cohort programme year via mentor API:', err);
      showToast(formatApiError(err, 'Programme year update'));
    }
  };

  const handleCancelSession = async (sessionId) => {
    try {
      await mentorApi.deleteSession(sessionId);
    } catch (err) {
      showToast(formatApiError(err, 'Cancel session'));
      throw err;
    }
    const refreshed = await refreshSessionAndProjections(sessionId);
    if (!refreshed) {
      const existing = sessions.find(s => String(s.id) === String(sessionId));
      if (existing) {
        syncWorkspaceSession(workspaceDispatch, { ...existing, status: 'Cancelled' });
      }
    }
    showToast('Session cancelled.');
  };

  const handleSessionStatusChange = async (sessionId, status) => {
    let updated = null;
    try {
      updated = await mentorApi.updateSession(sessionId, { status });
    } catch (err) {
      showToast(formatApiError(err, 'Update session status'));
      throw err;
    }
    await refreshSessionAndProjections(sessionId);
    const label = String(status).charAt(0).toUpperCase() + String(status).slice(1);
    if (updated?.nextSessionOpened?.id) {
      showToast(`Session marked ${label}. Next session is now live — nominations are open.`);
    } else {
      showToast(`Session marked ${label}.`);
    }
  };

  // Clash detection logic
  const checkAuditorClash = (auditorId, dateStr, slot, excludeSessionId = null) => {
    if (!dateStr || !slot) return false;
    const targetDate = dateStr.split('T')[0];

    return sessions.some(session => {
      if (excludeSessionId && String(session.id) === String(excludeSessionId)) return false;
      const sessionDate = session.scheduledAt.split(/[T ]/)[0];
      const sameTime = sessionDate === targetDate && slotsMatch(session, slot);
      const isActive = session.status === 'Planned' || session.status === 'Live';
      return isActive && sameTime && session.auditors.includes(auditorId);
    });
  };

  const handleSessionToggleAuditor = (auditorId) => {
    if (!sessionFormState.scheduledAt || !sessionFormState.timeSlot) return;

    const hasClash = checkAuditorClash(
      auditorId,
      sessionFormState.scheduledAt,
      sessionFormState.timeSlot,
      sessionFormState.editingSessionId || null
    );

    if (hasClash) {
      setSessionFormClashError('This auditor is already assigned to another session in this time slot');
      return; // HARD BLOCK: Prevents selection entirely
    }

    setSessionFormState(prev => {
      const auditorKey = String(auditorId);
      const current = prev.auditors.map(String);
      const auditorsList = current.includes(auditorKey)
        ? current.filter(id => id !== auditorKey)
        : [...current, auditorKey];
      return { ...prev, auditors: auditorsList };
    });
    setSessionFormClashError('');
  };

  const handleSessionToggleResource = (resourceId) => {
    setSessionFormState(prev => {
      const isSelected = prev.resources.includes(resourceId);
      const resourcesList = isSelected
        ? prev.resources.filter(id => id !== resourceId)
        : [...prev.resources, resourceId];
      return { ...prev, resources: resourcesList };
    });
  };

  const handleCreateSessionSubmit = async (e) => {
    e.preventDefault();
    if (sessionFormSubmitting) return;
    const { targetType, cohortId, groupId, sessionType, sessionNumber, scheduledAt, meetingLink, comments, resources: selectedRes, timeSlot, auditors: assignedAuditors, counselorId } = sessionFormState;
    const uniqueAssignedAuditors = [...new Set(assignedAuditors.map(String))];
    const resourceIds = (selectedRes || []).map(String);
    const isGroup = targetType === 'group';
    const hasTarget = isGroup ? !!groupId : !!cohortId;
    if (!hasTarget || !scheduledAt || !meetingLink || !timeSlot) {
      setSessionFormClashError('Please fill out all required fields (cohort/group, date, time slot, meeting link).');
      return;
    }

    const { editingSessionId } = sessionFormState;

    const clashFound = uniqueAssignedAuditors.some(id => checkAuditorClash(id, scheduledAt, timeSlot, editingSessionId || null));
    if (clashFound) {
      setSessionFormClashError('Operational Conflict: This auditor is already assigned to another session in this time slot');
      return;
    }

    setSessionFormSubmitting(true);
    setSessionFormClashError('');

    if (editingSessionId) {
      const apiPayload = {
        scheduled_at: scheduledAt,
        ...formatSlotForApi(timeSlot),
        meeting_link: meetingLink,
        comments,
        resource_ids: resourceIds,
        auditor_assignments: uniqueAssignedAuditors.map((auditorId, index) => ({
          user_id: Number(auditorId),
          breakout_room_number: index + 1,
        })),
      };
      if (counselorId) {
        apiPayload.counselor_id = Number(counselorId);
      }

      try {
        await mentorApi.updateSession(editingSessionId, apiPayload);
      } catch (err) {
        console.error('Failed to update session via mentor API:', err);
        showToast(formatApiError(err, 'Session update'));
        setSessionFormSubmitting(false);
        return;
      }

      await refreshSessionAndProjections(editingSessionId);
      setSessionFormState(null);
      setSessionFormSubmitting(false);
      showToast('Session updated.');
      return;
    }

    let sessionCohortId = cohortId;
    let sessionGroupId = null;

    if (isGroup) {
      sessionGroupId = groupId;
    } else {
      sessionCohortId = cohortId;
    }

    const apiPayload = {
      ...(isGroup
        ? { group_id: Number(sessionGroupId) }
        : { cohort_id: Number(sessionCohortId) }),
      session_number: Number(sessionNumber),
      session_type: sessionType.toLowerCase().startsWith('orientation') ? 'orientation' : 'council',
      council_number: sessionType.toLowerCase().startsWith('council') ? Number((sessionType.match(/\d+/) || [null])[0]) || null : null,
      scheduled_at: scheduledAt,
      ...formatSlotForApi(timeSlot),
      meeting_link: meetingLink,
      comments,
      resource_ids: resourceIds,
      counselor_id: counselorId ? Number(counselorId) : null,
      auditor_assignments: uniqueAssignedAuditors.map((auditorId, index) => ({
        user_id: Number(auditorId),
        breakout_room_number: index + 1,
      })),
    };

    let savedSession = null;
    try {
      savedSession = await mentorApi.createSession(apiPayload);
    } catch (err) {
      console.error('Failed to create session via mentor API:', err);
      setSessionFormClashError(formatApiError(err, 'Session create'));
      setSessionFormSubmitting(false);
      return;
    }

    if (savedSession?.id) {
      await refreshSessionAndProjections(savedSession.id);
    }
    setSessionFormState(null);
    setSessionFormSubmitting(false);
    setActiveTab('sessions');
    window.location.hash = '#/sessions';
    showToast('Session saved to mentor API.');
  };

  const isTypeCompleted = (cohortId, typeName) => {
    if (!cohortId) return false;
    const cohortSessions = sessions.filter(s => s.cohortId === cohortId);
    return cohortSessions.some(s => s.sessionType === typeName);
  };


  // -------------------------------------------------------------
  // EVALUATION APPROVALS HELPERS
  // -------------------------------------------------------------
  const toggleEvaluationExpanded = (evalId) => {
    setExpandedEvaluations(prev =>
      prev.includes(evalId)
        ? prev.filter(id => id !== evalId)
        : [...prev, evalId]
    );
  };

  const handleApproveEvaluation = async (evalId) => {
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const dateStr = new Date().toLocaleDateString([], { month: 'short', day: 'numeric' });
    const approvedBy = currentUser?.name || 'Counselor';
    try {
      await mentorApi.approveEvaluation(evalId);
    } catch (err) {
      console.error('Failed to approve evaluation via mentor API:', err);
      showToast(formatApiError(err, 'Evaluation approval'));
      return;
    }
    invalidateQuery(APPROVALS_EVAL_QUERY_KEY);
    setReports(prev =>
      prev.map(rep =>
        rep.id === evalId ? { ...rep, status: 'approved', approvedBy, approvedTime: `${dateStr}, ${timeStr}`, rejectionReason: '' } : rep
      )
    );
    if (rejectionInputId === evalId) {
      setRejectionInputId(null);
      setRejectionReasonText('');
    }
  };

  const handleStartRejectEvaluation = (evalId) => {
    setRejectionInputId(evalId);
    setRejectionReasonText('');
  };

  const handleCancelRejectEvaluation = () => {
    setRejectionInputId(null);
    setRejectionReasonText('');
  };

  const handleConfirmRejectEvaluation = async (evalId) => {
    if (!rejectionReasonText.trim()) {
      alert('Please provide a rejection reason before confirming.');
      return;
    }
    try {
      await mentorApi.rejectEvaluation(evalId, rejectionReasonText.trim());
    } catch (err) {
      console.error('Failed to reject evaluation via mentor API:', err);
      showToast(formatApiError(err, 'Evaluation rejection'));
      return;
    }
    invalidateQuery(APPROVALS_EVAL_QUERY_KEY);
    setReports(prev =>
      prev.map(rep =>
        rep.id === evalId ? { ...rep, status: 'rejected', rejectionReason: rejectionReasonText.trim() } : rep
      )
    );
    setRejectionInputId(null);
    setRejectionReasonText('');
  };

  const handleApproveYear2Evaluation = async (item) => {
    try {
      if (item.type === 'squad') {
        await mentorApi.approveYear2SquadAssessment(item.id);
      } else {
        await mentorApi.approveYear2StudentAssessment(item.id);
      }
      invalidateQuery(YEAR2_APPROVALS_QUERY_KEY);
      setYear2ApprovalItems(prev => prev.map(row =>
        row.uiId === item.uiId ? { ...row, status: 'approved' } : row
      ));
      showToast('Year 2 evaluation approved.');
    } catch (err) {
      console.error('Failed to approve Year 2 evaluation:', err);
      showToast(formatApiError(err, 'Year 2 evaluation approval'));
    }
  };

  const handleStartRejectYear2Evaluation = (item) => {
    setRejectionInputId(item.uiId);
    setRejectionReasonText('');
  };

  const handleConfirmRejectYear2Evaluation = async (item) => {
    if (!rejectionReasonText.trim()) {
      alert('Please provide a rejection reason before confirming.');
      return;
    }
    try {
      if (item.type === 'squad') {
        await mentorApi.rejectYear2SquadAssessment(item.id, rejectionReasonText.trim());
      } else {
        await mentorApi.rejectYear2StudentAssessment(item.id, rejectionReasonText.trim());
      }
      invalidateQuery(YEAR2_APPROVALS_QUERY_KEY);
      setYear2ApprovalItems(prev => prev.map(row =>
        row.uiId === item.uiId ? { ...row, status: 'rejected', rejection_reason: rejectionReasonText.trim() } : row
      ));
      setRejectionInputId(null);
      setRejectionReasonText('');
      showToast('Year 2 evaluation rejected.');
    } catch (err) {
      console.error('Failed to reject Year 2 evaluation:', err);
      showToast(formatApiError(err, 'Year 2 evaluation rejection'));
    }
  };

  const buildEvaluationDraftFromReports = (sessionId, sessionReports, cohortId) => {
    const base = initEvaluation(sessionId, cohortId);
    const evals = { ...base.evaluations };

    sessionReports.forEach(rep => {
      if (!rep.studentId) return;
      const studentKey = String(rep.studentId);
      const role = (rep.role || rep.roleBadge || '').toLowerCase();
      const data = {
        positiveKeywords: rep.keywords || [],
        negativeKeywords: rep.coachingKeywords || [],
        rating: rep.rating ?? (role === 'chair' ? null : ''),
        comment: rep.comment || '',
      };

      if (role === 'challenger') {
        const idx = base.challengers.findIndex(c => String(c.studentId) === studentKey);
        if (idx >= 0) {
          base.challengers[idx] = { ...base.challengers[idx], ...data, studentId: rep.studentId };
        } else {
          const emptyIdx = base.challengers.findIndex(c => !c.studentId);
          if (emptyIdx >= 0) {
            base.challengers[emptyIdx] = { ...base.challengers[emptyIdx], ...data, studentId: rep.studentId };
            if (emptyIdx === 3) base.show4thChallenger = true;
          }
        }
      } else {
        evals[studentKey] = data;
      }
    });

    return {
      ...base,
      evaluations: evals,
      status: 'draft',
      submittedAt: null,
    };
  };

  const handleReopenEvaluation = async (sessionId) => {
    const sessionReports = reports.filter(r => String(r.sessionId) === String(sessionId));
    if (sessionReports.length === 0) {
      showToast('No evaluations found for this session.');
      return;
    }

    const reopenedRows = [];
    try {
      for (const rep of sessionReports) {
        const updated = await mentorApi.reopenEvaluation(rep.id);
        reopenedRows.push(updated);
      }
    } catch (err) {
      console.error('Failed to reopen evaluation via mentor API:', err);
      showToast(formatApiError(err, 'Reopen evaluation'));
      return;
    }

    setReports(prev => mergeReopenResponsesIntoReports(prev, sessionId, reopenedRows));

    const cohortId = sessions.find(s => String(s.id) === String(sessionId))?.cohortId || evaluatingCohortId;
    setSessionEvaluations(prev => {
      const next = { ...prev };
      delete next[sessionId];
      next[sessionId] = buildEvaluationDraftFromReports(sessionId, sessionReports, cohortId);
      return next;
    });

    setViewingPastEvaluationId(null);
    setEvaluatingSessionId(sessionId);
    const sess = sessions.find(s => String(s.id) === String(sessionId));
    const yearTab = sess?.levelYear === 'Year 2' ? 'my_sessions_y2' : 'my_sessions_y1';
    window.location.hash = `#/${yearTab}/${sessionId}/evaluate`;
  };

  // -------------------------------------------------------------
  // COUNSELOR ACTIONS AND HELPERS
  // -------------------------------------------------------------
  const getSessionRoomAssignments = (session) => {
    if (!session) return {};
    const assigned = sessionRoomAssignments[session.id];
    if (assigned) return assigned;
    
    // If not assigned yet, derive room assignments from API-loaded auditors.
    const newAssigned = {};
    session.auditors.forEach((audId, index) => {
      newAssigned[index + 1] = audId;
    });
    return newAssigned;
  };

  const handleReassignRoom = async (sessionId, currentRoomNum, targetRoomNum) => {
    const updatedRooms = { ...(sessionRoomAssignments[sessionId] || getSessionRoomAssignments({ id: sessionId, auditors: sessions.find(s => s.id === sessionId)?.auditors || [] })) };
    const currentAuditorId = updatedRooms[currentRoomNum];
    const targetAuditorId = updatedRooms[targetRoomNum];
    updatedRooms[currentRoomNum] = targetAuditorId || null;
    updatedRooms[targetRoomNum] = currentAuditorId || null;
    const payload = Object.entries(updatedRooms)
      .filter(([, auditorId]) => auditorId)
      .map(([roomNumber, auditorId]) => ({
        user_id: Number(auditorId),
        breakout_room_number: Number(roomNumber),
      }));
    try {
      await mentorApi.updateSession(sessionId, { auditor_assignments: payload });
    } catch (err) {
      console.error('Failed to persist room reassignment via mentor API:', err);
      showToast(formatApiError(err, 'Room reassignment'));
      return;
    }
    await refreshSessionAndProjections(sessionId);
    showToast(`Auditor reassigned to Room ${targetRoomNum}.`);
  };

  const handleMarkNoShow = async (sessionId, auditorId) => {
    try {
      await mentorApi.markSessionNoShow(sessionId, auditorId);
    } catch (err) {
      console.error('Failed to mark no-show via mentor API:', err);
      showToast(formatApiError(err, 'Auditor no-show'));
      return;
    }
    await refreshSessionAndProjections(sessionId);
    setSessionNoShows(prev => ({
      ...prev,
      [sessionId]: {
        ...(prev[sessionId] || {}),
        [auditorId]: true
      }
    }));
    showToast('Auditor marked as no-show.');
  };

  // -------------------------------------------------------------
  // SPOC DATA MODEL HELPERS
  // -------------------------------------------------------------
  const getSessionCohortStudents = (sessionId, cohortId = null) => {
    const session = sessions.find(s => String(s.id) === String(sessionId));
    if (!session) return [];
    const targetCohortId = cohortId || outcomeFormCohortId || session.cohortId || nominationsCohortId;
    const perCohortIds = targetCohortId ? session.cohortStudentIdsByCohort?.[targetCohortId] : null;
    if (perCohortIds?.length) {
      return resolveStudents(perCohortIds, liveStudents);
    }
    if (!session.groupId && session.cohortStudentIds?.length) {
      return resolveStudents(session.cohortStudentIds, liveStudents);
    }
    const cohort = allCohortsForDisplay.find(c => String(c.id) === String(targetCohortId));
    if (!cohort) return [];
    return resolveStudents(cohort.studentIds, liveStudents);
  };

  const getChallengerCandidates = (sessionId) => getSessionCohortStudents(sessionId, outcomeFormCohortId);

  const getPresenterCandidates = (sessionId) => {
    const roles = getSessionRolesFromMap(sessionRoles, sessionId, outcomeFormCohortId);
    if (roles.presenterIds?.length) {
      return resolveStudents(roles.presenterIds, liveStudents);
    }
    return getSessionCohortStudents(sessionId);
  };

  const getMentorCandidates = (sessionId) => {
    const roles = getSessionRolesFromMap(sessionRoles, sessionId, outcomeFormCohortId);
    if (roles.mentorIds?.length) {
      return resolveStudents(roles.mentorIds, liveStudents);
    }
    return getSessionCohortStudents(sessionId);
  };

  const getSelectedChallengers = () => {
    const ids = [outcomeChallenger1, outcomeChallenger2, outcomeChallenger3];
    if (show4thChallenger && outcomeChallenger4) {
      ids.push(outcomeChallenger4);
    }
    return resolveStudents(ids, liveStudents);
  };

  // -------------------------------------------------------------
  // SPOC WORKFLOW HELPERS & HANDLERS
  // -------------------------------------------------------------
  const handleOpenOutcomeForm = (sessionId) => {
    const session = sessions.find(s => String(s.id) === String(sessionId));
    if (!session) return;
    const cohortId = session.groupId ? nominationsCohortId : (session.cohortId || '');
    setOutcomeFormCohortId(cohortId);
    const roles = getSessionRolesFromMap(sessionRoles, sessionId, cohortId);
    setActiveTab('nominations');
    // encode cohortId in hash for group sessions so cold-paste links can restore cohort context
    window.location.hash = session.groupId && cohortId
      ? `#/nominations/${sessionId}/${cohortId}`
      : `#/nominations/${sessionId}`;
    setOutcomeFormSessionId(sessionId);
    setOutcomeChallenger1(roles.challengerIds?.[0] || '');
    setOutcomeChallenger2(roles.challengerIds?.[1] || '');
    setOutcomeChallenger3(roles.challengerIds?.[2] || '');
    setOutcomeChallenger4(roles.challengerIds?.[3] || '');
    setShow4thChallenger(roles.challengerIds?.length > 3);
    setOutcomeBestPresenter(roles.bestPresenterId || '');
    setOutcomeBestMentor(roles.bestMentorId || '');
    setOutcomeBestChallenger(roles.bestChallengerId || '');
    setOutcomeChairScore(roles.chairScore || '');
    
    // Close other forms/cohort details
    setSelectedCohort(null);
    setWizardState(null);
    setSessionFormState(null);
  };

  const handleCancelOutcomeForm = () => {
    setOutcomeFormSessionId(null);
    setOutcomeFormCohortId('');
  };

  const handleSubmitOutcomes = async (e) => {
    e.preventDefault();
    if (!outcomeChallenger1 || !outcomeChallenger2 || !outcomeChallenger3) {
      alert('Please fill in all 3 required Challenger slots.');
      return false;
    }
    if (show4thChallenger && !outcomeChallenger4) {
      alert('Please select a student for the 4th Challenger slot.');
      return false;
    }
    if (!outcomeBestPresenter || !outcomeBestMentor || !outcomeBestChallenger) {
      alert('Please select best awards for Presenter, Mentor, and Challenger.');
      return false;
    }

    const scoreVal = parseFloat(outcomeChairScore);
    if (isNaN(scoreVal) || scoreVal < 1.0 || scoreVal > 9.9) {
      alert('Chair score must be a decimal number between 1.0 and 9.9 (e.g. 7.5).');
      return false;
    }

    // Update sessionRoles
    const challengers = [outcomeChallenger1, outcomeChallenger2, outcomeChallenger3];
    if (show4thChallenger && outcomeChallenger4) {
      challengers.push(outcomeChallenger4);
    }

    const payload = {
      challenger1_id: Number(challengers[0]),
      challenger2_id: Number(challengers[1]),
      challenger3_id: Number(challengers[2]),
      challenger4_id: show4thChallenger && challengers[3] ? Number(challengers[3]) : null,
      best_presenter_id: Number(outcomeBestPresenter),
      best_mentor_id: Number(outcomeBestMentor),
      best_challenger_id: Number(outcomeBestChallenger),
      chair_score: Number(outcomeChairScore),
    };

    const outcomeSession = sessions.find(s => String(s.id) === String(outcomeFormSessionId));
    const outcomeCohortId = outcomeSession?.groupId ? outcomeFormCohortId : null;

    try {
      await mentorApi.putSessionOutcomes(outcomeFormSessionId, payload, outcomeCohortId);
    } catch (err) {
      console.error('Failed to persist outcomes to mentor API:', err);
      showToast(formatApiError(err, 'Session outcomes'));
      return false;
    }

    await refreshSessionAndProjections(outcomeFormSessionId);

    setSpocTasks(prev => prev.filter(t => !(t.type === 'post_session_log' && t.sessionId === outcomeFormSessionId)));

    showToast('Session outcomes saved successfully!');
    setOutcomeFormSessionId(null);
    setOutcomeFormCohortId('');
    return true;
  };

  const handleSubmitNominations = async (e, sessionId) => {
    e.preventDefault();
    if (!sessionId) {
      alert('Session not found.');
      return false;
    }
    if (!nomChairId || !nomPresenter1 || !nomPresenter2 || !nomPresenter3 || !nomMentor1 || !nomMentor2 || !nomMentor3) {
      alert('Please select a student for all required roles.');
      return false;
    }
    if (show4thPresenter && !nomPresenter4) {
      alert('Please select the 4th Presenter.');
      return false;
    }
    if (show4thMentor && !nomMentor4) {
      alert('Please select the 4th Mentor.');
      return false;
    }

    const selectedIds = [
      nomChairId,
      nomPresenter1,
      nomPresenter2,
      nomPresenter3,
      nomMentor1,
      nomMentor2,
      nomMentor3
    ];
    if (show4thPresenter && nomPresenter4) selectedIds.push(nomPresenter4);
    if (show4thMentor && nomMentor4) selectedIds.push(nomMentor4);

    const uniqueIds = new Set(selectedIds);
    if (uniqueIds.size !== selectedIds.length) {
      alert('A student cannot be assigned to multiple roles in the same session.');
      return false;
    }

    const session = sessions.find(s => String(s.id) === String(sessionId));
    if (!session) {
      alert('Session not found.');
      return false;
    }

    const presenters = [nomPresenter1, nomPresenter2, nomPresenter3];
    if (show4thPresenter && nomPresenter4) presenters.push(nomPresenter4);
    const mentors = [nomMentor1, nomMentor2, nomMentor3];
    if (show4thMentor && nomMentor4) mentors.push(nomMentor4);

    const payload = {
      chair_id: Number(nomChairId),
      presenter1_id: Number(presenters[0]),
      presenter2_id: Number(presenters[1]),
      presenter3_id: Number(presenters[2]),
      presenter4_id: show4thPresenter && presenters[3] ? Number(presenters[3]) : null,
      mentor1_id: Number(mentors[0]),
      mentor2_id: Number(mentors[1]),
      mentor3_id: Number(mentors[2]),
      mentor4_id: show4thMentor && mentors[3] ? Number(mentors[3]) : null,
    };

    const nominationCohortId = session.groupId ? nominationsCohortId : null;

    try {
      await mentorApi.putSessionNominations(session.id, payload, nominationCohortId);
    } catch (err) {
      console.error('Failed to persist nominations to mentor API:', err);
      showToast(formatApiError(err, 'Role nominations'));
      return false;
    }

    await refreshSessionAndProjections(session.id);

    setSpocTasks(prev => prev.filter(t => !(t.type === 'nominations' && t.cohortId === nominationsCohortId)));

    showToast('Role nominations saved successfully!');

    setNomChairId('');
    setNomPresenter1('');
    setNomPresenter2('');
    setNomPresenter3('');
    setNomPresenter4('');
    setNomMentor1('');
    setNomMentor2('');
    setNomMentor3('');
    setNomMentor4('');
    setShow4thPresenter(false);
    setShow4thMentor(false);
    return true;
  };

  const sessionBelongsToCohort = useCallback((session, cohortId) => {
    if (String(session.cohortId) === String(cohortId)) return true;
    if (session.groupId && (session.groupCohortIds || []).map(String).includes(String(cohortId))) return true;
    return false;
  }, []);

  const handleYear2NomTasksRefresh = useCallback((cohortId, nominationPayloads = []) => {
    const cohort = allCohortsForDisplay.find((c) => String(c.id) === String(cohortId));
    setSpocTasks((prev) => {
      const others = prev.filter((t) => !(t.type === 'year2_nominations' && String(t.cohortId) === String(cohortId)));
      const additions = (nominationPayloads || [])
        .filter((nom) => nom && !councilNominationsComplete(nom.squads))
        .map((nom) => {
          const sess = sessions.find(
            (s) => sessionBelongsToCohort(s, cohortId)
              && councilNumberFromSession(s) === nom.council_number
              && isCouncilNominationEditable(s)
          );
          return {
            id: `year2-nom-${cohortId}-${nom.council_number}`,
            type: 'year2_nominations',
            cohortId,
            councilNumber: nom.council_number,
            sessionId: sess?.id || null,
            title: 'Nominate Year 2 chairs',
            sessionName: `${sess?.sessionType || `Council ${nom.council_number}`}${cohort ? ` — ${cohort.name}` : ''}`,
            timeLeftHours: hoursUntilSession(sess?.scheduledAt),
          };
        });
      return [...others, ...additions];
    });
  }, [allCohortsForDisplay, sessions, sessionBelongsToCohort]);

  const loadYear2Discovery = useCallback(async () => {
    if (year2DiscoveryInFlightRef.current) {
      return year2DiscoveryInFlightRef.current;
    }

    const discoveryPromise = (async () => {
      setYear2DiscoveryLoading(true);
      setYear2DiscoveryError('');
      try {
        const discovery = await fetchYear2Discovery();
        setYear2Discovery(discovery);
        if (currentUser?.role === 'spoc') {
          const cohorts = Object.values(discovery?.cohortsBySchool || {}).flat();
          cohorts.forEach((cohort) => {
            const setupStatus = cohort?.setup_status || cohort?.setupStatus || 'not_started';
            if (setupStatus === 'finalized') {
              handleYear2NomTasksRefresh(cohort.cohort_id ?? cohort.id, []);
            }
          });
        }
        return discovery;
      } catch (err) {
        setYear2Discovery(null);
        setYear2DiscoveryError(formatApiError(err, 'Loading Year 2 discovery'));
        throw err;
      } finally {
        setYear2DiscoveryLoading(false);
        if (year2DiscoveryInFlightRef.current === discoveryPromise) {
          year2DiscoveryInFlightRef.current = null;
        }
      }
    })();

    year2DiscoveryInFlightRef.current = discoveryPromise;
    return discoveryPromise;
  }, [currentUser?.role, handleYear2NomTasksRefresh]);

  useEffect(() => {
    if (apiLoading) return;
    if (!currentUser?.role) return;
    if (currentUser.role !== 'program_administrator' && currentUser.role !== 'spoc' && currentUser.role !== 'auditor') return;
    if (year2Discovery || year2DiscoveryLoading || year2DiscoveryError) return;
    mentorRequestLog.setCallerTag('lazy:year2-discovery');
    void loadYear2Discovery().catch(() => {});
  }, [apiLoading, currentUser?.role, loadYear2Discovery, year2Discovery, year2DiscoveryLoading, year2DiscoveryError]);

  useEffect(() => {
    setYear2Discovery(null);
    setYear2DiscoveryError('');
    setYear2DiscoveryLoading(false);
    year2DiscoveryInFlightRef.current = null;
  }, [currentUser?.id]);

  // -------------------------------------------------------------
  // AUDITOR WORKFLOW HELPERS & HANDLERS
  // -------------------------------------------------------------
  const initEvaluation = (sessId, cohortId = evaluatingCohortId) => {
    const roles = getSessionRolesFromMap(sessionRoles, sessId, cohortId);
    const evals = {};
    
    if (roles.chairId) {
      evals[roles.chairId] = { positiveKeywords: [], negativeKeywords: [], rating: null, comment: '' };
    }
    if (roles.presenterIds) {
      roles.presenterIds.forEach(id => {
        evals[id] = { positiveKeywords: [], negativeKeywords: [], rating: '', comment: '' };
      });
    }
    if (roles.mentorIds) {
      roles.mentorIds.forEach(id => {
        evals[id] = { positiveKeywords: [], negativeKeywords: [], rating: '', comment: '' };
      });
    }
    
    const { challengers, show4thChallenger } = buildEvaluationChallengerSlots(roles.challengerIds);

    return {
      sessionId: sessId,
      status: 'draft',
      evaluations: evals,
      challengers,
      show4thChallenger,
      submittedAt: null
    };
  };

  // Seed auditor eval challenger picks from SPOC-logged outcomes (after boot refresh or async session load).
  useEffect(() => {
    if (!evaluatingSessionId || currentUser?.role !== 'auditor') return;
    const challengerIds = getSessionRolesFromMap(sessionRoles, evaluatingSessionId, evaluatingCohortId).challengerIds || [];
    if (challengerIds.length === 0) return;

    setSessionEvaluations(prev => {
      const current = prev[evaluatingSessionId];
      if (current && !evaluationChallengersAreEmpty(current.challengers)) {
        return prev;
      }
      const { challengers, show4thChallenger } = buildEvaluationChallengerSlots(challengerIds);
      const base = current || initEvaluation(evaluatingSessionId);
      return {
        ...prev,
        [evaluatingSessionId]: {
          ...base,
          challengers,
          show4thChallenger,
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluatingSessionId, evaluatingCohortId, currentUser, sessionRoles]);

  useEffect(() => {
    if (!evaluatingSessionId) {
      setEvaluatingCohortId('');
      return;
    }
    const session = sessions.find(s => String(s.id) === String(evaluatingSessionId));
    if (!session) return;
    if (session.cohortId) {
      setEvaluatingCohortId(session.cohortId);
      return;
    }
    if (session.groupId) {
      const nominatedCohort = (session.groupCohortIds || []).find(
        cohortId => session.nominationsByCohort?.[cohortId]?.chairId
      );
      setEvaluatingCohortId(nominatedCohort || session.groupCohortIds?.[0] || '');
    }
  }, [evaluatingSessionId, sessions]);

  useEffect(() => {
    if (!evaluatingSessionId) {
      setYear2Squads([]);
      return;
    }
    const session = sessions.find(s => String(s.id) === String(evaluatingSessionId));
    const cohortForYear = allCohortsForDisplay.find(
      c => String(c.id) === String(evaluatingCohortId || session?.cohortId)
    );
    const resolvedLevelYear = session?.levelYear ?? cohortForYear?.levelYear ?? null;
    if (!session || resolvedLevelYear !== 'Year 2') {
      setYear2Squads([]);
      return;
    }
    const cohortId = evaluatingCohortId || session.cohortId;
    if (!cohortId) return;
    mentorApi.getYear2Squads(cohortId)
      .then(setYear2Squads)
      .catch(err => console.warn('Failed to load Year 2 squads for auditor workspace:', err));
  }, [evaluatingSessionId, evaluatingCohortId, sessions, allCohortsForDisplay]);

  const updateEvaluationDraft = (updater) => {
    setSessionEvaluations(prev => {
      const current = prev[evaluatingSessionId] || initEvaluation(evaluatingSessionId);
      const updated = typeof updater === 'function' ? updater(current) : updater;
      return {
        ...prev,
        [evaluatingSessionId]: updated
      };
    });
  };

  // Sync auditorSessionStatuses from the live session on 422 — shared by claim, respond, withdraw.
  const syncAuditorStatusFrom422 = async (sessionId, actionLabel) => {
    const session = await refreshSessionAndProjections(sessionId);
    const status = session ? projectAuditorStatusFromSession(session) : null;
    if (status) {
      showToast(`Already ${status} — status updated.`);
      return true;
    }
    showToast(`${actionLabel} failed — please refresh and try again.`);
    return false;
  };

  const handleClaimSession = async (sessionId) => {
    try {
      await mentorApi.respondToSessionAuditor(sessionId, 'accepted');
    } catch (err) {
      console.error('Failed to claim session:', err);
      if (err?.status === 422) { await syncAuditorStatusFrom422(sessionId, 'Claim'); return; }
      showToast(formatApiError(err, 'Session claim'));
      return;
    }
    await refreshSessionAndProjections(sessionId);
    showToast('Session claimed successfully!');
  };

  const handleAuditorResponse = async (sessionId, status) => {
    try {
      await mentorApi.respondToSessionAuditor(sessionId, status);
    } catch (err) {
      console.error('Failed to update auditor assignment via mentor API:', err);
      if (err?.status === 422) { await syncAuditorStatusFrom422(sessionId, 'Response'); return; }
      showToast(formatApiError(err, 'Auditor response'));
      return;
    }
    await refreshSessionAndProjections(sessionId);
    showToast(status === 'accepted' ? 'Session assignment accepted!' : 'Session assignment declined.');
  };

  const handleAuditorWithdraw = async (sessionId) => {
    try {
      await mentorApi.withdrawSessionAuditor(sessionId);
    } catch (err) {
      console.error('Failed to withdraw auditor assignment via mentor API:', err);
      if (err?.status === 422) { await syncAuditorStatusFrom422(sessionId, 'Withdraw'); return; }
      showToast(formatApiError(err, 'Auditor withdraw'));
      return;
    }
    await refreshSessionAndProjections(sessionId);
    showToast('Withdrawn from session successfully.');
  };

  // Stable identity so children that receive showToast as onError/onSuccess do not
  // remount effects on every toast (see BUG-year2-squad-setup-error-reload-01).
  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage('');
    }, 4000);
  }, []);

  // Render SSO handoff landing (portahead -> /sso?code=...)
  if (ssoState === 'pending') {
    return (
      <div className="welcome-overlay">
        <div className="welcome-content">
          <div className="welcome-spinner"></div>
          <h1 className="welcome-title">Signing you in...</h1>
          <p className="welcome-subtitle">Redirecting to operations console...</p>
        </div>
      </div>
    );
  }
  if (ssoState === 'error') {
    return (
      <div className="login-container">
        <div className="login-background-decor"></div>
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              IFSPD <span>Mentor Panel</span>
            </div>
            <p className="login-subtitle">mentor.portahead.com</p>
          </div>
          <div style={{ color: '#ef4444', background: '#fef2f2', padding: '12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, marginBottom: '16px', border: '1px solid #fee2e2' }}>
            ⚠️ {ssoErrorMsg}
          </div>
          <a href="https://portahead.com/login/mentor" className="btn-login" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  // Render Login
  if (authState === 'logged_out') {
    return (
      <div className="login-container">
        <div className="login-background-decor"></div>
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              IFSPD <span>Mentor Panel</span>
            </div>
            <p className="login-subtitle">mentor.portahead.com</p>
          </div>

          <form onSubmit={handleLoginSubmit}>
            {errorMsg && (
              <div style={{ color: '#ef4444', background: '#fef2f2', padding: '12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, marginBottom: '16px', border: '1px solid #fee2e2' }}>
                ⚠️ {errorMsg}
              </div>
            )}

            <div className="login-form-group">
              <label htmlFor="email">Email Address</label>
              <div className="login-input-wrapper">
                <input
                  id="email"
                  type="email"
                  className="login-input"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="login-form-group">
              <label htmlFor="password">Password</label>
              <div className="login-input-wrapper" style={{ position: 'relative' }}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="login-input"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingRight: '44px' }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    color: 'var(--text-muted)',
                    fontSize: '16px',
                    lineHeight: 1,
                  }}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button type="submit" className="btn-login" disabled={isLoggingIn}>
              {isLoggingIn ? 'Signing in…' : 'Authenticate'}
            </button>
          </form>

        </div>
      </div>
    );
  }

  // Render Welcome redirector
  if (authState === 'welcoming') {
    return (
      <div className="welcome-overlay">
        <div className="welcome-content">
          <div className="welcome-spinner"></div>
          <h1 className="welcome-title">Welcome back, {currentUser?.name}</h1>
          <p className="welcome-subtitle">Redirecting to operations console...</p>
          <span className={`role-badge ${currentUser?.badgeClass}`}>
            {currentUser?.roleLabel}
          </span>
        </div>
      </div>
    );
  }

  // Render Shell
  return (
    <div className="app-layout">
      {toastMessage && (
        <div className="toast-container">
          <div className="toast-message">
            ✓ {toastMessage}
          </div>
        </div>
      )}
      {bootPartial && bootWarnings.length > 0 && !bootBannerDismissed && (
        <div className="boot-degraded-banner" role="alert" data-testid="boot-degraded-banner">
          <div className="boot-degraded-banner-text">
            <strong>Some workspace data could not load.</strong>
            <span>Counts and details may be incomplete until you retry.</span>
            {bootWarnings.length > 1 && (
              <span className="boot-degraded-banner-meta">{bootWarnings.length} issues detected</span>
            )}
          </div>
          <div className="boot-degraded-banner-actions">
            <button
              type="button"
              className="btn-secondary-action"
              onClick={() => loadMentorWorkspaceData()}
              disabled={apiLoading}
            >
              {apiLoading ? 'Retrying…' : 'Retry'}
            </button>
            <button
              type="button"
              className="btn-secondary-action"
              onClick={() => setBootBannerDismissed(true)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {/* Top Header */}
      <header className="app-header">
        <div className="header-left">
          <div className="brand-badge-container">
            <a href="#/dashboard" className="header-brand" onClick={handleBrandClick}>
              IFSPD <span>Mentor Panel</span>
            </a>
            {currentUser?.role === 'counselor' ? (
              <span className="role-badge counselor header-counselor-badge">
                COUNSELOR
              </span>
            ) : currentUser?.role === 'auditor' ? (
              <span className="role-badge auditor header-auditor-badge">
                AUDITOR
              </span>
            ) : (
              <span className={`role-badge ${currentUser?.badgeClass} mobile-only-badge`}>
                {currentUser?.roleLabel}
              </span>
            )}
          </div>
        </div>
        
        <div className="header-profile">
          <div className="user-info">
            <button
              type="button"
              className="user-name"
              onClick={() => setProfileOpen(true)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}
              title="Open profile"
            >
              {profileOverlay?.display_name || currentUser?.name}
            </button>
            <span className={`role-badge ${currentUser?.badgeClass}`}>
              {currentUser?.roleLabel}
            </span>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>
      {profileOpen && (
        <ProfileModal
          currentUser={currentUser}
          onClose={() => setProfileOpen(false)}
          showToast={showToast}
          onSaved={(saved) => setProfileOverlay(saved)}
        />
      )}

      <div className="app-container">
        {/* Sidebar Nav */}
        <aside className="app-sidebar">
          <nav>
            {navItems.map((item) => {
              if (item.children) {
                const hasActiveChild = activeTab === item.id;
                const isExpanded = expandedNavGroups.has(item.id) || hasActiveChild;
                return (
                  <div key={item.id} className="sidebar-nav-group">
                    <button
                      className={`sidebar-nav-group-toggle ${isExpanded ? 'expanded' : ''} ${hasActiveChild ? 'has-active-child' : ''}`}
                      onClick={() => {
                        setExpandedNavGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                          return next;
                        });
                      }}
                    >
                      <span className="sidebar-nav-group-toggle-left">
                        <span>{item.icon}</span>
                        {item.label}
                      </span>
                      <span className="sidebar-nav-caret">▶</span>
                    </button>
                    {isExpanded && (
                      <div className="sidebar-nav-subitems">
                        {item.children.map((child) => (
                          <button
                            key={child.id}
                            className={`sidebar-nav-subitem ${activeTab === item.id && esgScreen === child.id ? 'active' : ''}`}
                            onClick={() => handleNavigateToChild(item.id, child.id)}
                          >
                            {child.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={item.id}
                  className={`sidebar-nav-item ${activeTab === item.id ? 'active' : ''} ${item.id === 'resources' ? 'sidebar-nav-item--learning-space' : ''}`}
                  onClick={() => handleNavigate(item.id)}
                >
                  <span>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>
          <div className="sidebar-footer">
            <div>Glosco Fellowship</div>
            <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7 }}>v2.0 Operations Panel</div>
          </div>
        </aside>

        {/* Content Wrapper */}
        <main className="app-content animate-fade-in">
          {['program_administrator', 'counselor', 'auditor'].includes(currentUser?.role) && (
            <ActionItemsStrip
              role={currentUser.role}
              onOpen={(item) => {
                const screen = item?.target?.screen;
                if (screen === 'approvals') handleNavigate('approvals');
                else if (screen === 'attendance') handleNavigate('attendance');
                else if (screen === 'support') handleNavigateToChild('esg', 'support');
                else if (screen === 'evaluations' || screen === 'sessions') {
                  handleNavigate(currentUser.role === 'auditor' ? 'my_sessions_y1' : 'sessions');
                }
              }}
            />
          )}
          {[
            cohortsFetch.status === 'error' && cohortsFetch.error,
            sessionsFetch.status === 'error' && sessionsFetch.error,
            evaluationsFetch.status === 'error' && evaluationsFetch.error,
          ].filter(Boolean).length > 0 && (
            <div
              role="alert"
              style={{
                margin: '-24px -40px 24px -40px',
                padding: '12px 40px',
                background: '#fff4f4',
                borderBottom: '1px solid #f5c2c2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ color: '#8a1f1f', fontSize: '14px', fontWeight: 600 }}>
                {[
                  cohortsFetch.status === 'error' && cohortsFetch.error,
                  sessionsFetch.status === 'error' && sessionsFetch.error,
                  evaluationsFetch.status === 'error' && evaluationsFetch.error,
                ].filter(Boolean).join(' ')}
              </div>
              <button
                type="button"
                className="btn-secondary-action"
                onClick={() => loadMentorWorkspaceData()}
                style={{ flexShrink: 0 }}
              >
                Retry
              </button>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCREEN: COHORT CREATION WIZARD */}
          {/* ========================================================= */}
          {wizardState && (
            <CreateCohortWizard
              wizardState={wizardState}
              wizardSearchText={wizardSearchText}
              filteredLeftStudents={filteredLeftStudents}
              selectedStudents={selectedStudents}
              highlightedStudentId={highlightedStudentId}
              schools={liveSchools}
              spocs={activeSpocs}
              counselors={activeCounselors}
              auditors={activeAuditors}
              handleCancelCohortWizard={handleCancelCohortWizard}
              handleWizardSchoolSelect={handleWizardSchoolSelect}
              setWizardSearchText={setWizardSearchText}
              setHighlightedStudentId={setHighlightedStudentId}
              handleAddStudent={handleAddStudent}
              handleRemoveStudent={handleRemoveStudent}
              setWizardState={setWizardState}
              handleWizardToggleCounselor={handleWizardToggleCounselor}
              handleWizardToggleAuditorSelect={handleWizardToggleAuditorSelect}
              handleWizardToggleAuditorStandby={handleWizardToggleAuditorStandby}
              handleCreateCohortSubmit={handleCreateCohortSubmit}
              isStudentAssignedToAnyCohort={isStudentAssignedToAnyCohort}
              getAssignedCohortName={getAssignedCohortName}
            />
          )}

          {/* ========================================================= */}
          {/* SCREEN: COHORT DETAILS PAGE */}
          {/* ========================================================= */}
          {selectedCohort && !wizardState && !sessionFormState && (
            <CohortDetail
              selectedCohort={selectedCohort}
              cohortDetailTab={cohortDetailTab}
              sessions={sessions}
              auditors={activeAuditors}
              allStudents={liveStudents}
              currentUser={currentUser}
              spocTasks={spocTasks}
              setCohortDetailTab={setCohortDetailTab}
              setSelectedCohort={setSelectedCohort}
              setActiveTab={setActiveTab}
              handleStartSessionForm={handleStartSessionForm}
              handleOpenOutcomeForm={handleOpenOutcomeForm}
              onSessionClick={handleOpenSessionDetail}
              setNominationsCohortId={setNominationsCohortId}
              setNomChairId={setNomChairId}
              setNomPresenter1={setNomPresenter1}
              setNomPresenter2={setNomPresenter2}
              setNomPresenter3={setNomPresenter3}
              setNomPresenter4={setNomPresenter4}
              setNomMentor1={setNomMentor1}
              setNomMentor2={setNomMentor2}
              setNomMentor3={setNomMentor3}
              setNomMentor4={setNomMentor4}
              setShow4thPresenter={setShow4thPresenter}
              setShow4thMentor={setShow4thMentor}
              spocPickerOpen={spocPickerOpen}
              setSpocPickerOpen={setSpocPickerOpen}
              schoolSpocs={(() => {
                return liveTeachers.filter(t => String(t.schoolId) === String(selectedCohort?.schoolId));
              })()}
              onAssignSpoc={handleAssignSpoc}
              onUnassignSpoc={handleUnassignSpoc}
              onUpdateLevelYear={handleUpdateCohortLevelYear}
              onStudentClick={handleOpenStudentDrawer}
            />
          )}

          {/* ========================================================= */}
          {/* SCREEN: SESSION PLANNER FORM */}
          {/* ========================================================= */}
          {sessionFormState && (
            <SessionForm
              sessionFormState={sessionFormState}
              sessionFormClashError={sessionFormClashError}
              sessionFormSubmitting={sessionFormSubmitting}
              cohorts={allCohortsForDisplay}
              allGroups={Object.values(schoolGroupsMap).flat()}
              sessions={sessions}
              auditors={activeAuditors}
              counselors={activeCounselors}
              resources={activeResources}
              resourceLoadState={resourceLoadState}
              onRetryResources={loadLiveResources}
              getAutoResources={getAutoResources}
              isTypeCompleted={isTypeCompleted}
              setSessionFormState={setSessionFormState}
              setSessionFormClashError={setSessionFormClashError}
              handleCancelSessionForm={handleCancelSessionForm}
              handleCreateSessionSubmit={handleCreateSessionSubmit}
              handleSessionToggleAuditor={handleSessionToggleAuditor}
              handleSessionToggleResource={handleSessionToggleResource}
              checkAuditorClash={checkAuditorClash}
              schools={liveSchools}
              lockContext={sessionFormState.lockContext || false}
              isEdit={!!sessionFormState.editingSessionId}
            />
          )}

          {/* ========================================================= */}
          {/* SCREEN: SESSION DETAIL VIEW */}
          {/* ========================================================= */}
          {selectedSession && !sessionFormState && (
            <SessionDetail
              session={selectedSession}
              currentUser={currentUser}
              auditors={activeAuditors}
              counselors={activeCounselors}
              teachers={liveTeachers}
              resources={activeResources}
              getAutoResources={getAutoResources}
              cohorts={allCohortsForDisplay}
              schools={liveSchools}
              onClose={handleCloseSessionDetail}
              onEdit={handleStartSessionEdit}
              onCancel={handleCancelSession}
              onStatusChange={handleSessionStatusChange}
              onManageAssignments={handleOpenManageAssignments}
              showManageAssignments={showManageAssignments}
              onManageAssignmentsClose={() => setShowManageAssignments(false)}
              onManageAssignmentsSaved={handleManageAssignmentsSaved}
              showToast={showToast}
            />
          )}
          {showManageAssignments && selectedSession && !sessionFormState && (
            <ManageAssignmentsModal
              session={selectedSession}
              counselors={activeCounselors}
              auditors={activeAuditors}
              teachers={liveTeachers}
              cohorts={allCohortsForDisplay}
              onClose={() => setShowManageAssignments(false)}
              onSaved={handleManageAssignmentsSaved}
              onRefreshAuditors={fetchLiveCounselorsAndAuditors}
              showToast={showToast}
            />
          )}


          {activeTab === 'api_activity_log' && currentUser?.role === 'program_administrator' && (
            <ApiActivityLog />
          )}

          {activeTab === 'reports_insights' && ['program_administrator', 'counselor', 'auditor'].includes(currentUser?.role) && (
            <ReportsInsights currentUser={currentUser} />
          )}

          {activeTab === 'resources' && (
            <ResourcesViewer resources={activeResources} />
          )}

          {activeTab === 'year2' && (
            (currentUser?.role === 'program_administrator' || currentUser?.role === 'spoc') ? (
              <Year2Portal
                user={currentUser}
                students={liveStudents}
                liveSchools={liveSchools}
                year2Discovery={year2Discovery}
                year2DiscoveryLoading={year2DiscoveryLoading || year2DiscoveryBootstrapPending}
                year2DiscoveryError={year2DiscoveryError}
                onRefreshYear2Discovery={loadYear2Discovery}
                onError={showToast}
                onSuccess={showToast}
              />
            ) : (
              <Year2DisabledMessage
                title="Year 2 Portal unavailable"
                onBack={() => {
                  setActiveTab('dashboard');
                  window.location.hash = '#/dashboard';
                }}
              />
            )
          )}

          {activeTab === 'esg' && (
            ESG_ALLOWED_SCREENS[currentUser?.role] ? (
              <>
                {esgScreen === 'squad-formation' && <EsgSquadFormation onError={showToast} onSuccess={showToast} />}
                {esgScreen === 'squads' && <EsgSquadsList onError={showToast} onSuccess={showToast} assignedOnly={currentUser?.role === 'auditor'} />}
                {esgScreen === 'ratings' && <EsgRatings onError={showToast} />}
                {esgScreen === 'support' && <EsgSupportInbox onError={showToast} onSuccess={showToast} />}
                {esgScreen === 'certificates' && <EsgCertificates onError={showToast} onSuccess={showToast} />}
                {esgScreen === 'evaluation' && <EsgEvaluation onError={showToast} onSuccess={showToast} />}
                {esgScreen === 'tags' && <EsgTags onError={showToast} onSuccess={showToast} />}
                {esgScreen === 'squad' && esgSquadId && (
                  <EsgSquadDetail
                    key={esgSquadId}
                    squadId={esgSquadId}
                    backLabel={currentUser?.role === 'auditor' ? 'Back to Evaluation' : 'Back to ESG Squads'}
                    onBack={() => { window.location.hash = currentUser?.role === 'auditor' ? '#/esg/evaluation' : '#/esg/squads'; }}
                    onError={showToast}
                    onSuccess={showToast}
                  />
                )}
              </>
            ) : (
              <Year2DisabledMessage
                title="ESG Specialist unavailable"
                message="ESG Specialist is only available to Program Administrators, SPOCs and auditors."
                onBack={() => {
                  setActiveTab('dashboard');
                  window.location.hash = '#/dashboard';
                }}
              />
            )
          )}

          {/* ========================================================= */}
          {/* SCHOOLS MODULE */}
          {/* ========================================================= */}
          {/* SCHOOLS MODULE */}
          {/* ========================================================= */}
          {activeTab === 'schools' && !selectedSchool && currentUser?.role === 'program_administrator' && (
            <SchoolsList
              schools={liveSchools}
              students={liveStudents}
              schoolCohorts={Object.values(schoolCohortsMap).flat()}
              onSelectSchool={handleSelectSchoolAndLoadStudents}
              branches={liveBranches}
              selectedBranchId={selectedBranchId}
              onSelectBranch={handleSelectBranch}
            />
          )}

          {activeTab === 'schools' && selectedSchool && currentUser?.role === 'program_administrator' && (
            <SchoolView
              school={selectedSchool}
              allStudents={liveStudents}
              schoolCohorts={schoolCohortsMap[String(selectedSchool.id)] || []}
              schoolGroups={schoolGroupsMap[String(selectedSchool.id)] || []}
              sessions={sessions}
              allCohorts={allCohortsForDisplay}
              auditors={liveAuditors}
              onBack={() => {
                setSelectedSchool(null);
                window.location.hash = '#/schools';
              }}
              onCohortView={cohort => {
                setSelectedCohort(cohort);
                setActiveTab('cohorts');
                window.location.hash = `#/cohorts/${cohort.id}`;
              }}
              onCohortSave={(cohortData) => {
                syncWorkspaceCohort(workspaceDispatch, cohortData);
              }}
              onCohortsReplace={handleSchoolCohortsReplace}
              onGroupSave={handleSchoolGroupSave}
              onPlanSession={(schoolId) => {
                handleStartSessionForm(null, schoolId);
              }}
              onSessionClick={(session) => handleOpenSessionDetail(session)}
              syncStatus={schoolSyncInfo[String(selectedSchool?.id)] || null}
              onSyncStudents={async () => {
                const school = selectedSchool;
                const branchId = school?.branch_id;
                const result = await mentorApi.syncStudents(school.id, branchId);
                setSchoolSyncInfo(prev => ({ ...prev, [String(school.id)]: { ...result, stale: false } }));
                // Re-fetch students so the list reflects the post-sync roster
                const students = await mentorApi.getStudents(school.id, branchId);
                const mapped = students.map(s => mapStudentFromApi(s, school.id, branchId));
                syncWorkspaceRoster(workspaceDispatch, school.id, branchId, mapped);
                return result;
              }}
              onStudentClick={handleOpenStudentDrawer}
            />
          )}

          {/* ========================================================= */}
          {/* SCREEN 1: COHORTS LIST VIEW */}
          {/* ========================================================= */}
          {activeTab === 'cohorts' && !selectedCohort && !wizardState && !sessionFormState && (
            <div className="animate-fade-in">
              <div className="dashboard-title-row">
                <div>
                  <h1 className="dashboard-title">Operational Cohorts</h1>
                  <p className="dashboard-subtitle">Cohorts are built in Schools → Cohort Builder and appear here once saved.</p>
                </div>
                <button className="btn-create-cohort" onClick={() => {
                  setActiveTab('schools');
                  window.location.hash = '#/schools';
                }}>
                  + Go to Schools
                </button>
              </div>

              <div className="students-table-card">
                <table className="students-table">
                  <thead>
                    <tr>
                      <th>Cohort Name</th>
                      <th>Year</th>
                      <th>Partner School</th>
                      <th>SPOC (Teacher)</th>
                      <th>Students Count</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCohortsForDisplay.length === 0 && (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        No cohorts yet. Go to Schools → select a school → open Cohort Builder to create cohorts.
                      </td></tr>
                    )}
                    {allCohortsForDisplay.map(c => (
                      <tr
                        key={c.id}
                        onClick={() => {
                          setSelectedCohort(c);
                          setCohortDetailTab('students');
                          window.location.hash = `#/cohorts/${c.id}/students`;
                        }}
                        style={{ cursor: 'pointer' }}
                        className="cohort-table-row"
                      >
                        <td><strong style={{ color: 'var(--primary-navy)' }}>{c.name}</strong></td>
                        <td><YearLevelBadge levelYear={c.levelYear} /></td>
                        <td>{c.schoolName}</td>
                        <td>
                          {(() => {
                            if (!c.spocId) return '—';
                            const spocTeacher = liveTeachers.find(t => String(t.id) === String(c.spocId));
                            return c.spocName || spocTeacher?.name || `SPOC #${c.spocId}`;
                          })()}
                        </td>
                        <td>
                          <span className="checklist-row-badge">{(c.studentIds || []).length} Students</span>
                        </td>
                        <td>
                          <span className={`status-pill ${(c.status || 'active').toLowerCase()}`}>{c.status || 'Active'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCREEN: ATTENDANCE FILTER LIST (CR-mentor-attendance-01) */}
          {/* ========================================================= */}
          {activeTab === 'attendance' && !selectedCohort && !wizardState && !sessionFormState && !selectedSession && (
            <AttendanceOverview
              sessions={sessions}
              cohorts={allCohortsForDisplay}
              onSessionClick={handleOpenSessionDetail}
            />
          )}

          {/* ========================================================= */}
          {/* SCREEN 2: SESSIONS LIST VIEW */}
          {/* ========================================================= */}
          {activeTab === 'sessions' && !selectedCohort && !wizardState && !sessionFormState && !selectedSession && (
            <div className="animate-fade-in">
              <div className="dashboard-title-row">
                <div>
                  <h1 className="dashboard-title">Fellowship Sessions</h1>
                  <p className="dashboard-subtitle">Timeline of scheduled, live, and completed Glosco audit sessions.</p>
                </div>
                <button className="btn-create-cohort" onClick={() => handleStartSessionForm('')}>
                  Plan New Session
                </button>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                {[
                  { key: 'all', label: 'All' },
                  { key: 'planned', label: 'Planned' },
                  { key: 'live', label: 'Live' },
                  { key: 'completed', label: 'Completed' },
                  { key: 'cancelled', label: 'Cancelled' },
                ].map(({ key, label }) => {
                  const active = sessionListStatusFilter === key;
                  const count = key === 'all'
                    ? sessions.length
                    : sessions.filter(s => String(s.status || '').toLowerCase() === key).length;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSessionListStatusFilter(key)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '999px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: active ? 'var(--primary-navy)' : 'var(--border-color)',
                        background: active ? 'var(--primary-navy)' : '#fff',
                        color: active ? '#fff' : 'var(--neutral-700)',
                      }}
                    >
                      {label}
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '11px',
                        fontWeight: 800,
                        opacity: active ? 0.9 : 0.65,
                      }}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {Object.keys(groupedSessionsAll).length > 0 ? (
                Object.keys(groupedSessionsAll).map(weekLabel => (
                  <div key={weekLabel} className="timeline-week-group">
                    <div className="timeline-week-header">
                      <span>{weekLabel}</span>
                    </div>
                    
                    <div className="timeline-grid">
                      {groupedSessionsAll[weekLabel].map(session => {
                        const cohort = allCohortsForDisplay.find(c => c.id === session.cohortId);
                        
                        return (
                          <div
                            key={session.id}
                            className="timeline-session-card"
                            onClick={() => handleOpenSessionDetail(session)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="session-card-header">
                              <div>
                                <span className="session-card-type">{session.sessionType}</span>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                  {(session.scheduledAt || '').split(/[T ]/)[0]}
                                </div>
                              </div>
                              <span className={`status-pill ${session.status.toLowerCase()}`}>{session.status}</span>
                            </div>

                            <div className="session-card-meta">
                              <div>🏫 School: <strong>{session.schoolName}</strong></div>
                              <div style={{ marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span>👥 Cohort: <strong>{session.cohortName}</strong></span>
                                <YearLevelBadge levelYear={cohort?.levelYear} />
                              </div>
                              <div style={{ marginTop: '2px' }}>🕒 Slot: <strong>{session.timeSlot}</strong></div>
                              <div style={{ marginTop: '4px' }}>🔗 Zoom: <a href={session.meetingLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }} onClick={(e) => e.stopPropagation()}>Join Room</a></div>
                            </div>

                            <div className="session-card-footer">
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Auditors:</span>
                              <div className="session-card-auditors">
                                {session.auditors.map(audId => {
                                  const aud = activeAuditors.find(a => a.id === audId);
                                  const assignmentObj = cohort?.auditorAssignments?.find(a => a.auditorId === audId);
                                  const isStandby = assignmentObj ? assignmentObj.isStandby : false;
                                  
                                  return (
                                    <span key={audId} className={`auditor-avatar-pill ${isStandby ? 'standby' : ''}`}>
                                      {aud ? aud.name : 'Auditor'} {isStandby ? '(S)' : ''}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                            {((session.auditors || []).length === 0 || !session.counselorId) && (
                              <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {(session.auditors || []).length === 0 && (
                                  <span style={{ fontSize: '11px', background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '999px', fontWeight: 600 }}>Needs Auditor</span>
                                )}
                                {!session.counselorId && (
                                  <span style={{ fontSize: '11px', background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: '999px', fontWeight: 600 }}>Needs Counselor</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state-card">
                  <div className="empty-state-icon-wrapper">
                    <span>📅</span>
                  </div>
                  <h2 className="empty-state-heading">
                    {sessionListStatusFilter === 'all' ? 'No sessions scheduled' : `No ${sessionListStatusFilter} sessions`}
                  </h2>
                  <p className="empty-state-description">
                    {sessionListStatusFilter === 'all'
                      ? 'Plan fellowship sessions by setting up a cohort and scheduling orientation or council audits.'
                      : 'Try another status filter (All / Planned / Live / Completed / Cancelled), or plan a new session.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* SCREEN 3: AUDITORS ROSTER */}
          {/* ========================================================= */}
          {activeTab === 'auditors' && !selectedCohort && !wizardState && !sessionFormState && (
            <div className="animate-fade-in">
              <div className="dashboard-title-row">
                <div>
                  <h1 className="dashboard-title">Auditors</h1>
                  <p className="dashboard-subtitle">{activeAuditors.length} auditor{activeAuditors.length !== 1 ? 's' : ''} in the system</p>
                </div>
                <button
                  type="button"
                  className="btn-secondary-action"
                  style={{ flexShrink: 0, fontSize: '12.5px', padding: '6px 14px' }}
                  onClick={async () => {
                    try {
                      await ensurePeopleRosterFresh({ force: true });
                      showToast('Auditor availability refreshed.');
                    } catch (err) {
                      showToast(formatApiError(err, 'Refreshing auditors'));
                    }
                  }}
                >
                  Refresh
                </button>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                <input
                  type="search"
                  aria-label="Search auditors"
                  placeholder="Search auditors by name or email..."
                  value={auditorRosterSearch}
                  onChange={e => setAuditorRosterSearch(e.target.value)}
                  style={{ flex: '1 1 280px', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '7px', fontSize: '13px' }}
                />
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'available', label: 'Has slots' },
                    { key: 'unavailable', label: 'No slots' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setAuditorRosterFilter(key)}
                      style={{ padding: '7px 13px', borderRadius: '999px', border: '1px solid var(--border-color)', background: auditorRosterFilter === key ? 'var(--primary-navy)' : '#fff', color: auditorRosterFilter === key ? '#fff' : 'var(--neutral-600)', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {activeAuditors.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)', background: 'var(--neutral-50)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  No auditors found. Accounts are managed at admin.ifspd.eu/manage-auditors.
                </div>
              ) : (
                (() => {
                  const search = auditorRosterSearch.trim().toLowerCase();
                  const rosterRows = activeAuditors
                    .map(aud => ({
                      aud,
                      assignedCount: sessions.filter(s => [
                        ...(s.auditors || []),
                        ...(s.auditorAssignments || []).map(a => a.auditorId),
                      ].map(String).includes(String(aud.id))).length,
                    }))
                    .filter(({ aud }) => !search || aud.name.toLowerCase().includes(search) || aud.email.toLowerCase().includes(search))
                    .filter(({ aud }) => auditorRosterFilter === 'all'
                      || (auditorRosterFilter === 'available' && (aud.availabilitySlots || []).length > 0)
                      || (auditorRosterFilter === 'unavailable' && (aud.availabilitySlots || []).length === 0))
                    .sort((a, b) => b.assignedCount - a.assignedCount || a.aud.name.localeCompare(b.aud.name));
                  const selectedAuditor = activeAuditors.find(aud => String(aud.id) === String(selectedAuditorRosterId));
                  const selectedSessions = selectedAuditor
                    ? sessions.filter(s => [
                      ...(s.auditors || []),
                      ...(s.auditorAssignments || []).map(a => a.auditorId),
                    ].map(String).includes(String(selectedAuditor.id)))
                    : [];

                  return (
                    <>
                {rosterRows.length === 0 ? (
                  <div className="empty-state-card" style={{ marginBottom: '16px' }}>
                    <h2 className="empty-state-heading">No auditors match this filter</h2>
                    <p className="empty-state-description">Try a different name, email, or availability filter.</p>
                  </div>
                ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {rosterRows.map(({ aud, assignedCount }) => {
                    const slots = aud.availabilitySlots || [];
                    return (
                      <button key={aud.id} type="button" onClick={() => setSelectedAuditorRosterId(aud.id)} style={{
                        background: '#ffffff',
                        border: '1px solid var(--border-color)',
                        borderRadius: '10px',
                        padding: '18px 20px',
                        boxShadow: 'var(--shadow-sm)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--primary-navy)' }}>{aud.name}</div>
                            {aud.email && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{aud.email}</div>}
                          </div>
                          {assignedCount > 0 && (
                            <span style={{ fontSize: '11px', fontWeight: 700, background: 'rgba(99,102,241,0.1)', color: '#6366f1', padding: '3px 8px', borderRadius: '12px', whiteSpace: 'nowrap' }}>
                              {assignedCount} session{assignedCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {(aud.college || aud.subjectSpecialisation) && (
                          <div style={{ fontSize: '12px', color: 'var(--neutral-600)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {aud.college && <span>{aud.college}{aud.collegeYear ? ` · ${aud.collegeYear}` : ''}</span>}
                            {aud.subjectSpecialisation && <span style={{ color: 'var(--text-muted)' }}>{aud.subjectSpecialisation}</span>}
                          </div>
                        )}

                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
                            Available slots
                          </div>
                          {slots.length === 0 ? (
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No slots set</span>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {slots.map(s => (
                                <span key={s.id} style={{
                                  fontSize: '11px', fontWeight: 600,
                                  background: 'rgba(16,185,129,0.08)',
                                  color: '#059669',
                                  border: '1px solid rgba(16,185,129,0.2)',
                                  padding: '3px 8px',
                                  borderRadius: '4px',
                                }}>
                                  {s.slot}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {aud.internshipStartDate && (
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            Since {aud.internshipStartDate}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                )}
                {selectedAuditor && (
                  <div style={{ marginTop: '18px', padding: '16px', background: 'var(--neutral-50)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                      <div>
                        <strong>{selectedAuditor.name}</strong>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{selectedSessions.length} assigned session{selectedSessions.length === 1 ? '' : 's'}</div>
                      </div>
                      <button type="button" className="btn-secondary-action" onClick={() => setSelectedAuditorRosterId(null)}>Close</button>
                    </div>
                    {selectedSessions.length > 0 && (
                      <ul style={{ margin: '12px 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                        {selectedSessions.map(s => <li key={s.id}>{s.sessionType} · {s.cohortName || s.groupName || 'Session'} · {s.scheduledAt || 'No date'}</li>)}
                      </ul>
                    )}
                  </div>
                )}
                    </>
                  );
                })()
              )}
            </div>
          )}

          {/* ========================================================= */}
          {/* SCREEN: APPROVALS QUEUE (PA + Counselor) */}
          {/* ========================================================= */}
          {activeTab === 'approvals' && (currentUser?.role === 'program_administrator' || currentUser?.role === 'counselor') && !selectedCohort && !wizardState && !sessionFormState && (() => {
            const filteredReports = reports.filter(rep => {
              if (rep.status !== activeApprovalTab) return false;
              
              if (activeApprovalTab === 'pending') {
                if (selectedCohortFilter !== 'all') {
                  const session = sessions.find(s => String(s.id) === String(rep.sessionId));
                  if (!session || String(session.cohortId) !== String(selectedCohortFilter)) return false;
                }
                if (selectedSessionFilter !== 'all') {
                  if (String(rep.sessionId) !== String(selectedSessionFilter)) return false;
                }
                if (selectedAuditorFilter !== 'all') {
                  const aud = activeAuditors.find(a => a.id === selectedAuditorFilter);
                  if (!aud || rep.auditorName !== aud.name) return false;
                }
              }
              return true;
            });

            return (
              <div className="animate-fade-in">
                <div className="dashboard-title-row">
                  <div>
                    <h1 className="dashboard-title">Year 1 Evaluation Approvals Queue</h1>
                    <p className="dashboard-subtitle">Exception review — student ratings publish on auditor submit. Approve, reject, or reopen only when you need to override.</p>
                  </div>
                  {/* BUG-mentor-counselor-approval-stale-01: refresh button for post-boot eval updates */}
                  <button
                    type="button"
                    className="btn-secondary-action"
                    style={{ flexShrink: 0, fontSize: '12.5px', padding: '6px 14px' }}
                    onClick={async () => {
                      try {
                        const refreshed = await refreshEvaluationsReports();
                        if (refreshed) {
                          showToast(`Approvals refreshed — ${refreshed.filter(r => r.status === 'pending').length} pending`);
                        }
                      } catch (err) {
                        showToast(formatApiError(err, 'Refreshing approvals'));
                      }
                    }}
                    disabled={approvalsEvalQuery.isLoading}
                  >
                    {approvalsEvalQuery.isLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </div>

                {/* Tab Header Row */}
                <div className="approvals-tab-row">
                  <button
                    className={`approvals-tab-button pending-tab ${activeApprovalTab === 'pending' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveApprovalTab('pending');
                      setRejectionInputId(null);
                    }}
                  >
                    Pending
                    <span className="approvals-tab-badge">
                      {reports.filter(r => r.status === 'pending').length}
                    </span>
                  </button>
                  <button
                    className={`approvals-tab-button approved-tab ${activeApprovalTab === 'approved' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveApprovalTab('approved');
                      setRejectionInputId(null);
                    }}
                  >
                    Approved
                    <span className="approvals-tab-badge">
                      {reports.filter(r => r.status === 'approved').length}
                    </span>
                  </button>
                  <button
                    className={`approvals-tab-button rejected-tab ${activeApprovalTab === 'rejected' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveApprovalTab('rejected');
                      setRejectionInputId(null);
                    }}
                  >
                    Rejected
                    <span className="approvals-tab-badge">
                      {reports.filter(r => r.status === 'rejected').length}
                    </span>
                  </button>
                </div>

                {/* Filters Chips row (Pending tab only) */}
                {activeApprovalTab === 'pending' && (
                  <div className="approvals-filters-row" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                    {/* Cohort Filter */}
                    <div className="filter-chip-wrapper" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className={`filter-chip ${selectedCohortFilter !== 'all' ? 'active' : ''}`}
                        onClick={() => setOpenFilterDropdown(openFilterDropdown === 'cohort' ? null : 'cohort')}
                      >
                        Cohort: {selectedCohortFilter === 'all' ? 'All' : allCohortsForDisplay.find(c => c.id === selectedCohortFilter)?.name || 'Selected'}
                        <span style={{ marginLeft: '6px', fontSize: '10px' }}>▼</span>
                      </button>
                      {openFilterDropdown === 'cohort' && (
                        <div className="filter-dropdown-panel animate-fade-in">
                          <div
                            className={`filter-dropdown-item ${selectedCohortFilter === 'all' ? 'selected' : ''}`}
                            onClick={() => { setSelectedCohortFilter('all'); setOpenFilterDropdown(null); }}
                          >
                            All Cohorts
                          </div>
                          {allCohortsForDisplay.map(c => (
                            <div
                              key={c.id}
                              className={`filter-dropdown-item ${selectedCohortFilter === c.id ? 'selected' : ''}`}
                              onClick={() => { setSelectedCohortFilter(c.id); setOpenFilterDropdown(null); }}
                            >
                              {c.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Session Filter */}
                    <div className="filter-chip-wrapper" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className={`filter-chip ${selectedSessionFilter !== 'all' ? 'active' : ''}`}
                        onClick={() => setOpenFilterDropdown(openFilterDropdown === 'session' ? null : 'session')}
                      >
                        Session: {selectedSessionFilter === 'all' ? 'All' : sessions.find(s => s.id === selectedSessionFilter) ? `${sessions.find(s => s.id === selectedSessionFilter).cohortName} (S${sessions.find(s => s.id === selectedSessionFilter).sessionNumber})` : 'Selected'}
                        <span style={{ marginLeft: '6px', fontSize: '10px' }}>▼</span>
                      </button>
                      {openFilterDropdown === 'session' && (
                        <div className="filter-dropdown-panel animate-fade-in">
                          <div
                            className={`filter-dropdown-item ${selectedSessionFilter === 'all' ? 'selected' : ''}`}
                            onClick={() => { setSelectedSessionFilter('all'); setOpenFilterDropdown(null); }}
                          >
                            All Sessions
                          </div>
                          {sessions.map(s => (
                            <div
                              key={s.id}
                              className={`filter-dropdown-item ${selectedSessionFilter === s.id ? 'selected' : ''}`}
                              onClick={() => { setSelectedSessionFilter(s.id); setOpenFilterDropdown(null); }}
                            >
                              {s.cohortName} (S{s.sessionNumber} - {s.sessionType})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Auditor Filter */}
                    <div className="filter-chip-wrapper" style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className={`filter-chip ${selectedAuditorFilter !== 'all' ? 'active' : ''}`}
                        onClick={() => setOpenFilterDropdown(openFilterDropdown === 'auditor' ? null : 'auditor')}
                      >
                        Auditor: {selectedAuditorFilter === 'all' ? 'All' : activeAuditors.find(a => a.id === selectedAuditorFilter)?.name || 'Selected'}
                        <span style={{ marginLeft: '6px', fontSize: '10px' }}>▼</span>
                      </button>
                      {openFilterDropdown === 'auditor' && (
                        <div className="filter-dropdown-panel animate-fade-in">
                          <div
                            className={`filter-dropdown-item ${selectedAuditorFilter === 'all' ? 'selected' : ''}`}
                            onClick={() => { setSelectedAuditorFilter('all'); setOpenFilterDropdown(null); }}
                          >
                            All Auditors
                          </div>
                          {activeAuditors.map(a => (
                            <div
                              key={a.id}
                              className={`filter-dropdown-item ${selectedAuditorFilter === a.id ? 'selected' : ''}`}
                              onClick={() => { setSelectedAuditorFilter(a.id); setOpenFilterDropdown(null); }}
                            >
                              {a.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Evaluation List/Queue */}
                {filteredReports.length > 0 ? (
                  <div className="approvals-list-stack animate-fade-in">
                    {filteredReports.map(rep => {
                      const isExpanded = expandedEvaluations.includes(rep.id);
                      const showRating = rep.roleBadge && (rep.roleBadge.toLowerCase() === 'presenter' || rep.roleBadge.toLowerCase() === 'mentor');
                      
                      return (
                        <div
                          key={rep.id}
                          className={`approval-card-item clickable ${isExpanded ? 'active-row-highlight' : ''}`}
                        >
                          <div
                            className="approval-card-main-header"
                            onClick={() => toggleEvaluationExpanded(rep.id)}
                            style={{ padding: '16px', cursor: 'pointer' }}
                          >
                            <div className="approval-card-header-left">
                              <span className="approval-card-student-name" style={{ fontSize: '16px', fontWeight: 700, color: 'var(--primary-navy)' }}>
                                {rep.studentName}
                              </span>
                              <span className={`role-badge ${rep.roleBadge ? rep.roleBadge.toLowerCase() : 'delegate'}`}>
                                {rep.roleBadge || 'Delegate'}
                              </span>
                              {rep.isRevision && (
                                <span className="role-badge revised">
                                  Revised (v{rep.revisionNumber})
                                </span>
                              )}
                              <div className="approval-card-meta-summary">
                                <span className="approval-card-meta-session">{rep.sessionName}</span>
                                <span className="approval-card-meta-auditor">
                                  Audited by {rep.auditorName} • Submitted {rep.submittedAt}
                                </span>
                              </div>
                            </div>
                            <div className="approval-card-header-right">
                              {rep.status !== 'pending' && (
                                <span className={`approval-status-badge ${rep.status}`}>
                                  {rep.status}
                                </span>
                              )}
                              <span style={{
                                fontSize: '12px',
                                display: 'inline-block',
                                transition: 'transform 0.15s ease',
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: 'var(--text-muted)'
                              }}>
                                ▼
                              </span>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="approval-card-expanded-body" style={{ padding: '0 16px 20px 16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                              <div className="expanded-eval-grid" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                
                                {/* STRENGTHS DEMONSTRATED */}
                                <div>
                                  <span style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'var(--neutral-500)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
                                    STRENGTHS DEMONSTRATED
                                  </span>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {rep.keywords && rep.keywords.map(kw => (
                                      <span key={kw} className="keyword-tag-pill positive">
                                        {kw}
                                      </span>
                                    ))}
                                    {(!rep.keywords || rep.keywords.length === 0) && (
                                      <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                                        None recorded
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* COACHING REQUIRED */}
                                <div>
                                  <span style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'var(--neutral-500)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
                                    COACHING REQUIRED
                                  </span>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {rep.coachingKeywords && rep.coachingKeywords.map(kw => (
                                      <span key={kw} className="keyword-tag-pill warning">
                                        {kw}
                                      </span>
                                    ))}
                                    {(!rep.coachingKeywords || rep.coachingKeywords.length === 0) && (
                                      <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                                        None recorded
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* RATING */}
                                {showRating && rep.rating && (
                                  <div>
                                    <span style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'var(--neutral-500)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
                                      RATING
                                    </span>
                                    <div className="expanded-eval-rating-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <span className="expanded-eval-rating-value" style={{ fontSize: '14px', fontWeight: 700, color: 'var(--neutral-800)' }}>
                                        {rep.rating} / 10
                                      </span>
                                      <div className="expanded-eval-rating-bar" style={{ flex: 1, height: '6px', background: 'var(--neutral-200)', borderRadius: '999px', overflow: 'hidden', maxWidth: '200px' }}>
                                        <div
                                          className="expanded-eval-rating-fill"
                                          style={{ width: `${rep.rating * 10}%`, height: '100%', background: '#0147a2' }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* COMMENT */}
                                <div>
                                  <span style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'var(--neutral-500)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px' }}>
                                    COMMENT
                                  </span>
                                  {rep.comment ? (
                                    <div className="eval-quote-box">
                                      "{rep.comment}"
                                    </div>
                                  ) : (
                                    <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                      No comments recorded.
                                    </span>
                                  )}
                                </div>

                                {/* Rejection reason for rejected cards */}
                                {rep.status === 'rejected' && rep.rejectionReason && (
                                  <div className="rejection-reason-callout" style={{ marginTop: '16px', padding: '12px', background: 'rgba(239, 68, 68, 0.05)', borderLeft: '4px solid #ef4444', borderRadius: '4px' }}>
                                    <strong style={{ color: '#ef4444', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rejection Reason:</strong>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '13.5px', color: 'var(--neutral-700)' }}>
                                      {rep.rejectionReason}
                                    </p>
                                  </div>
                                )}

                                {/* Approved details for approved cards */}
                                {rep.status === 'approved' && (
                                  <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontWeight: 700, fontSize: '13px' }}>
                                    <span>✓</span> Approved by {rep.approvedBy || 'Counselor'} · {rep.approvedTime || rep.submittedAt}
                                  </div>
                                )}

                              </div>

                              {/* Action buttons for pending card */}
                              {rep.status === 'pending' && (
                                <div style={{ marginTop: '24px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
                                  {rejectionInputId !== rep.id ? (
                                    <div className="approval-card-actions-footer" style={{ background: 'transparent', padding: 0, borderTop: 'none', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                      <button
                                        type="button"
                                        className="btn-reject-action"
                                        onClick={() => handleStartRejectEvaluation(rep.id)}
                                      >
                                        Reject
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-approve-action"
                                        onClick={() => handleApproveEvaluation(rep.id)}
                                      >
                                        Approve
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="rejection-panel-inline" style={{ background: 'rgba(239, 68, 68, 0.02)', border: '1px solid rgba(239, 68, 68, 0.15)', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                      <div>
                                        <label
                                          style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: 'var(--neutral-700)', marginBottom: '6px' }}
                                          htmlFor={`rej-reason-input-${rep.id}`}
                                        >
                                          Reason for rejection (required)
                                        </label>
                                        <textarea
                                          id={`rej-reason-input-${rep.id}`}
                                          className="styled-textarea-input"
                                          style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none', fontSize: '13.5px', resize: 'vertical', boxSizing: 'border-box' }}
                                          rows="3"
                                          placeholder="Specify what needs correction or expansion..."
                                          value={rejectionReasonText}
                                          onChange={(e) => setRejectionReasonText(e.target.value)}
                                        />
                                        <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                          Provide clear, constructive feedback on what the auditor needs to correct.
                                        </span>
                                      </div>
                                      
                                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        <button
                                          type="button"
                                          className="btn-secondary-action"
                                          style={{ padding: '8px 16px', fontSize: '12.5px' }}
                                          onClick={handleCancelRejectEvaluation}
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          className="btn-submit-session"
                                          style={{ padding: '8px 16px', fontSize: '12.5px', background: '#ef4444', borderColor: '#ef4444', color: '#ffffff' }}
                                          onClick={() => handleConfirmRejectEvaluation(rep.id)}
                                        >
                                          Confirm Reject
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-state-card animate-fade-in">
                    <div className="empty-state-icon-wrapper" style={{
                      color: activeApprovalTab === 'pending' ? '#10b981' : 'var(--text-muted)',
                      background: activeApprovalTab === 'pending' ? 'rgba(16, 185, 129, 0.1)' : 'var(--neutral-100)'
                    }}>
                      <span>{activeApprovalTab === 'pending' ? '✓' : '📂'}</span>
                    </div>
                    <h2 className="empty-state-heading">
                      {activeApprovalTab === 'pending'
                        ? 'No evaluations pending approval'
                        : activeApprovalTab === 'approved'
                        ? 'No approved evaluations'
                        : 'No rejected evaluations'}
                    </h2>
                    <p className="empty-state-description">
                      {activeApprovalTab === 'pending'
                        ? 'All submitted auditor evaluations have been reviewed and processed.'
                        : activeApprovalTab === 'approved'
                        ? 'Evaluations approved will appear here.'
                        : 'Evaluations rejected with remarks will appear here.'}
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

          {activeTab === 'year2_approvals' && (currentUser?.role === 'program_administrator' || currentUser?.role === 'counselor') && !selectedCohort && !wizardState && !sessionFormState && (() => {
            const filteredItems = year2ApprovalItems.filter(item => item.status === activeApprovalTab);

            return (
              <div className="animate-fade-in">
                <div className="dashboard-title-row">
                  <div>
                    <h1 className="dashboard-title">Year 2 Evaluation Approvals Queue</h1>
                    <p className="dashboard-subtitle">Exception review — Year 2 student ratings publish on auditor submit. Override with reject or reopen when needed.</p>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary-action"
                    style={{ flexShrink: 0, fontSize: '12.5px', padding: '6px 14px' }}
                    onClick={() => year2ApprovalsQuery.refetch()}
                    disabled={year2ApprovalsQuery.isLoading}
                  >
                    {year2ApprovalsQuery.isLoading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>

                <div className="approvals-tab-row">
                  <button className={`approvals-tab-button pending-tab ${activeApprovalTab === 'pending' ? 'active' : ''}`} onClick={() => { setActiveApprovalTab('pending'); setRejectionInputId(null); }}>
                    Pending <span className="approvals-tab-badge">{year2ApprovalItems.filter(r => r.status === 'pending').length}</span>
                  </button>
                  <button className={`approvals-tab-button approved-tab ${activeApprovalTab === 'approved' ? 'active' : ''}`} onClick={() => { setActiveApprovalTab('approved'); setRejectionInputId(null); }}>
                    Approved <span className="approvals-tab-badge">{year2ApprovalItems.filter(r => r.status === 'approved').length}</span>
                  </button>
                  <button className={`approvals-tab-button rejected-tab ${activeApprovalTab === 'rejected' ? 'active' : ''}`} onClick={() => { setActiveApprovalTab('rejected'); setRejectionInputId(null); }}>
                    Rejected <span className="approvals-tab-badge">{year2ApprovalItems.filter(r => r.status === 'rejected').length}</span>
                  </button>
                </div>

                {filteredItems.length > 0 ? (
                  <div className="approvals-list-stack animate-fade-in">
                    {filteredItems.map(item => {
                      const isExpanded = expandedEvaluations.includes(item.uiId);
                      return (
                        <div key={item.uiId} className={`approval-card-item clickable ${isExpanded ? 'active-row-highlight' : ''}`}>
                          <div className="approval-card-main-header" onClick={() => toggleEvaluationExpanded(item.uiId)} style={{ padding: '16px', cursor: 'pointer' }}>
                            <div className="approval-card-header-left">
                              <span className="approval-card-student-name" style={{ fontSize: '16px', fontWeight: 700, color: 'var(--primary-navy)' }}>{item.title}</span>
                              <span className={`role-badge ${item.type === 'squad' ? 'delegate' : item.type}`}>{item.roleLabel}</span>
                              {item.is_revision && <span className="role-badge revised">Revised (v{item.revision_number || 1})</span>}
                              <div className="approval-card-meta-summary">
                                <span className="approval-card-meta-session">{item.sessionName || `Council ${item.council_number || '-'}`}</span>
                                <span className="approval-card-meta-auditor">{item.cohort_id ? `Cohort ${item.cohort_id}` : 'Year 2 cohort'}</span>
                              </div>
                            </div>
                            <div className="approval-card-header-right">
                              {item.status !== 'pending' && <span className={`approval-status-badge ${item.status}`}>{item.status}</span>}
                              <span style={{ fontSize: '12px', display: 'inline-block', transition: 'transform 0.15s ease', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', color: 'var(--text-muted)' }}>▼</span>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="approval-card-expanded-body" style={{ padding: '16px', borderTop: '1px solid var(--border-color)' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', fontSize: '13px', color: 'var(--neutral-700)' }}>
                                <div><strong>Type:</strong> {item.roleLabel}</div>
                                <div><strong>Squad:</strong> {item.squad_name || item.squad_id || '—'}</div>
                                <div><strong>Student:</strong> {item.student_name || item.student_id || '—'}</div>
                                <div><strong>Council:</strong> {item.council_number || '—'}</div>
                              </div>

                              {item.rejection_reason && (
                                <div className="rejection-reason-callout" style={{ marginTop: '16px', padding: '12px', background: 'rgba(239, 68, 68, 0.05)', borderLeft: '4px solid #ef4444', borderRadius: '4px' }}>
                                  <strong style={{ color: '#ef4444', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rejection Reason:</strong>
                                  <p style={{ margin: '4px 0 0 0', fontSize: '13.5px', color: 'var(--neutral-700)' }}>{item.rejection_reason}</p>
                                </div>
                              )}

                              {item.status === 'pending' && (
                                <div style={{ marginTop: '24px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
                                  {rejectionInputId !== item.uiId ? (
                                    <div className="approval-card-actions-footer" style={{ background: 'transparent', padding: 0, borderTop: 'none', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                      <button type="button" className="btn-reject-action" onClick={() => handleStartRejectYear2Evaluation(item)}>Reject</button>
                                      <button type="button" className="btn-approve-action" onClick={() => handleApproveYear2Evaluation(item)}>Approve</button>
                                    </div>
                                  ) : (
                                    <div className="rejection-panel-inline" style={{ background: 'rgba(239, 68, 68, 0.02)', border: '1px solid rgba(239, 68, 68, 0.15)', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                      <div>
                                        <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: 'var(--neutral-700)', marginBottom: '6px' }} htmlFor={`year2-rej-${item.uiId}`}>Reason for rejection (required)</label>
                                        <textarea
                                          id={`year2-rej-${item.uiId}`}
                                          className="styled-textarea-input"
                                          style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none', fontSize: '13.5px', resize: 'vertical', boxSizing: 'border-box' }}
                                          rows="3"
                                          placeholder="Specify what needs correction or expansion..."
                                          value={rejectionReasonText}
                                          onChange={(e) => setRejectionReasonText(e.target.value)}
                                        />
                                      </div>
                                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        <button type="button" className="btn-secondary-action" style={{ padding: '8px 16px', fontSize: '12.5px' }} onClick={handleCancelRejectEvaluation}>Cancel</button>
                                        <button type="button" className="btn-submit-session" style={{ padding: '8px 16px', fontSize: '12.5px', background: '#ef4444', borderColor: '#ef4444', color: '#ffffff' }} onClick={() => handleConfirmRejectYear2Evaluation(item)}>Confirm Reject</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-state-card animate-fade-in">
                    <div className="empty-state-icon-wrapper" style={{ color: activeApprovalTab === 'pending' ? '#10b981' : 'var(--text-muted)', background: activeApprovalTab === 'pending' ? 'rgba(16, 185, 129, 0.1)' : 'var(--neutral-100)' }}>
                      <span>{activeApprovalTab === 'pending' ? '✓' : '📂'}</span>
                    </div>
                    <h2 className="empty-state-heading">
                      {activeApprovalTab === 'pending' ? 'No Year 2 evaluations pending approval' : activeApprovalTab === 'approved' ? 'No approved Year 2 evaluations' : 'No rejected Year 2 evaluations'}
                    </h2>
                    <p className="empty-state-description">Year 2 assessment records will appear here after auditors submit them.</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ========================================================= */}
          {/* SCREEN: GENERAL DASHBOARD (PA VIEW) */}
          {/* ========================================================= */}
          {activeTab === 'dashboard' && currentUser?.role === 'program_administrator' && !selectedCohort && !wizardState && !sessionFormState && (
            <DashboardPA
              dashboardStats={dashboardStats}
              cohorts={allCohortsForDisplay}
              sessions={sessions}
              reports={reports}
              auditors={activeAuditors}
              groupedSessionsActive={groupedSessionsActive}
              handleStartCohortWizard={handleStartCohortWizard}
              handleStartSessionForm={handleStartSessionForm}
              handleStartSessionEdit={handleStartSessionEdit}
              handleNavigate={handleNavigate}
              setActiveTab={setActiveTab}
              setSelectedCohort={setSelectedCohort}
              setCohortDetailTab={setCohortDetailTab}
            />
          )}

          {/* ========================================================= */}
          {/* SCREEN 3: POST-SESSION OUTCOME FORM */}
          {/* ========================================================= */}
          {outcomeFormSessionId && (
            <div className="animate-fade-in">
              <div className="dashboard-title-row">
                <div>
                  <h1 className="dashboard-title">Log Session Outcomes</h1>
                  <p className="dashboard-subtitle">
                    Log challengers, awards, and chair score for: <strong>{(() => {
                      const sess = sessions.find(s => String(s.id) === String(outcomeFormSessionId));
                      if (!sess) return '';
                      const cohortLabel = sess.groupId
                        ? (allCohortsForDisplay.find(c => String(c.id) === String(outcomeFormCohortId))?.name || 'Cohort')
                        : (sess.cohortName || '');
                      return `${sess.sessionType}${cohortLabel ? ` — ${cohortLabel}` : ''}`;
                    })()}</strong>
                  </p>
                  <p className="dashboard-subtitle" style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-muted)' }}>
                    Challengers are usually logged after the session ends — log when you are ready. The PA manages session scheduling; you decide when to record outcomes.
                  </p>
                </div>
                <button className="btn-secondary-action" onClick={handleCancelOutcomeForm}>Cancel</button>
              </div>

              <form onSubmit={handleSubmitOutcomes} className="session-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="session-form-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
                  
                  {/* Section: Challengers */}
                  <div className="form-section-container">
                    <h3 className="form-section-title">Challengers</h3>
                    <hr className="form-section-divider" />
                    
                    {/* Challenger 1 */}
                    <div className="form-field-group">
                      <label htmlFor="challenger1">Challenger 1 *</label>
                      <select
                        id="challenger1"
                        className="form-control-select"
                        value={outcomeChallenger1}
                        onChange={(e) => setOutcomeChallenger1(e.target.value)}
                        required
                      >
                        <option value="">-- Select Student --</option>
                        {getChallengerCandidates(outcomeFormSessionId)
                          .filter(stud => stud.id !== outcomeChallenger2 && stud.id !== outcomeChallenger3 && stud.id !== outcomeChallenger4)
                          .map(stud => (
                            <option key={stud.id} value={stud.id}>{stud.name}</option>
                          ))}
                      </select>
                      <span className="form-helper-text">Pick any cohort student. Challengers are usually audience members who spoke during the session.</span>
                    </div>

                    {/* Challenger 2 */}
                    <div className="form-field-group">
                      <label htmlFor="challenger2">Challenger 2 *</label>
                      <select
                        id="challenger2"
                        className="form-control-select"
                        value={outcomeChallenger2}
                        onChange={(e) => setOutcomeChallenger2(e.target.value)}
                        required
                      >
                        <option value="">-- Select Student --</option>
                        {getChallengerCandidates(outcomeFormSessionId)
                          .filter(stud => stud.id !== outcomeChallenger1 && stud.id !== outcomeChallenger3 && stud.id !== outcomeChallenger4)
                          .map(stud => (
                            <option key={stud.id} value={stud.id}>{stud.name}</option>
                          ))}
                      </select>
                    </div>

                    {/* Challenger 3 */}
                    <div className="form-field-group">
                      <label htmlFor="challenger3">Challenger 3 *</label>
                      <select
                        id="challenger3"
                        className="form-control-select"
                        value={outcomeChallenger3}
                        onChange={(e) => setOutcomeChallenger3(e.target.value)}
                        required
                      >
                        <option value="">-- Select Student --</option>
                        {getChallengerCandidates(outcomeFormSessionId)
                          .filter(stud => stud.id !== outcomeChallenger1 && stud.id !== outcomeChallenger2 && stud.id !== outcomeChallenger4)
                          .map(stud => (
                            <option key={stud.id} value={stud.id}>{stud.name}</option>
                          ))}
                      </select>
                    </div>

                    {/* Outlined Toggle Button for 4th Challenger */}
                    {!show4thChallenger && (
                      <button
                        type="button"
                        className="btn-form-toggle-outlined"
                        onClick={() => setShow4thChallenger(true)}
                      >
                        + Add 4th Challenger (Optional)
                      </button>
                    )}

                    {show4thChallenger && (
                      <div className="form-field-group animate-fade-in">
                        <label htmlFor="challenger4">Challenger 4 (Optional)</label>
                        <select
                          id="challenger4"
                          className="form-control-select"
                          value={outcomeChallenger4}
                          onChange={(e) => setOutcomeChallenger4(e.target.value)}
                          required={show4thChallenger}
                        >
                          <option value="">-- Select Student --</option>
                          {getChallengerCandidates(outcomeFormSessionId)
                            .filter(stud => stud.id !== outcomeChallenger1 && stud.id !== outcomeChallenger2 && stud.id !== outcomeChallenger3)
                            .map(stud => (
                              <option key={stud.id} value={stud.id}>{stud.name}</option>
                            ))}
                        </select>
                        <button
                          type="button"
                          className="btn-form-toggle-outlined remove-btn"
                          onClick={() => {
                            setShow4thChallenger(false);
                            setOutcomeChallenger4('');
                          }}
                        >
                          Remove 4th Challenger
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Section: Awards */}
                  <div className="form-section-container" style={{ marginTop: '24px' }}>
                    <h3 className="form-section-title">Awards</h3>
                    <hr className="form-section-divider" />
                    
                    {/* Best Presenter Award */}
                    <div className="form-field-group">
                      <label htmlFor="bestPresenter">Best Presenter Award *</label>
                      <select
                        id="bestPresenter"
                        className="form-control-select"
                        value={outcomeBestPresenter}
                        onChange={(e) => setOutcomeBestPresenter(e.target.value)}
                        required
                      >
                        <option value="">-- Select Best Presenter --</option>
                        {getPresenterCandidates(outcomeFormSessionId).map(stud => (
                          <option key={stud.id} value={stud.id}>{stud.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Best Mentor Award */}
                    <div className="form-field-group">
                      <label htmlFor="bestMentor">Best Mentor Award *</label>
                      <select
                        id="bestMentor"
                        className="form-control-select"
                        value={outcomeBestMentor}
                        onChange={(e) => setOutcomeBestMentor(e.target.value)}
                        required
                      >
                        <option value="">-- Select Best Mentor --</option>
                        {getMentorCandidates(outcomeFormSessionId).map(stud => (
                          <option key={stud.id} value={stud.id}>{stud.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Best Challenger Award */}
                    <div className="form-field-group">
                      <label htmlFor="bestChallenger">Best Challenger Award *</label>
                      <select
                        id="bestChallenger"
                        className="form-control-select"
                        value={outcomeBestChallenger}
                        onChange={(e) => setOutcomeBestChallenger(e.target.value)}
                        required
                      >
                        <option value="">-- Select Best Challenger --</option>
                        {getSelectedChallengers().map(stud => (
                          <option key={stud.id} value={stud.id}>{stud.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Section: Chair Score */}
                  <div className="form-section-container" style={{ marginTop: '24px', marginBottom: '32px' }}>
                    <h3 className="form-section-title">Chair Score</h3>
                    <hr className="form-section-divider" />

                    <div className="form-field-group">
                      <label htmlFor="chairScore">Chair Score (1.0 – 9.9) *</label>
                      <input
                        type="number"
                        id="chairScore"
                        className="form-control-input"
                        step="0.1"
                        min="1.0"
                        max="9.9"
                        placeholder="e.g. 7.5"
                        value={outcomeChairScore}
                        onChange={(e) => setOutcomeChairScore(e.target.value)}
                        required
                        style={{ fontSize: '18px', fontWeight: 'bold' }}
                      />
                      <span className="form-helper-text">Numeric score between 1.0 and 9.9.</span>
                    </div>
                  </div>

                  <button type="submit" className="btn-submit-outcomes">
                    Submit Outcomes
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ========================================================= */}
          {/* SCREEN 1: SPOC DASHBOARD */}
          {/* ========================================================= */}
          {activeTab === 'dashboard' && currentUser?.role === 'spoc' && !selectedCohort && !outcomeFormSessionId && (
            <DashboardSPOC
              cohorts={allCohortsForDisplay} sessions={sessions} spocTasks={spocTasks} currentSpocId={currentSpocId}
              handleOpenOutcomeForm={handleOpenOutcomeForm}
              setNominationsCohortId={setNominationsCohortId} setNomChairId={setNomChairId}
              setNomPresenter1={setNomPresenter1} setNomPresenter2={setNomPresenter2} setNomPresenter3={setNomPresenter3} setNomPresenter4={setNomPresenter4}
              setNomMentor1={setNomMentor1} setNomMentor2={setNomMentor2} setNomMentor3={setNomMentor3} setNomMentor4={setNomMentor4}
              setShow4thPresenter={setShow4thPresenter} setShow4thMentor={setShow4thMentor}
              setYear2NominationsCohortId={setYear2NominationsCohortId}
              setActiveTab={setActiveTab} setSelectedCohort={setSelectedCohort} setCohortDetailTab={setCohortDetailTab}
              onSessionClick={handleOpenSessionDetail}
            />
          )}

          {/* ========================================================= */}
          {/* SCREEN 2: MY COHORTS LIST VIEW */}
          {/* ========================================================= */}
          {activeTab === 'my_cohorts' && currentUser?.role === 'spoc' && !selectedCohort && !outcomeFormSessionId && (
            <MyCohortsSPOC
              cohorts={allCohortsForDisplay} sessions={sessions} currentSpocId={currentSpocId}
              setSelectedCohort={setSelectedCohort} setCohortDetailTab={setCohortDetailTab}
            />
          )}

          {/* ========================================================= */}
          {/* SCREEN 4: NEXT-SESSION NOMINATION FORM */}
          {/* ========================================================= */}
          {activeTab === 'nominations' && (currentUser?.role === 'spoc' || currentUser?.role === 'auditor') && !selectedCohort && !outcomeFormSessionId && (
            <NominationsSPOC
              cohorts={allCohortsForDisplay} sessions={sessions} allStudents={liveStudents} sessionRoles={sessionRoles} currentSpocId={currentSpocId}
              nominationsCohortId={nominationsCohortId} nomChairId={nomChairId}
              nomPresenter1={nomPresenter1} nomPresenter2={nomPresenter2} nomPresenter3={nomPresenter3} nomPresenter4={nomPresenter4} show4thPresenter={show4thPresenter}
              nomMentor1={nomMentor1} nomMentor2={nomMentor2} nomMentor3={nomMentor3} nomMentor4={nomMentor4} show4thMentor={show4thMentor}
              setNominationsCohortId={setNominationsCohortId} setNomChairId={setNomChairId}
              setNomPresenter1={setNomPresenter1} setNomPresenter2={setNomPresenter2} setNomPresenter3={setNomPresenter3} setNomPresenter4={setNomPresenter4} setShow4thPresenter={setShow4thPresenter}
              setNomMentor1={setNomMentor1} setNomMentor2={setNomMentor2} setNomMentor3={setNomMentor3} setNomMentor4={setNomMentor4} setShow4thMentor={setShow4thMentor}
              handleSubmitNominations={handleSubmitNominations}
              handleOpenOutcomeForm={handleOpenOutcomeForm}
            />
          )}

          {activeTab === 'year2_nominations' && (currentUser?.role === 'spoc' || currentUser?.role === 'auditor') && !selectedCohort && !outcomeFormSessionId && (
            <Year2NominationsSPOC
              sessions={sessions}
              preloadedCohorts={year2Discovery ? year2DiscoveryCohorts : null}
              bootstrapLoading={year2DiscoveryLoading || year2DiscoveryBootstrapPending}
              bootstrapError={year2DiscoveryError}
              onBootstrapRetry={loadYear2Discovery}
              nominationsCohortId={year2NominationsCohortId}
              setNominationsCohortId={setYear2NominationsCohortId}
              setActiveTab={setActiveTab}
              onError={(msg) => showToast(msg)}
              onSuccess={(msg) => showToast(msg)}
              onTasksRefresh={handleYear2NomTasksRefresh}
            />
          )}

          {/* ========================================================= */}
          {/* AUDITOR WORKSPACE VIEWS */}
          {/* ========================================================= */}

          {/* SCREEN 4: SESSION DETAIL + EVALUATION FORM */}
          {evaluatingSessionId !== null && currentUser?.role === 'auditor' && (() => {
            const session = sessions.find(s => s.id === evaluatingSessionId);
            if (!session) return null;

            const evalCohortForYear = allCohortsForDisplay.find(
              c => String(c.id) === String(evaluatingCohortId || session.cohortId)
            );
            const resolvedLevelYear = session.levelYear ?? evalCohortForYear?.levelYear ?? null;
            if (resolvedLevelYear === 'Year 2' && isCouncilSession(session)) {
              return (
                <Year2SessionView
                  session={session}
                  user={currentUser}
                  evaluatingCohortId={evaluatingCohortId}
                  onCohortChange={setEvaluatingCohortId}
                  liveStudents={liveStudents}
                  onBack={() => { setEvaluatingSessionId(null); window.location.hash = '#/my_sessions_y2'; }}
                  onError={showToast}
                  onSuccess={showToast}
                />
              );
            }
            if (resolvedLevelYear === 'Year 2' && isOrientationSession(session)) {
              return (
                <Year2DisabledMessage
                  title="Council evaluation not available for Orientation"
                  message="Year 2 squad and chair assessments open on Council sessions (Council 1–5) after SPOC chair nominations are complete."
                  onBack={() => { setEvaluatingSessionId(null); window.location.hash = '#/my_sessions_y2'; }}
                />
              );
            }

            const evalDraft = sessionEvaluations[evaluatingSessionId] || initEvaluation(evaluatingSessionId, evaluatingCohortId);
            const roles = getSessionRolesFromMap(sessionRoles, session.id, evaluatingCohortId);
            const { nomination: evalNomination, outcome: evalOutcome } = resolveNominationOutcomeForCohort(
              session,
              evaluatingCohortId
            );
            // resolveStudent: always returns { id, name } — real name from liveStudents
            // when loaded, "Student #N" fallback otherwise. String() coercion is consistent
            // across chair, presenters, mentors, and challengers.
            const nominationNames = evalNomination || evalOutcome
              ? { ...(evalNomination || {}), ...(evalOutcome || {}) }
              : null;
            const chairStudent = roles.chairId
              ? resolveStudent(roles.chairId, liveStudents, nominationNames)
              : null;
            const presenterIds = roles.presenterIds || [];
            const mentorIds = roles.mentorIds || [];
            const presenters = resolveStudents(presenterIds, liveStudents, nominationNames);
            const mentors = resolveStudents(mentorIds, liveStudents, nominationNames);

            // Per-cohort student pool from API — group sessions must not use union roster for challengers.
            const perCohortStudentIds = evaluatingCohortId
              ? (session.cohortStudentIdsByCohort?.[evaluatingCohortId] || [])
              : [];
            const sessionCohortStudentIds = perCohortStudentIds.length > 0
              ? perCohortStudentIds
              : (session.cohortStudentIds || []);
            const cohort = allCohortsForDisplay.find(
              c => String(c.id) === String(evaluatingCohortId || session.cohortId)
            );
            const cohortStudentIds = sessionCohortStudentIds.length > 0
              ? sessionCohortStudentIds
              : (cohort ? cohort.studentIds : []);
            // Name resolution uses school roster; challenger pool stays cohort-scoped via cohortStudentIds only.
            const schoolBranchRoster = getStudentsForSchoolBranch(
              workspaceState,
              session.schoolId,
              session.branchId
            );
            const rosterPool = schoolBranchRoster.length > 0 ? schoolBranchRoster : liveStudents;
            const cohortStudents = resolveStudents(cohortStudentIds, rosterPool, nominationNames);
            const nominatedIds = [
              roles.chairId,
              ...(roles.presenterIds || []),
              ...(roles.mentorIds || [])
            ].filter(Boolean).map(String);
            const audienceMembers = cohortStudents.filter(s => !nominatedIds.includes(String(s.id)));

            // Challenger Live Deduplication list of selected IDs
            const selectedChallengerIds = evalDraft.challengers.map(c => c.studentId).filter(Boolean);

            // Handle toggling strength/coaching chips
            const handleToggleKeyword = (studentId, type, word, isChallenger = false, challengerIdx = null) => {
              if (evalDraft.status === 'submitted') return;
              if (isChallenger) {
                updateEvaluationDraft(current => {
                  const challengers = [...current.challengers];
                  const chal = { ...challengers[challengerIdx] };
                  if (type === 'strengths') {
                    chal.positiveKeywords = chal.positiveKeywords.includes(word)
                      ? chal.positiveKeywords.filter(w => w !== word)
                      : [...chal.positiveKeywords, word];
                  } else {
                    chal.negativeKeywords = chal.negativeKeywords.includes(word)
                      ? chal.negativeKeywords.filter(w => w !== word)
                      : [...chal.negativeKeywords, word];
                  }
                  challengers[challengerIdx] = chal;
                  return { ...current, challengers };
                });
              } else {
                updateEvaluationDraft(current => {
                  const evals = { ...current.evaluations };
                  const studentEval = evals[studentId] || { positiveKeywords: [], negativeKeywords: [], rating: '', comment: '' };
                  if (type === 'strengths') {
                    studentEval.positiveKeywords = studentEval.positiveKeywords.includes(word)
                      ? studentEval.positiveKeywords.filter(w => w !== word)
                      : [...studentEval.positiveKeywords, word];
                  } else {
                    studentEval.negativeKeywords = studentEval.negativeKeywords.includes(word)
                      ? studentEval.negativeKeywords.filter(w => w !== word)
                      : [...studentEval.negativeKeywords, word];
                  }
                  evals[studentId] = studentEval;
                  return { ...current, evaluations: evals };
                });
              }
            };

            // Handle setting rating
            const handleSetRating = (studentId, rating) => {
              if (evalDraft.status === 'submitted') return;
              updateEvaluationDraft(current => {
                const evals = { ...current.evaluations };
                const studentEval = evals[studentId] || { positiveKeywords: [], negativeKeywords: [], rating: '', comment: '' };
                studentEval.rating = studentEval.rating === rating ? '' : rating;
                evals[studentId] = studentEval;
                return { ...current, evaluations: evals };
              });
            };

            // Handle comment input
            const handleCommentChange = (studentId, text, isChallenger = false, challengerIdx = null) => {
              if (evalDraft.status === 'submitted') return;
              if (text.length > 500) return;
              if (isChallenger) {
                updateEvaluationDraft(current => {
                  const challengers = [...current.challengers];
                  challengers[challengerIdx] = { ...challengers[challengerIdx], comment: text };
                  return { ...current, challengers };
                });
              } else {
                updateEvaluationDraft(current => {
                  const evals = { ...current.evaluations };
                  const studentEval = evals[studentId] || { positiveKeywords: [], negativeKeywords: [], rating: '', comment: '' };
                  studentEval.comment = text;
                  evals[studentId] = studentEval;
                  return { ...current, evaluations: evals };
                });
              }
            };

            // Handle challenger student selection
            const handleSelectChallengerStudent = (idx, studentId) => {
              if (evalDraft.status === 'submitted') return;
              updateEvaluationDraft(current => {
                const challengers = [...current.challengers];
                challengers[idx] = { ...challengers[idx], studentId, positiveKeywords: [], negativeKeywords: [], comment: '' };
                return { ...current, challengers };
              });
            };

            // Submit logic
            const handleSubmit = async (e) => {
              e.preventDefault();

              // Build list of items to validate and submit
              const toValidate = [];
              if (roles.chairId) {
                toValidate.push({
                  id: roles.chairId,
                  name: chairStudent?.name || 'Chair',
                  role: 'chair',
                  data: evalDraft.evaluations[roles.chairId]
                });
              }
              presenters.forEach(p => {
                toValidate.push({
                  id: p.id,
                  name: p.name,
                  role: 'presenter',
                  data: evalDraft.evaluations[p.id]
                });
              });
              mentors.forEach(m => {
                toValidate.push({
                  id: m.id,
                  name: m.name,
                  role: 'mentor',
                  data: evalDraft.evaluations[m.id]
                });
              });

              const filledChallengers = evalDraft.challengers.filter(c => c.studentId);

              // Require at least 1 challenger
              if (filledChallengers.length === 0) {
                alert('Please select at least one Challenger before submitting.');
                return;
              }

              filledChallengers.forEach((c, idx) => {
                const name = resolveStudent(c.studentId, liveStudents, nominationNames)?.name || `Challenger ${idx+1}`;
                toValidate.push({
                  id: c.studentId,
                  name,
                  role: 'challenger',
                  data: c
                });
              });

              // Validate each has at least 1 keyword
              for (const item of toValidate) {
                const posLen = (item.data?.positiveKeywords || []).length;
                const negLen = (item.data?.negativeKeywords || []).length;
                if (posLen + negLen === 0) {
                  alert(`Please select at least one Strength or Coaching keyword for ${item.name} (${item.role}).`);
                  return;
                }
              }

              // Find this auditor's session_auditor_id
              const sessionAuditorId = session.auditorAssignments?.find(
                a => String(a.auditorId) === String(currentAuditorId)
              )?.id;

              if (!sessionAuditorId) {
                alert('Could not find your auditor assignment for this session. Please refresh and try again.');
                return;
              }

              // Optimistically lock the form
              updateEvaluationDraft(current => ({
                ...current,
                status: 'submitted',
                submittedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }));

              // POST + submit each evaluation to the backend
              let submitSucceeded = false;
              try {
                for (const item of toValidate) {
                  const payload = {
                    session_auditor_id: sessionAuditorId,
                    student_id: Number(item.id),
                    role: item.role,
                    positive_keywords: item.data?.positiveKeywords || [],
                    negative_keywords: item.data?.negativeKeywords || [],
                    rating: (item.role === 'presenter' || item.role === 'mentor' || item.role === 'challenger')
                      ? (Number(item.data?.rating) || null)
                      : null,
                    comment: item.data?.comment || '',
                  };

                  const existingEval = reports.find(r =>
                    String(r.sessionId) === String(evaluatingSessionId) &&
                    String(r.studentId) === String(item.id) &&
                    (r.role || '').toLowerCase() === item.role
                  );

                  let evalId;
                  if (existingEval?.id) {
                    await mentorApi.updateEvaluation(existingEval.id, {
                      positive_keywords: payload.positive_keywords,
                      negative_keywords: payload.negative_keywords,
                      rating: payload.rating,
                      comment: payload.comment,
                    });
                    evalId = existingEval.id;
                  } else {
                    const draft = await mentorApi.createEvaluation(payload);
                    evalId = draft.id;
                  }
                  await mentorApi.submitEvaluation(evalId);
                }
                submitSucceeded = true;

                // Best-effort enriched re-sync once session detail is available — non-blocking
                void refreshSessionAndProjections(evaluatingSessionId)
                  .then(refreshedSession => {
                    if (!refreshedSession?.id) return null;
                    return refreshEvaluationsReports(buildSessionsSnapshotWithRefresh(refreshedSession));
                  })
                  .catch(err => {
                    console.warn('Non-blocking post-submit session/report refresh failed:', err);
                  });

                await refreshEvaluationsReports(sessions);

                showToast('Evaluation submitted successfully!');
              } catch (err) {
                if (submitSucceeded) {
                  console.error('Evaluation submit succeeded but queue sync failed:', err);
                  showToast(formatApiError(err, 'Queue sync after submit'));
                  alert(
                    'Your evaluation was submitted, but the approvals queue could not be refreshed. '
                    + 'Please reload the page to confirm it appears for review.'
                  );
                  return;
                }
                console.error('Evaluation submit failed:', err);
                try {
                  await refreshEvaluationsReports(sessions);
                } catch (refreshErr) {
                  console.warn('Failed to refresh evaluations after submit error:', refreshErr);
                }
                updateEvaluationDraft(current => ({ ...current, status: 'draft', submittedAt: null }));
                alert('Failed to submit evaluation: ' + (err?.message || 'Unknown error. Please try again.'));
              }
            };

            const isSubmitted = evalDraft.status === 'submitted';
            const evalCohortName = allCohortsForDisplay.find(
              c => String(c.id) === String(evaluatingCohortId)
            )?.name || session.cohortName || session.groupName || '';

            return (
              <div className="evaluation-workspace animate-fade-in" style={{ paddingBottom: '120px', paddingTop: '24px' }}>
                <button type="button" className="btn-back-link" onClick={() => { const yearTab = session?.levelYear === 'Year 2' ? 'my_sessions_y2' : 'my_sessions_y1'; setEvaluatingSessionId(null); window.location.hash = `#/${yearTab}`; }}>
                  ← Back to My Sessions
                </button>
                {/* Sticky Header */}
                <div className="evaluation-sticky-header">
                  <div className="sticky-header-container">
                    <div className="sticky-header-meta">
                      <h2 className="sticky-title">{session.sessionType} — {evalCohortName}</h2>
                      <p className="sticky-subtitle">Room {session.sessionNumber + 2} • {session.timeSlot} • {session.schoolName}</p>
                    </div>
                    <a href={session.meetingLink} target="_blank" rel="noopener noreferrer" className="btn-sticky-join">
                      Join Zoom
                    </a>
                  </div>
                </div>

                {session.groupId && (session.groupCohortIds || []).length > 1 && (
                  <div style={{ marginBottom: '20px', maxWidth: '320px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                      Cohort
                    </label>
                    <select
                      className="form-control-select"
                      value={evaluatingCohortId}
                      onChange={(e) => {
                        const nextCohortId = e.target.value;
                        setEvaluatingCohortId(nextCohortId);
                        setSessionEvaluations(prev => ({
                          ...prev,
                          [evaluatingSessionId]: initEvaluation(evaluatingSessionId, nextCohortId),
                        }));
                      }}
                    >
                      {(session.groupCohortIds || []).map(cohortId => {
                        const cohort = allCohortsForDisplay.find(c => String(c.id) === String(cohortId));
                        return (
                          <option key={cohortId} value={cohortId}>
                            {cohort?.name || `Cohort ${cohortId}`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Session context card */}
                <div style={{ background: 'var(--neutral-50)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Date</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--neutral-800)' }}>{session.scheduledAt.split(/[T ]/)[0]}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Time Slot</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--neutral-800)' }}>{session.timeSlot}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>School</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--neutral-800)' }}>{session.schoolName || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Breakout Room</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--neutral-800)' }}>Room {session.sessionNumber + 2}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Status</div>
                    <span className={`status-pill ${(session.status || '').toLowerCase()}`}>{session.status}</span>
                  </div>
                </div>

                {isSubmitted && (
                  <div className="success-banner" style={{ background: '#ecfdf5', border: '1.5px solid #10b981', color: '#065f46', padding: '16px', borderRadius: '12px', marginBottom: '24px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>✓</span> Evaluation submitted. The Counselor will review and approve.
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* CHAIR SECTION */}
                  <div className="form-section-header">
                    <h3>Chair Evaluation</h3>
                    <div className="section-divider"></div>
                  </div>
                  {roles.chairId ? (
                    <div className="form-card-container">
                      <div className="student-card-header">
                        <span className="student-card-name">{chairStudent.name}</span>
                        <span className="role-badge chair">CHAIR</span>
                      </div>
                      
                      <div className="form-field-group">
                        <label>Strengths Demonstrated (Tap to Select)</label>
                        <div className="keyword-chips-grid">
                          {ROLE_KEYWORDS.chair.strengths.map(word => {
                            const isSelected = (evalDraft.evaluations[roles.chairId]?.positiveKeywords || []).includes(word);
                            return (
                              <button
                                type="button"
                                key={word}
                                className={`keyword-chip strength ${isSelected ? 'selected' : ''}`}
                                disabled={isSubmitted}
                                onClick={() => handleToggleKeyword(roles.chairId, 'strengths', word)}
                              >
                                {word}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="form-field-group">
                        <label>Coaching Required (Tap to Select)</label>
                        <div className="keyword-chips-grid">
                          {ROLE_KEYWORDS.chair.coaching.map(word => {
                            const isSelected = (evalDraft.evaluations[roles.chairId]?.negativeKeywords || []).includes(word);
                            return (
                              <button
                                type="button"
                                key={word}
                                className={`keyword-chip coaching ${isSelected ? 'selected' : ''}`}
                                disabled={isSubmitted}
                                onClick={() => handleToggleKeyword(roles.chairId, 'coaching', word)}
                              >
                                {word}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="form-field-group">
                        <label htmlFor={`comment-${roles.chairId}`}>Feedback / Comments</label>
                        <textarea
                          id={`comment-${roles.chairId}`}
                          className="form-control-textarea"
                          placeholder="Optional comment (max 500 chars)"
                          disabled={isSubmitted}
                          value={evalDraft.evaluations[roles.chairId]?.comment || ''}
                          onChange={(e) => handleCommentChange(roles.chairId, e.target.value)}
                        />
                        <span className="char-counter">
                          {(evalDraft.evaluations[roles.chairId]?.comment || '').length} / 500 characters
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="no-student-placeholder">No Chair nominated for this session.</div>
                  )}

                  {/* PRESENTERS SECTION */}
                  <div className="form-section-header" style={{ marginTop: '40px' }}>
                    <h3>Presenters Evaluation</h3>
                    <div className="section-divider"></div>
                  </div>
                  {presenterIds.length > 0 ? presenters.map(student => {
                    const studentEval = evalDraft.evaluations[student.id] || { positiveKeywords: [], negativeKeywords: [], rating: '', comment: '' };
                    return (
                      <div className="form-card-container" key={student.id} style={{ marginBottom: '24px' }}>
                        <div className="student-card-header">
                          <span className="student-card-name">{student.name}</span>
                          <span className="role-badge delegate">PRESENTER</span>
                        </div>

                        <div className="form-field-group">
                          <label>Strengths Demonstrated (Tap to Select)</label>
                          <div className="keyword-chips-grid">
                            {ROLE_KEYWORDS.presenter.strengths.map(word => {
                              const isSelected = studentEval.positiveKeywords.includes(word);
                              return (
                                <button
                                  type="button"
                                  key={word}
                                  className={`keyword-chip strength ${isSelected ? 'selected' : ''}`}
                                  disabled={isSubmitted}
                                  onClick={() => handleToggleKeyword(student.id, 'strengths', word)}
                                >
                                  {word}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="form-field-group">
                          <label>Coaching Required (Tap to Select)</label>
                          <div className="keyword-chips-grid">
                            {ROLE_KEYWORDS.presenter.coaching.map(word => {
                              const isSelected = studentEval.negativeKeywords.includes(word);
                              return (
                                <button
                                  type="button"
                                  key={word}
                                  className={`keyword-chip coaching ${isSelected ? 'selected' : ''}`}
                                  disabled={isSubmitted}
                                  onClick={() => handleToggleKeyword(student.id, 'coaching', word)}
                                >
                                  {word}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="form-field-group">
                          <label>Live Rating (1–10)</label>
                          <div className="rating-buttons-container">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => {
                              const isSelected = Number(studentEval.rating) === num;
                              return (
                                <button
                                  type="button"
                                  key={num}
                                  className={`rating-button ${isSelected ? 'selected' : ''}`}
                                  disabled={isSubmitted}
                                  onClick={() => handleSetRating(student.id, num)}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                          <span className="form-helper-text">Rate the student's performance from 1 (poor) to 10 (outstanding).</span>
                        </div>

                        <div className="form-field-group">
                          <label htmlFor={`comment-${student.id}`}>Feedback / Comments</label>
                          <textarea
                            id={`comment-${student.id}`}
                            className="form-control-textarea"
                            placeholder="Optional comment (max 500 chars)"
                            disabled={isSubmitted}
                            value={studentEval.comment || ''}
                            onChange={(e) => handleCommentChange(student.id, e.target.value)}
                          />
                          <span className="char-counter">
                            {(studentEval.comment || '').length} / 500 characters
                          </span>
                        </div>
                      </div>
                    );
                  }) : <div className="no-student-placeholder">No Presenters nominated.</div>}

                  {/* MENTORS SECTION */}
                  <div className="form-section-header" style={{ marginTop: '40px' }}>
                    <h3>Mentors Evaluation</h3>
                    <div className="section-divider"></div>
                  </div>
                  {mentorIds.length > 0 ? mentors.map(student => {
                    const studentEval = evalDraft.evaluations[student.id] || { positiveKeywords: [], negativeKeywords: [], rating: '', comment: '' };
                    return (
                      <div className="form-card-container" key={student.id} style={{ marginBottom: '24px' }}>
                        <div className="student-card-header">
                          <span className="student-card-name">{student.name}</span>
                          <span className="role-badge spoc">MENTOR</span>
                        </div>

                        <div className="form-field-group">
                          <label>Strengths Demonstrated (Tap to Select)</label>
                          <div className="keyword-chips-grid">
                            {ROLE_KEYWORDS.mentor.strengths.map(word => {
                              const isSelected = studentEval.positiveKeywords.includes(word);
                              return (
                                <button
                                  type="button"
                                  key={word}
                                  className={`keyword-chip strength ${isSelected ? 'selected' : ''}`}
                                  disabled={isSubmitted}
                                  onClick={() => handleToggleKeyword(student.id, 'strengths', word)}
                                >
                                  {word}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="form-field-group">
                          <label>Coaching Required (Tap to Select)</label>
                          <div className="keyword-chips-grid">
                            {ROLE_KEYWORDS.mentor.coaching.map(word => {
                              const isSelected = studentEval.negativeKeywords.includes(word);
                              return (
                                <button
                                  type="button"
                                  key={word}
                                  className={`keyword-chip coaching ${isSelected ? 'selected' : ''}`}
                                  disabled={isSubmitted}
                                  onClick={() => handleToggleKeyword(student.id, 'coaching', word)}
                                >
                                  {word}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="form-field-group">
                          <label>Live Rating (1–10)</label>
                          <div className="rating-buttons-container">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => {
                              const isSelected = Number(studentEval.rating) === num;
                              return (
                                <button
                                  type="button"
                                  key={num}
                                  className={`rating-button ${isSelected ? 'selected' : ''}`}
                                  disabled={isSubmitted}
                                  onClick={() => handleSetRating(student.id, num)}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                          <span className="form-helper-text">Rate the student's performance from 1 (poor) to 10 (outstanding).</span>
                        </div>

                        <div className="form-field-group">
                          <label htmlFor={`comment-${student.id}`}>Feedback / Comments</label>
                          <textarea
                            id={`comment-${student.id}`}
                            className="form-control-textarea"
                            placeholder="Optional comment (max 500 chars)"
                            disabled={isSubmitted}
                            value={studentEval.comment || ''}
                            onChange={(e) => handleCommentChange(student.id, e.target.value)}
                          />
                          <span className="char-counter">
                            {(studentEval.comment || '').length} / 500 characters
                          </span>
                        </div>
                      </div>
                    );
                  }) : <div className="no-student-placeholder">No Mentors nominated.</div>}

                  {/* CHALLENGERS SECTION */}
                  <div className="form-section-header" style={{ marginTop: '40px' }}>
                    <h3>{(roles.challengerIds || []).length > 0 ? 'Challengers (Logged by SPOC)' : 'Challengers (Live Selected)'}</h3>
                    <div className="section-divider"></div>
                  </div>

                  <div className="instruction-banner" style={{ background: '#fef3c7', borderLeft: '4px solid #d97706', color: '#92400e', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '24px', fontWeight: 500 }}>
                    {(roles.challengerIds || []).length > 0
                      ? '💡 Challengers were logged by the SPOC after the session. Confirm each student before submitting your evaluation.'
                      : '💡 Challengers are picked live by the Chair. If you cannot identify one, ask the Chair.'}
                  </div>

                  {openDropdownIdx !== null && (
                    <div 
                      className="dropdown-overlay" 
                      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9, background: 'transparent' }} 
                      onClick={() => setOpenDropdownIdx(null)} 
                    />
                  )}

                  {evalDraft.challengers.map((chal, idx) => {
                    if (idx === 3 && !evalDraft.show4thChallenger) return null;
                    const availableOptions = audienceMembers.filter(s => s.id === chal.studentId || !selectedChallengerIds.includes(s.id));

                    return (
                      <div className="form-card-container" key={idx} style={{ marginBottom: '24px' }}>
                        <div className="form-field-group">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label htmlFor={`challenger-select-${idx}`}>Select Challenger {idx + 1}</label>
                            {idx === 3 && !isSubmitted && (
                              <button
                                type="button"
                                className="remove-link-btn"
                                onClick={() => {
                                  updateEvaluationDraft(current => {
                                    const challengers = [...current.challengers];
                                    challengers[3] = { studentId: '', positiveKeywords: [], negativeKeywords: [], comment: '' };
                                    return { ...current, challengers, show4thChallenger: false };
                                  });
                                }}
                                style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          
                          <div className="custom-dropdown-container" style={{ position: 'relative' }}>
                            <button
                              type="button"
                              className="custom-dropdown-trigger"
                              disabled={isSubmitted}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSubmitted) return;
                                setOpenDropdownIdx(openDropdownIdx === idx ? null : idx);
                              }}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                background: '#ffffff',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                textAlign: 'left',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: isSubmitted ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                color: chal.studentId ? 'var(--neutral-900)' : 'var(--neutral-400)',
                                fontWeight: chal.studentId ? '600' : 'normal'
                              }}
                            >
                              <span>
                                {chal.studentId
                                  ? (audienceMembers.find(s => String(s.id) === String(chal.studentId))?.name
                                    || resolveStudent(chal.studentId, rosterPool, nominationNames)?.name
                                    || chal.studentId)
                                  : '-- Choose Challenger Student --'}
                              </span>
                              <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: openDropdownIdx === idx ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                            </button>
                            {openDropdownIdx === idx && !isSubmitted && (
                              <div
                                className="custom-dropdown-options"
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  background: '#ffffff',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '8px',
                                  marginTop: '4px',
                                  boxShadow: 'var(--shadow-lg)',
                                  zIndex: 10,
                                  maxHeight: '200px',
                                  overflowY: 'auto'
                                }}
                              >
                                <div
                                  className="custom-dropdown-option"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectChallengerStudent(idx, '');
                                    setOpenDropdownIdx(null);
                                  }}
                                  style={{
                                    padding: '10px 16px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: 'var(--neutral-500)',
                                    background: chal.studentId === '' ? 'var(--neutral-100)' : 'transparent'
                                  }}
                                >
                                  -- Choose Challenger Student --
                                </div>
                                {availableOptions.map(stud => (
                                  <div
                                    key={stud.id}
                                    className="custom-dropdown-option"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectChallengerStudent(idx, stud.id);
                                      setOpenDropdownIdx(null);
                                    }}
                                    style={{
                                      padding: '10px 16px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      color: 'var(--neutral-900)',
                                      background: chal.studentId === stud.id ? 'var(--neutral-100)' : 'transparent',
                                      borderTop: '1px solid var(--neutral-50)'
                                    }}
                                  >
                                    {stud.name}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {chal.studentId && (
                          <div className="animate-fade-in" style={{ marginTop: '20px' }}>
                            <div className="form-field-group">
                              <label>Strengths Demonstrated (Tap to Select)</label>
                              <div className="keyword-chips-grid">
                                {ROLE_KEYWORDS.challenger.strengths.map(word => {
                                  const isSelected = chal.positiveKeywords.includes(word);
                                  return (
                                    <button
                                      type="button"
                                      key={word}
                                      className={`keyword-chip strength ${isSelected ? 'selected' : ''}`}
                                      disabled={isSubmitted}
                                      onClick={() => handleToggleKeyword(null, 'strengths', word, true, idx)}
                                    >
                                      {word}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="form-field-group">
                              <label>Coaching Required (Tap to Select)</label>
                              <div className="keyword-chips-grid">
                                {ROLE_KEYWORDS.challenger.coaching.map(word => {
                                  const isSelected = chal.negativeKeywords.includes(word);
                                  return (
                                    <button
                                      type="button"
                                      key={word}
                                      className={`keyword-chip coaching ${isSelected ? 'selected' : ''}`}
                                      disabled={isSubmitted}
                                      onClick={() => handleToggleKeyword(null, 'coaching', word, true, idx)}
                                    >
                                      {word}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="form-field-group">
                              <label htmlFor={`comment-challenger-${idx}`}>Feedback / Comments</label>
                              <textarea
                                id={`comment-challenger-${idx}`}
                                className="form-control-textarea"
                                placeholder="Optional comment (max 500 chars)"
                                disabled={isSubmitted}
                                value={chal.comment}
                                onChange={(e) => handleCommentChange(null, e.target.value, true, idx)}
                              />
                              <span className="char-counter">
                                {chal.comment.length} / 500 characters
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {!evalDraft.show4thChallenger && !isSubmitted && (
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => updateEvaluationDraft(current => ({ ...current, show4thChallenger: true }))}
                      style={{ display: 'block', margin: '16px auto 32px auto', background: 'none', border: 'none', color: '#0147a2', fontSize: '14px', fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}
                    >
                      + Add 4th Challenger (Optional)
                    </button>
                  )}

                  {/* Sticky Footer */}
                  <div className="evaluation-sticky-footer">
                    <div className="sticky-footer-container">
                      <span className="autosave-indicator">
                        {isSubmitted ? `Submitted at ${evalDraft.submittedAt}` : 'Saved as draft · just now'}
                      </span>
                      <button
                        type="submit"
                        className="btn-submit-eval"
                        disabled={isSubmitted}
                      >
                        {isSubmitted ? 'Locked & Submitted' : 'Submit Evaluation'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            );
          })()}

          {/* SCREEN 2 DETAILED VIEW: PAST EVALUATION VIEW */}
          {viewingPastEvaluationId !== null && currentUser?.role === 'auditor' && (() => {
            const session = sessions.find(s => String(s.id) === String(viewingPastEvaluationId));
            if (!session) return null;
            const sessionReports = reports.filter(r => String(r.sessionId) === String(viewingPastEvaluationId));

            return (
              <div className="past-evaluation-view-container animate-fade-in" style={{ paddingBottom: '40px', paddingTop: '24px' }}>
                <button type="button" className="btn-back-link" onClick={() => { const yearTab = session?.levelYear === 'Year 2' ? 'my_sessions_y2' : 'my_sessions_y1'; setViewingPastEvaluationId(null); window.location.hash = `#/${yearTab}`; }}>
                  ← Back
                </button>
                <div className="evaluation-sticky-header">
                  <div className="sticky-header-container" style={{ justifyContent: 'flex-start', gap: '16px' }}>
                    <div>
                      <h2 className="sticky-title" style={{ fontSize: '18px' }}>Submitted Evaluation — {session.sessionType}</h2>
                      <p className="sticky-subtitle">{session.cohortName} • {session.timeSlot}</p>
                    </div>
                  </div>
                </div>

                {(() => {
                  const primaryReport = sessionReports[0];
                  if (primaryReport?.status === 'rejected') {
                    return (
                      <div className="rejection-banner" style={{ background: '#fef2f2', border: '1.5px solid #ef4444', color: '#991b1b', padding: '16px', borderRadius: '12px', marginBottom: '16px', fontWeight: 600 }}>
                        ✗ Evaluation Rejected — {primaryReport.rejectionReason || 'No reason given'}
                        <br /><small style={{ fontWeight: 500 }}>Re-evaluate to resubmit.</small>
                      </div>
                    );
                  }
                  if (primaryReport?.status === 'approved') {
                    return (
                      <div className="success-banner" style={{ background: '#ecfdf5', border: '1.5px solid #10b981', color: '#065f46', padding: '16px', borderRadius: '12px', marginBottom: '16px', fontWeight: 600 }}>
                        ✓ Evaluation Submitted & Approved by Counselor
                      </div>
                    );
                  }
                  if (primaryReport?.status === 'draft') {
                    return (
                      <div className="draft-banner" style={{ background: '#eff6ff', border: '1.5px solid #3b82f6', color: '#1e40af', padding: '16px', borderRadius: '12px', marginBottom: '16px', fontWeight: 600 }}>
                        ↩ Reopened for editing
                      </div>
                    );
                  }
                  return (
                    <div className="pending-banner" style={{ background: '#fffbeb', border: '1.5px solid #f59e0b', color: '#92400e', padding: '16px', borderRadius: '12px', marginBottom: '16px', fontWeight: 600 }}>
                      ⏳ Evaluation Submitted — Awaiting Review
                    </div>
                  );
                })()}

                <button
                  type="button"
                  className="btn-secondary-action"
                  style={{ marginBottom: '24px' }}
                  onClick={() => handleReopenEvaluation(session.id)}
                >
                  ↩ Re-evaluate
                </button>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {sessionReports.length > 0 ? sessionReports.map(rep => {
                    const isPresenterOrMentor = rep.roleBadge === 'Presenter' || rep.roleBadge === 'Mentor';
                    
                    let roleBadgeClass = 'delegate';
                    if (rep.roleBadge === 'Chair') roleBadgeClass = 'chair';
                    if (rep.roleBadge === 'Mentor') roleBadgeClass = 'spoc';
                    if (rep.roleBadge === 'Challenger') roleBadgeClass = 'challenger';

                    return (
                      <div className="form-card-container" key={rep.id}>
                        <div className="student-card-header">
                          <span className="student-card-name">{rep.studentName}</span>
                          <span className={`role-badge ${roleBadgeClass}`}>{rep.roleBadge}</span>
                        </div>

                        {isPresenterOrMentor && rep.rating && (
                          <div className="form-field-group" style={{ marginBottom: '16px' }}>
                            <label>Live Rating</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: 800, color: 'var(--accent-orange)' }}>{rep.rating}</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>/ 10</span>
                            </div>
                          </div>
                        )}

                        <div className="form-field-group" style={{ marginBottom: '16px' }}>
                          <label>Strengths Demonstrated</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                            {rep.keywords && rep.keywords.length > 0 ? rep.keywords.map(kw => (
                              <span key={kw} className="keyword-chip strength selected" style={{ padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold', border: 'none', pointerEvents: 'none' }}>
                                {kw}
                              </span>
                            )) : <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>None selected</span>}
                          </div>
                        </div>

                        <div className="form-field-group" style={{ marginBottom: '16px' }}>
                          <label>Coaching Required</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                            {rep.coachingKeywords && rep.coachingKeywords.length > 0 ? rep.coachingKeywords.map(kw => (
                              <span key={kw} className="keyword-chip coaching selected" style={{ padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold', border: 'none', pointerEvents: 'none' }}>
                                {kw}
                              </span>
                            )) : <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>None selected</span>}
                          </div>
                        </div>

                        {rep.comment && (
                          <div className="form-field-group" style={{ margin: 0 }}>
                            <label>Feedback Comment</label>
                            <p style={{ background: 'var(--neutral-50)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '13.5px', color: 'var(--neutral-800)', margin: '4px 0 0 0', fontStyle: 'italic', lineHeight: 1.4 }}>
                              "{rep.comment}"
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  }) : (
                    <div className="empty-state-card" style={{ padding: '40px' }}>
                      <p className="empty-state-description">No submitted reports were returned by the API.</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* SCREEN 1: AUDITOR DASHBOARD */}
          {activeTab === 'dashboard' && currentUser?.role === 'auditor' && evaluatingSessionId === null && viewingPastEvaluationId === null && (() => {
            const todayStr = new Date().toISOString().split('T')[0];
            
            // For auditor role, the backend already scopes /sessions to only assigned sessions.
            // isAssigned falls back to checking auditorSessionStatuses when currentAuditorId
            // hasn't resolved yet (liveAuditors empty at load time for auditor role).
            const isMySession = (s) =>
              (currentAuditorId && s.auditors.map(String).includes(String(currentAuditorId)))
              || s.id in auditorSessionStatuses;

            // Find today's session that is assigned to me and accepted
            const todaySess = sessions.find(s => {
              const isToday = s.scheduledAt.split(/[T ]/)[0] === todayStr;
              const isAccepted = auditorSessionStatuses[s.id] === 'accepted';
              const isPlannedOrLive = s.status === 'Planned' || s.status === 'Live';
              return isMySession(s) && isToday && isAccepted && isPlannedOrLive;
            });

            // Find pending sessions assigned to me
            const pendingSessions = sessions.filter(s => {
              const isPending = auditorSessionStatuses[s.id] === 'pending';
              const isPlannedOrLive = s.status === 'Planned' || s.status === 'Live';
              return isMySession(s) && isPending && isPlannedOrLive;
            });

            // Find all accepted sessions (today's + upcoming)
            const acceptedSessions = sessions.filter(s => {
              const isAccepted = auditorSessionStatuses[s.id] === 'accepted';
              const isPlannedOrLive = s.status === 'Planned' || s.status === 'Live';
              return isMySession(s) && isAccepted && isPlannedOrLive;
            });

            // Find all claimable sessions (Planned/Live and NOT assigned to me)
            const claimableSessions = sessions.filter(s => {
              const isPlannedOrLive = s.status === 'Planned' || s.status === 'Live';
              return !isMySession(s) && isPlannedOrLive;
            });

            // Toggle filter selection helper
            const toggleFilter = (filterType) => {
              setAuditorDashboardFilter(prev => prev === filterType ? 'all' : filterType);
            };

            return (
              <div className="auditor-dashboard-container animate-fade-in">
                {/* Dashboard Title */}
                <div className="dashboard-title-row" style={{ marginBottom: '16px' }}>
                  <div>
                    <h1 className="dashboard-title">Auditor Operations Console</h1>
                    <p className="dashboard-subtitle">Evaluate live sessions, manage availability, and review past submissions.</p>
                  </div>
                </div>

                {/* Stat Chips Row */}
                <div className="stat-chips-row" style={{ marginBottom: '24px' }}>
                  <button
                    type="button"
                    className={`stat-chip accepted ${auditorDashboardFilter === 'accepted' ? 'active' : ''}`}
                    onClick={() => toggleFilter('accepted')}
                  >
                    <span className="stat-chip-dot"></span>
                    <span className="stat-chip-label">Accepted</span>
                    <span className="stat-chip-count">{acceptedSessions.length}</span>
                  </button>
                  
                  <button
                    type="button"
                    className={`stat-chip pending ${auditorDashboardFilter === 'pending' ? 'active' : ''}`}
                    onClick={() => toggleFilter('pending')}
                  >
                    <span className="stat-chip-dot"></span>
                    <span className="stat-chip-label">Pending Response</span>
                    <span className="stat-chip-count">{pendingSessions.length}</span>
                  </button>

                  <button
                    type="button"
                    className={`stat-chip claimable ${auditorDashboardFilter === 'claimable' ? 'active' : ''}`}
                    onClick={() => toggleFilter('claimable')}
                  >
                    <span className="stat-chip-dot"></span>
                    <span className="stat-chip-label">Available to Claim</span>
                    <span className="stat-chip-count">{claimableSessions.length}</span>
                  </button>
                </div>

                {/* Active Filter Title */}
                {auditorDashboardFilter !== 'all' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 className="section-title" style={{ margin: 0, textTransform: 'capitalize' }}>
                      Showing: {auditorDashboardFilter === 'claimable' ? 'Available to Claim' : auditorDashboardFilter === 'pending' ? 'Pending Response' : 'Accepted Sessions'}
                    </h2>
                    <button 
                      className="link-btn" 
                      style={{ background: 'none', border: 'none', color: 'var(--primary-navy)', fontWeight: '600', cursor: 'pointer' }}
                      onClick={() => setAuditorDashboardFilter('all')}
                    >
                      Clear Filter (Show All)
                    </button>
                  </div>
                )}

                {/* Dashboard Main Content Area */}
                <div className="auditor-dashboard-content-area">
                  
                  {/* FILTER: ALL */}
                  {auditorDashboardFilter === 'all' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      
                      {/* Today's Session Section */}
                      <div>
                        <h2 className="section-title">📅 Today's Session</h2>
                        {todaySess ? (
                          <div className="today-session-accent-card" style={{ borderTop: '6px solid var(--accent-orange)' }}>
                            <div className="today-session-card-body">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                                <div>
                                  <span className="today-session-type-badge">{todaySess.sessionType}</span>
                                  <h3 className="today-session-title" style={{ marginBottom: '8px' }}>{todaySess.cohortName}</h3>
                                  <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--text-muted)' }}>🏫 {todaySess.schoolName}</p>
                                </div>
                                <span className="status-pill active" style={{ background: '#e0f2fe', color: '#0369a1' }}>Live Now</span>
                              </div>
                              
                              <div className="today-session-meta-grid" style={{ marginTop: '20px', marginBottom: '20px' }}>
                                <div>🕒 Time Slot: <strong>{todaySess.timeSlot}</strong></div>
                                <div>🚪 Breakout Room: <strong>Room {todaySess.sessionNumber + 2}</strong></div>
                                <div>📅 Date: <strong>Today ({todaySess.scheduledAt.split(/[T ]/)[0]})</strong></div>
                              </div>
                              
                              <div className="today-session-buttons-row">
                                <a
                                  href={todaySess.meetingLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn-join-room-spoc"
                                  style={{ background: 'var(--primary-navy)', color: '#ffffff', textAlign: 'center', margin: 0 }}
                                >
                                  Join Zoom Meeting
                                </a>
                                <button
                                  type="button"
                                  className="btn-submit-session"
                                  style={{ background: 'var(--accent-orange)', borderColor: 'var(--accent-orange)', color: '#ffffff', fontWeight: 'bold', margin: 0 }}
                                  onClick={() => { const yt = todaySess.levelYear === 'Year 2' ? 'my_sessions_y2' : 'my_sessions_y1'; setActiveTab(yt); window.location.hash = `#/${yt}/${todaySess.id}/evaluate`; setEvaluatingSessionId(todaySess.id); }}
                                >
                                  Start Live Student Evaluation
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="empty-state-card" style={{ padding: '24px' }}>
                            <div className="empty-state-icon-wrapper" style={{ background: 'rgba(107, 114, 128, 0.08)', color: '#6b7280', width: '40px', height: '40px', fontSize: '16px' }}>📅</div>
                            <h3 className="empty-state-heading" style={{ fontSize: '14px', marginTop: '8px' }}>No accepted sessions scheduled for today</h3>
                          </div>
                        )}
                      </div>

                      {/* Pending Assignments Section */}
                      <div>
                        <h2 className="section-title">⚠️ Pending Assignments ({pendingSessions.length})</h2>
                        {pendingSessions.length > 0 ? (
                          <div className="auditor-sessions-grid">
                            {pendingSessions.map(sess => (
                              <div className="compact-session-card" key={sess.id} style={{ borderLeft: '4px solid var(--accent-orange)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                  <div>
                                    <span className="today-session-type-badge" style={{ background: '#fef3c7', color: '#d97706' }}>{sess.sessionType}</span>
                                    <h3 className="today-session-title" style={{ fontSize: '15px', margin: '4px 0', fontWeight: 'bold' }}>{sess.cohortName}</h3>
                                    <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-muted)' }}>🏫 {sess.schoolName}</p>
                                  </div>
                                </div>

                                <div className="today-session-meta-grid" style={{ margin: '12px 0', fontSize: '12.5px', gap: '8px' }}>
                                  <div>📅 Date: <strong>{sess.scheduledAt.split(/[T ]/)[0]}</strong></div>
                                  <div>🕒 Slot: <strong>{sess.timeSlot}</strong></div>
                                  <div>🚪 Breakout: <strong>Room {sess.sessionNumber + 2}</strong></div>
                                </div>

                                <div className="pending-actions-row" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                  <button
                                    type="button"
                                    className="btn-wizard-next"
                                    style={{ flex: 1, background: '#10b981', borderColor: '#10b981', color: '#ffffff', padding: '8px', fontSize: '13px', margin: 0 }}
                                    onClick={() => handleAuditorResponse(sess.id, 'accepted')}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-reject-action"
                                    style={{ flex: 1, background: '#ef4444', borderColor: '#ef4444', color: '#ffffff', padding: '8px', fontSize: '13px', margin: 0 }}
                                    onClick={() => handleAuditorResponse(sess.id, 'declined')}
                                  >
                                    Decline
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="empty-state-card" style={{ padding: '24px' }}>
                            <h3 className="empty-state-heading" style={{ fontSize: '13.5px', color: 'var(--text-muted)' }}>No pending assignments</h3>
                          </div>
                        )}
                      </div>

                    </div>
                  )}

                  {/* FILTER: ACCEPTED */}
                  {auditorDashboardFilter === 'accepted' && (
                    <div className="auditor-sessions-grid">
                      {acceptedSessions.length > 0 ? (
                        acceptedSessions.map(sess => {
                          const isToday = sess.scheduledAt.split(/[T ]/)[0] === todayStr;
                          return (
                            <div className="compact-session-card" key={sess.id} style={{ borderLeft: isToday ? '4px solid var(--accent-orange)' : '4px solid #10b981' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                  <span className="today-session-type-badge" style={{ background: '#dcfce7', color: '#15803d' }}>{sess.sessionType}</span>
                                  <h3 className="today-session-title" style={{ fontSize: '15px', margin: '4px 0', fontWeight: 'bold' }}>{sess.cohortName}</h3>
                                  <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-muted)' }}>🏫 {sess.schoolName}</p>
                                </div>
                                {isToday && <span className="status-pill active" style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', fontSize: '10px' }}>Today</span>}
                              </div>

                              <div className="today-session-meta-grid" style={{ margin: '12px 0', fontSize: '12.5px', gap: '8px' }}>
                                <div>📅 Date: <strong>{sess.scheduledAt.split(/[T ]/)[0]}</strong></div>
                                <div>🕒 Slot: <strong>{sess.timeSlot}</strong></div>
                                <div>🚪 Breakout: <strong>Room {sess.sessionNumber + 2}</strong></div>
                              </div>

                              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <button
                                  type="button"
                                  className="btn-submit-session"
                                  style={{ flex: 1, padding: '8px', background: 'var(--primary-navy)', borderColor: 'var(--primary-navy)', color: '#ffffff', fontSize: '13px', margin: 0 }}
                                  onClick={() => { const yt = sess.levelYear === 'Year 2' ? 'my_sessions_y2' : 'my_sessions_y1'; setActiveTab(yt); window.location.hash = `#/${yt}/${sess.id}/evaluate`; setEvaluatingSessionId(sess.id); }}
                                >
                                  Evaluate
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary-action"
                                  style={{ flex: 1, padding: '8px', color: '#ef4444', borderColor: '#fee2e2', fontSize: '13px', margin: 0 }}
                                  onClick={() => setShowWithdrawConfirmId(sess.id)}
                                >
                                  Withdraw
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="empty-state-card" style={{ gridColumn: '1 / -1', padding: '32px' }}>
                          <h3 className="empty-state-heading" style={{ fontSize: '14px', color: 'var(--text-muted)' }}>No accepted sessions found</h3>
                        </div>
                      )}
                    </div>
                  )}

                  {/* FILTER: PENDING */}
                  {auditorDashboardFilter === 'pending' && (
                    <div className="auditor-sessions-grid">
                      {pendingSessions.length > 0 ? (
                        pendingSessions.map(sess => (
                          <div className="compact-session-card" key={sess.id} style={{ borderLeft: '4px solid var(--accent-orange)' }}>
                            <div>
                              <span className="today-session-type-badge" style={{ background: '#fef3c7', color: '#d97706' }}>{sess.sessionType}</span>
                              <h3 className="today-session-title" style={{ fontSize: '15px', margin: '4px 0', fontWeight: 'bold' }}>{sess.cohortName}</h3>
                              <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-muted)' }}>🏫 {sess.schoolName}</p>
                            </div>

                            <div className="today-session-meta-grid" style={{ margin: '12px 0', fontSize: '12.5px', gap: '8px' }}>
                              <div>📅 Date: <strong>{sess.scheduledAt.split(/[T ]/)[0]}</strong></div>
                              <div>🕒 Slot: <strong>{sess.timeSlot}</strong></div>
                              <div>🚪 Breakout: <strong>Room {sess.sessionNumber + 2}</strong></div>
                            </div>

                            <div className="pending-actions-row" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                              <button
                                type="button"
                                className="btn-wizard-next"
                                style={{ flex: 1, background: '#10b981', borderColor: '#10b981', color: '#ffffff', padding: '8px', fontSize: '13px', margin: 0 }}
                                onClick={() => handleAuditorResponse(sess.id, 'accepted')}
                              >
                                Accept
                              </button>
                              <button
                                type="button"
                                className="btn-reject-action"
                                style={{ flex: 1, background: '#ef4444', borderColor: '#ef4444', color: '#ffffff', padding: '8px', fontSize: '13px', margin: 0 }}
                                onClick={() => handleAuditorResponse(sess.id, 'declined')}
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="empty-state-card" style={{ gridColumn: '1 / -1', padding: '32px' }}>
                          <h3 className="empty-state-heading" style={{ fontSize: '14px', color: 'var(--text-muted)' }}>No pending assignments</h3>
                        </div>
                      )}
                    </div>
                  )}

                  {/* FILTER: CLAIMABLE */}
                  {auditorDashboardFilter === 'claimable' && (
                    <div className="auditor-sessions-grid">
                      {claimableSessions.length > 0 ? (
                        claimableSessions.map(sess => (
                          <div className="compact-session-card" key={sess.id} style={{ borderLeft: '4px solid #3b82f6' }}>
                            <div>
                              <span className="today-session-type-badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>{sess.sessionType}</span>
                              <h3 className="today-session-title" style={{ fontSize: '15px', margin: '4px 0', fontWeight: 'bold' }}>{sess.cohortName}</h3>
                              <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-muted)' }}>🏫 {sess.schoolName}</p>
                            </div>

                            <div className="today-session-meta-grid" style={{ margin: '12px 0', fontSize: '12.5px', gap: '8px' }}>
                              <div>📅 Date: <strong>{sess.scheduledAt.split(/[T ]/)[0]}</strong></div>
                              <div>🕒 Slot: <strong>{sess.timeSlot}</strong></div>
                              <div>🚪 Breakout: <strong>Room {sess.sessionNumber + 2}</strong></div>
                            </div>

                            <div style={{ marginTop: '12px' }}>
                              <button
                                type="button"
                                className="btn-submit-session"
                                style={{ width: '100%', padding: '8px', background: '#3b82f6', borderColor: '#3b82f6', color: '#ffffff', fontSize: '13px', margin: 0 }}
                                onClick={() => handleClaimSession(sess.id)}
                              >
                                Claim Session
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="empty-state-card" style={{ gridColumn: '1 / -1', padding: '32px' }}>
                          <h3 className="empty-state-heading" style={{ fontSize: '14px', color: 'var(--text-muted)' }}>No open sessions available to claim</h3>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
            );
          })()}

          {/* SCREEN 2: MY SESSIONS LIST VIEW */}
          {(activeTab === 'my_sessions_y1' || activeTab === 'my_sessions_y2') && currentUser?.role === 'auditor' && evaluatingSessionId === null && viewingPastEvaluationId === null && (() => {
            const isY2Tab = activeTab === 'my_sessions_y2';
            const yearFilter = (s) => isY2Tab ? s.levelYear === 'Year 2' : s.levelYear !== 'Year 2';
            const myUpcoming = filterAuditorUpcomingSessions(sessions, currentAuditorId, auditorSessionStatuses).filter(yearFilter);
            const myPast = filterAuditorPastSessions(sessions, currentAuditorId, auditorSessionStatuses).filter(yearFilter);
            const groupedCurrent = auditorSessionsTab === 'upcoming'
              ? Object.fromEntries(Object.entries(groupedAuditorUpcoming).map(([k, v]) => [k, v.filter(yearFilter)]).filter(([, v]) => v.length > 0))
              : Object.fromEntries(Object.entries(groupedAuditorPast).map(([k, v]) => [k, v.filter(yearFilter)]).filter(([, v]) => v.length > 0));
            const currentCount = auditorSessionsTab === 'upcoming' ? myUpcoming.length : myPast.length;

            const renderSessionCard = (sess) => {
              const status = auditorSessionStatuses[sess.id] || 'pending';
              const sessCohort = allCohortsForDisplay.find(c => String(c.id) === String(sess.cohortId));
              const resolvedLevelYear = sess.levelYear ?? sessCohort?.levelYear ?? null;
              const isYear2Session = resolvedLevelYear === 'Year 2';
              const isYear2CouncilEval = isYear2Session && isCouncilSession(sess);
              const isYear2OrientationOnly = isYear2Session && isOrientationSession(sess);
              const councilNumber = sess.councilNumber || councilNumberFromSession(sess) || '';
              let statusBadge = null;
              if (sess.status === 'Completed') {
                statusBadge = <span className="status-pill completed">Completed</span>;
              } else if (status === 'pending') {
                statusBadge = <span className="status-pill active" style={{ background: '#fef3c7', color: '#d97706' }}>Pending</span>;
              } else if (status === 'accepted') {
                statusBadge = <span className="status-pill active" style={{ background: '#dcfce7', color: '#15803d' }}>Accepted</span>;
              } else if (status === 'declined') {
                statusBadge = <span className="status-pill active" style={{ background: '#fee2e2', color: '#b91c1c' }}>Declined</span>;
              } else if (status === 'withdrawn') {
                statusBadge = <span className="status-pill active" style={{ background: '#f3f4f6', color: '#4b5563' }}>Withdrawn</span>;
              }

              return (
                <div className="timeline-session-card" key={sess.id}>
                  <div className="session-card-header">
                    <div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span className="session-card-type">{sess.sessionType}</span>
                        {resolvedLevelYear && <YearLevelBadge levelYear={resolvedLevelYear} />}
                        {isYear2CouncilEval && councilNumber && (
                          <span className="status-badge status-planned" style={{ fontSize: '11px', padding: '3px 8px' }}>
                            Council {councilNumber}
                          </span>
                        )}
                      </div>
                      <h3 style={{ margin: '6px 0 2px', fontSize: '14px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{sess.cohortName}</h3>
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>🏫 {sess.schoolName}</p>
                    </div>
                    {statusBadge}
                  </div>

                  <div className="session-card-meta" style={{ marginTop: '12px' }}>
                    <div>📅 Date: <strong>{sess.scheduledAt.split(/[T ]/)[0]}</strong></div>
                    <div style={{ marginTop: '2px' }}>🕒 Slot: <strong>{sess.timeSlot}</strong></div>
                    <div style={{ marginTop: '2px' }}>🚪 Breakout: <strong>Room {sess.sessionNumber + 2}</strong></div>
                  </div>

                  <div className="session-card-footer" style={{ marginTop: '12px' }}>
                    {sess.status === 'Completed' ? (
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {isYear2CouncilEval ? (
                          <button
                            type="button"
                            className="btn-secondary-action"
                            style={{ padding: '8px 12px', fontSize: '12.5px' }}
                            onClick={() => { setEvaluatingSessionId(sess.id); window.location.hash = `#/${activeTab}/${sess.id}/evaluate`; }}
                          >
                            Evaluate
                          </button>
                        ) : isYear2OrientationOnly ? (
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            Council evaluation opens on Council sessions.
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn-secondary-action"
                            style={{ padding: '8px 12px', fontSize: '12.5px' }}
                            onClick={() => { setViewingPastEvaluationId(sess.id); window.location.hash = `#/${activeTab}/${sess.id}/past`; }}
                          >
                            View Evaluation
                          </button>
                        )}
                        {isYear2CouncilEval ? (
                          <button
                            type="button"
                            className="btn-secondary-action"
                            style={{ padding: '8px 12px', fontSize: '12.5px' }}
                            onClick={() => { setEvaluatingSessionId(sess.id); window.location.hash = `#/${activeTab}/${sess.id}/evaluate`; }}
                          >
                            Re-evaluate
                          </button>
                        ) : reports.some(r => String(r.sessionId) === String(sess.id)) && (
                          <button
                            type="button"
                            className="btn-secondary-action"
                            style={{ padding: '8px 12px', fontSize: '12.5px' }}
                            onClick={() => handleReopenEvaluation(sess.id)}
                          >
                            Re-evaluate
                          </button>
                        )}
                      </div>
                    ) : status === 'pending' ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          className="btn-wizard-next"
                          style={{ flex: 1, background: '#10b981', borderColor: '#10b981', color: '#fff', padding: '8px' }}
                          onClick={() => handleAuditorResponse(sess.id, 'accepted')}
                        >Accept</button>
                        <button
                          type="button"
                          className="btn-reject-action"
                          style={{ flex: 1, background: '#ef4444', borderColor: '#ef4444', color: '#fff', padding: '8px' }}
                          onClick={() => handleAuditorResponse(sess.id, 'declined')}
                        >Decline</button>
                      </div>
                    ) : status === 'accepted' ? (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {isYear2OrientationOnly ? (
                          <span style={{ flex: 1, fontSize: '12px', color: 'var(--text-muted)' }}>
                            Council evaluation opens on Council sessions.
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn-submit-session"
                            style={{ flex: 1, padding: '8px', background: 'var(--primary-navy)', borderColor: 'var(--primary-navy)', color: '#fff', fontWeight: 'bold' }}
                            onClick={() => { setEvaluatingSessionId(sess.id); window.location.hash = `#/${activeTab}/${sess.id}/evaluate`; }}
                          >Evaluate</button>
                        )}
                        <button
                          type="button"
                          className="btn-secondary-action"
                          style={{ flex: isYear2OrientationOnly ? '0 0 auto' : 1, padding: '8px', color: '#ef4444', borderColor: '#fee2e2' }}
                          onClick={() => setShowWithdrawConfirmId(sess.id)}
                        >Withdraw</button>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            };

            return (
              <div className="auditor-sessions-container animate-fade-in">
                <div className="dashboard-title-row">
                  <div>
                    <h1 className="dashboard-title">{isY2Tab ? 'My Year 2 Sessions' : 'My Year 1 Sessions'}</h1>
                    <p className="dashboard-subtitle">{isY2Tab ? 'Glosco squad sessions and assessments.' : 'Council sessions, evaluations, and past submissions.'}</p>
                  </div>
                </div>

                <div className="auditor-tab-row" style={{ marginBottom: '24px' }}>
                  <button
                    type="button"
                    className={`auditor-tab-button ${auditorSessionsTab === 'upcoming' ? 'active' : ''}`}
                    onClick={() => setAuditorSessionsTab('upcoming')}
                  >
                    Upcoming Sessions ({myUpcoming.length})
                  </button>
                  <button
                    type="button"
                    className={`auditor-tab-button ${auditorSessionsTab === 'past' ? 'active' : ''}`}
                    onClick={() => setAuditorSessionsTab('past')}
                  >
                    Past Sessions ({myPast.length})
                  </button>
                </div>

                {currentCount > 0 ? (
                  Object.keys(groupedCurrent).map(weekLabel => (
                    <div key={weekLabel} className="timeline-week-group">
                      <div className="timeline-week-header">
                        <span>{weekLabel}</span>
                      </div>
                      <div className="timeline-grid">
                        {groupedCurrent[weekLabel].map(sess => renderSessionCard(sess))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state-card" style={{ padding: '64px 24px' }}>
                    <div className="empty-state-icon-wrapper" style={{ background: 'rgba(107, 114, 128, 0.08)', color: '#6b7280' }}>📅</div>
                    <h3 className="empty-state-heading" style={{ fontSize: '15px' }}>No sessions found</h3>
                    <p className="empty-state-description" style={{ fontSize: '13px' }}>
                      You have no {auditorSessionsTab} session assignments in the list.
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* SCREEN 3: AUDITOR AVAILABILITY EDITOR */}
          {activeTab === 'availability' && currentUser?.role === 'auditor' && evaluatingSessionId === null && viewingPastEvaluationId === null && (() => {
            const handleToggleCell = (day, slot) => {
              setMyAvailability(prev => ({
                ...prev,
                [day]: {
                  ...prev[day],
                  [slot]: !prev[day][slot]
                }
              }));
            };

            const handleSave = async () => {
              try {
                // Day-based API: POST full day×slot grid (day_id + start/end times).
                await mentorApi.saveAuditorAvailability(myAvailability);
                // Keep local grid; re-fetch so UI matches server (directory fallback if
                // GET /auditor/availability still returns [] after a successful save).
                invalidateQuery(['mentor', 'auditor', 'availability']);
                showToast('Availability saved.');
              } catch (err) {
                showToast('Failed to save availability: ' + (err.message || 'Unknown error'));
              }
            };

            return (
              <div className="auditor-availability-container animate-fade-in">
                {/* Header */}
                <div className="dashboard-title-row">
                  <div>
                    <h1 className="dashboard-title">My Availability</h1>
                    <p className="dashboard-subtitle">Toggle your weekly availability slots. Active session slots are locked.</p>
                  </div>
                </div>

                <div className="form-card-container" style={{ overflow: 'hidden' }}>
                  {/* Horizontally scrolling wrapper */}
                  <div className="availability-grid-scrollbox" style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                    <table className="availability-table" style={{ width: '100%', minWidth: '650px', borderCollapse: 'collapse', textAlign: 'center' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '12px', border: '1px solid var(--border-color)', fontWeight: 'bold', fontSize: '13.5px', background: 'var(--neutral-50)', textAlign: 'left', minWidth: '110px' }}>Time Slot</th>
                          {DAYS_OF_WEEK.map(day => (
                            <th key={day} style={{ padding: '12px', border: '1px solid var(--border-color)', fontWeight: 'bold', fontSize: '13.5px', background: 'var(--neutral-50)' }}>
                              {day}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {TIME_SLOTS.map(slot => (
                          <tr key={slot}>
                            <td style={{ padding: '12px', border: '1px solid var(--border-color)', fontWeight: 'bold', fontSize: '12.5px', background: 'var(--neutral-50)', textAlign: 'left', color: 'var(--neutral-700)' }}>
                              {slot}
                            </td>
                            {DAYS_OF_WEEK.map(day => {
                              const isAvailable = myAvailability[day]?.[slot];
                              
                              const isCellLocked = sessions.some(s => {
                                const isAssigned = (currentAuditorId && s.auditors.map(String).includes(String(currentAuditorId))) || s.id in auditorSessionStatuses;
                                const isAccepted = auditorSessionStatuses[s.id] === 'accepted';
                                const isPlannedOrLive = s.status === 'Planned' || s.status === 'Live';
                                const sessionDay = getDayOfWeek(s.scheduledAt);
                                return isAssigned && isAccepted && isPlannedOrLive && sessionDay === day && slotsMatch(s, slot);
                              });

                              return (
                                <td
                                  key={day}
                                  onClick={() => handleToggleCell(day, slot)}
                                  className={`availability-grid-cell ${isCellLocked ? 'locked' : ''} ${isAvailable ? 'available' : 'unavailable'}`}
                                  style={{
                                    border: '1px solid var(--border-color)',
                                    height: '48px',
                                    minWidth: '60px',
                                    cursor: 'pointer',
                                    transition: 'var(--transition-smooth)',
                                    position: 'relative'
                                  }}
                                  title={isCellLocked ? 'Contact PA to change' : isAvailable ? 'Available' : 'Unavailable'}
                                >
                                  {isCellLocked && (
                                    <span style={{ fontSize: '12px', position: 'absolute', top: '2px', right: '4px' }}>🔒</span>
                                  )}
                                  <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{isAvailable ? 'FREE' : ''}</span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ marginTop: '24px' }}>
                    <button
                      type="button"
                      className="btn-create-cohort"
                      style={{ width: '100%', background: 'var(--accent-orange)' }}
                      onClick={handleSave}
                    >
                      Save Availability
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* WITHDRAW CONFIRMATION MODAL */}
          {showWithdrawConfirmId && (
            <div className="modal-overlay animate-fade-in" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
              <div className="modal-card" style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '100%', boxShadow: 'var(--shadow-lg)', animation: 'scaleIn 0.3s ease-out' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--neutral-900)', marginBottom: '12px' }}>Confirm Withdrawal</h3>
                <p style={{ fontSize: '14.5px', color: 'var(--neutral-600)', lineHeight: 1.5, marginBottom: '24px' }}>
                  Are you sure? PA and Counselor will be notified.
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    className="btn-secondary-action"
                    style={{ flex: 1, padding: '12px' }}
                    onClick={() => setShowWithdrawConfirmId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-submit-session"
                    style={{ flex: 1, padding: '12px', background: '#ef4444', borderColor: '#ef4444', color: '#ffffff' }}
                    onClick={() => {
                      handleAuditorWithdraw(showWithdrawConfirmId);
                      setShowWithdrawConfirmId(null);
                    }}
                  >
                    Yes, Withdraw
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* COUNSELOR WORKFLOWS */}
          {/* ========================================================= */}

          {/* Screen 1: Counselor Dashboard */}
          {activeTab === 'dashboard' && currentUser?.role === 'counselor' && !selectedCohort && !wizardState && !sessionFormState && !outcomeFormSessionId && (
            <div className="animate-fade-in">
              {/* Page Header */}
              <div className="dashboard-title-row">
                <div>
                  <h1 className="dashboard-title">Counselor Dashboard</h1>
                  <p className="dashboard-subtitle">Manage breakout room assignments, monitor live fellowship sessions, and review evaluation reports.</p>
                </div>
              </div>

              {/* Pending Approvals Card */}
              <div
                className="dashboard-stats-card approvals-alert-card clickable"
                onClick={() => handleNavigate('approvals')}
                style={{
                  background: '#ffffff',
                  borderLeft: '6px solid #f89f09',
                  boxShadow: 'var(--shadow-md)',
                  borderRadius: '8px',
                  padding: '24px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}
              >
                <div>
                  <span style={{ fontSize: '12.5px', textTransform: 'uppercase', fontWeight: 700, color: 'var(--neutral-500)', letterSpacing: '0.5px' }}>
                    Needs Review
                  </span>
                  <h2 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--primary-navy)', margin: '4px 0 0 0' }}>
                    {reports.filter(r => r.status === 'pending').length} evaluations awaiting your approval
                  </h2>
                </div>
                <div style={{ fontSize: '24px', color: '#f89f09' }}>➔</div>
              </div>

              {/* Today's Sessions Section */}
              <div style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--primary-navy)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📅</span> Today's Sessions
                </h2>
                
                {(() => {
                  const todayStr = new Date().toISOString().split('T')[0];
                  const todaySess = sessions.filter(s => s.scheduledAt.startsWith(todayStr) && s.status !== 'Completed');
                  
                  if (todaySess.length === 0) {
                    return (
                      <div className="empty-state-card" style={{ padding: '24px' }}>
                        <p style={{ margin: 0, color: 'var(--text-muted)' }}>No sessions scheduled for today.</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="sessions-today-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                      {todaySess.map(s => {
                        const timeStr = s.scheduledAt.split(/[T ]/)[1] || '';
                        return (
                          <div
                            key={s.id}
                            className="session-card"
                            style={{
                              background: '#ffffff',
                              border: '1px solid var(--border-color)',
                              borderRadius: '8px',
                              padding: '18px',
                              boxShadow: 'var(--shadow-sm)',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              minHeight: '160px'
                            }}
                          >
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <span className="status-pill active" style={{ background: 'rgba(1, 71, 162, 0.08)', color: '#0147a2' }}>
                                  {s.sessionType}
                                </span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--neutral-500)' }}>
                                  {s.timeSlot || timeStr}
                                </span>
                              </div>
                              <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--primary-navy)', margin: '0 0 4px 0' }}>
                                {s.cohortName}
                              </h3>
                              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 16px 0' }}>
                                {s.schoolName}
                              </p>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '10px' }}>
                              <button
                                className="btn-secondary-action"
                                style={{ flex: 1, padding: '10px', fontSize: '12px' }}
                                onClick={() => {
                                  setSelectedCounselorSessionId(s.id);
                                  window.location.hash = `#/sessions/${s.id}`;
                                  setCounselorSessionsTab('today');
                                  handleNavigate('sessions');
                                }}
                              >
                                View Live Session
                              </button>
                              <a
                                href={s.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-submit-session"
                                style={{ flex: 1, padding: '10px', fontSize: '12px', textAlign: 'center', textDecoration: 'none', background: '#0147a2', borderColor: '#0147a2' }}
                              >
                                Join Zoom
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Upcoming Sessions Section */}
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--primary-navy)', marginBottom: '16px' }}>
                  Upcoming Sessions
                </h2>
                <div className="students-table-card" style={{ padding: '0px' }}>
                  {(() => {
                    const todayStr = new Date().toISOString().split('T')[0];
                    const upcomingSess = sessions.filter(s => {
                      const dateStr = s.scheduledAt.split(/[T ]/)[0];
                      return dateStr > todayStr && s.status !== 'Completed';
                    });
                    
                    if (upcomingSess.length === 0) {
                      return (
                        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                          No upcoming sessions scheduled.
                        </div>
                      );
                    }
                    
                    return (
                      <React.Fragment>
                        {/* Desktop Table View */}
                        <div className="desktop-upcoming-sessions">
                          <table className="students-table">
                            <thead>
                              <tr>
                                <th>Session Type</th>
                                <th>Cohort</th>
                                <th>School</th>
                                <th>Date & Time</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {upcomingSess.map(s => {
                                const dateObj = new Date(s.scheduledAt);
                                const formattedDate = dateObj.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) + ' @ ' + (s.timeSlot || dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
                                return (
                                  <tr key={s.id} className="cohort-table-row">
                                    <td><strong>{s.sessionType}</strong></td>
                                    <td>{s.cohortName}</td>
                                    <td>{s.schoolName}</td>
                                    <td>{formattedDate}</td>
                                    <td>
                                      <span className="status-pill pending" style={{ background: 'rgba(248, 159, 9, 0.08)', color: '#f89f09' }}>
                                        Scheduled
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile Stacked Card List View */}
                        <div className="mobile-upcoming-sessions" style={{ padding: '16px' }}>
                          {upcomingSess.map(s => {
                            const dateObj = new Date(s.scheduledAt);
                            const formattedDate = dateObj.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) + ' @ ' + (s.timeSlot || dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
                            return (
                              <div
                                key={s.id}
                                className="session-card"
                                style={{
                                  background: '#ffffff',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '8px',
                                  padding: '16px',
                                  boxShadow: 'var(--shadow-sm)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '12px',
                                  marginBottom: '16px'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <strong style={{ fontSize: '15px', color: 'var(--primary-navy)' }}>{s.sessionType}</strong>
                                  <span className="status-pill pending" style={{ background: 'rgba(248, 159, 9, 0.08)', color: '#f89f09' }}>
                                    Scheduled
                                  </span>
                                </div>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13.5px', color: 'var(--neutral-700)' }}>
                                  <div>
                                    <span style={{ color: 'var(--neutral-500)', fontWeight: 600 }}>Cohort:</span> {s.cohortName}
                                  </div>
                                  <div>
                                    <span style={{ color: 'var(--neutral-500)', fontWeight: 600 }}>School:</span> {s.schoolName}
                                  </div>
                                  <div>
                                    <span style={{ color: 'var(--neutral-500)', fontWeight: 600 }}>Date & Time:</span> {formattedDate}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </React.Fragment>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Screen 2: Sessions List View & Live Session View */}
          {activeTab === 'sessions' && currentUser?.role === 'counselor' && !selectedCohort && !wizardState && !sessionFormState && !outcomeFormSessionId && (
            <div className="animate-fade-in">
              {selectedCounselorSessionId === null ? (
                <div>
                  {/* Sessions List View */}
                  <div className="dashboard-title-row">
                    <div>
                      <h1 className="dashboard-title">Fellowship Sessions</h1>
                      <p className="dashboard-subtitle">Monitor fellowship sessions, allocate breakout rooms, and audit auditor attendance.</p>
                    </div>
                  </div>

                  {/* Tabs Row */}
                  <div className="approvals-tab-row" style={{ marginBottom: '24px' }}>
                    <button
                      className={`approvals-tab-button ${counselorSessionsTab === 'today' ? 'active' : ''}`}
                      onClick={() => setCounselorSessionsTab('today')}
                    >
                      Today
                    </button>
                    <button
                      className={`approvals-tab-button ${counselorSessionsTab === 'upcoming' ? 'active' : ''}`}
                      onClick={() => setCounselorSessionsTab('upcoming')}
                    >
                      Upcoming
                    </button>
                    <button
                      className={`approvals-tab-button ${counselorSessionsTab === 'past' ? 'active' : ''}`}
                      onClick={() => setCounselorSessionsTab('past')}
                    >
                      Past
                    </button>
                  </div>

                  {/* Sessions Grid */}
                  {(() => {
                    const todayStr = new Date().toISOString().split('T')[0];
                    
                    let filteredList = [];
                    if (counselorSessionsTab === 'today') {
                      filteredList = sessions.filter(s => s.scheduledAt.startsWith(todayStr) && s.status !== 'Completed');
                    } else if (counselorSessionsTab === 'upcoming') {
                      filteredList = sessions.filter(s => {
                        const dateStr = s.scheduledAt.split(/[T ]/)[0];
                        return dateStr > todayStr && s.status !== 'Completed';
                      });
                    } else {
                      filteredList = sessions.filter(s => {
                        const dateStr = s.scheduledAt.split(/[T ]/)[0];
                        return s.status === 'Completed' || (dateStr < todayStr && !s.scheduledAt.startsWith(todayStr));
                      });
                    }

                    if (filteredList.length === 0) {
                      return (
                        <div className="empty-state-card animate-fade-in">
                          <div className="empty-state-icon-wrapper" style={{ background: 'var(--neutral-100)', color: 'var(--text-muted)' }}>
                            <span>📅</span>
                          </div>
                          <h2 className="empty-state-heading">No sessions found</h2>
                          <p className="empty-state-description">There are no sessions in this category at the moment.</p>
                        </div>
                      );
                    }

                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
                        {filteredList.map(s => {
                          const dateObj = new Date(s.scheduledAt);
                          const formattedDate = dateObj.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
                          const timeStr = s.timeSlot || dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                          
                          return (
                            <div
                              key={s.id}
                              className="session-card clickable"
                              onClick={() => { setSelectedCounselorSessionId(s.id); window.location.hash = `#/sessions/${s.id}`; }}
                              style={{
                                background: '#ffffff',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                padding: '20px',
                                boxShadow: 'var(--shadow-sm)',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                              }}
                            >
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                  <span className="status-pill active" style={{ background: 'rgba(1, 71, 162, 0.08)', color: '#0147a2' }}>
                                    {s.sessionType}
                                  </span>
                                  <span className={`status-pill ${s.status === 'Completed' ? 'completed' : 'pending'}`} style={{
                                    background: s.status === 'Completed' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(248, 159, 9, 0.08)',
                                    color: s.status === 'Completed' ? '#10b981' : '#f89f09'
                                  }}>
                                    {s.status}
                                  </span>
                                </div>
                                
                                <h3 style={{ fontSize: '16px', fontWeight: 800, color: 'var(--primary-navy)', margin: '0 0 6px 0' }}>
                                  Session {s.sessionNumber}: {s.cohortName}
                                </h3>
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 12px 0' }}>
                                  🏢 {s.schoolName}
                                </p>
                                
                                <div style={{ fontSize: '13px', color: 'var(--neutral-700)', marginBottom: '16px' }}>
                                  <strong>🕒 Time:</strong> {formattedDate} @ {timeStr}
                                </div>

                                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', marginBottom: '16px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                                    Assigned Auditors
                                  </span>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {s.auditors.map(audId => {
                                      const aud = activeAuditors.find(a => a.id === audId);
                                      const isNoShow = sessionNoShows[s.id]?.[audId];
                                      return (
                                        <span
                                          key={audId}
                                          className="status-pill"
                                          style={{
                                            background: isNoShow ? 'rgba(239, 68, 68, 0.08)' : 'rgba(30, 56, 114, 0.05)',
                                            color: isNoShow ? '#ef4444' : 'var(--dark-navy)',
                                            fontSize: '11.5px',
                                            fontWeight: 600
                                          }}
                                        >
                                          {aud?.name || audId} {isNoShow && '(No-show)'}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>

                              <div style={{ display: 'flex', gap: '10px' }} onClick={(e) => e.stopPropagation()}>
                                <button
                                  className="btn-secondary-action"
                                  style={{ flex: 1, padding: '10px', fontSize: '12.5px' }}
                                  onClick={() => { setSelectedCounselorSessionId(s.id); window.location.hash = `#/sessions/${s.id}`; }}
                                >
                                  Manage Rooms
                                </button>
                                <a
                                  href={s.meetingLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn-submit-session"
                                  style={{ flex: 1, padding: '10px', fontSize: '12.5px', textAlign: 'center', textDecoration: 'none', background: '#0147a2', borderColor: '#0147a2' }}
                                >
                                  Join Zoom
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div>
                  {/* Live Session View */}
                  {(() => {
                    const session = sessions.find(s => s.id === selectedCounselorSessionId);
                    if (!session) return null;
                    
                    const roomAssignments = getSessionRoomAssignments(session);
                    
                    return (
                      <div className="animate-fade-in">
                        <button
                          className="btn-back-link"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0147a2',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginBottom: '16px',
                            padding: 0
                          }}
                          onClick={() => { setSelectedCounselorSessionId(null); window.location.hash = '#/sessions'; }}
                        >
                          ← Back to Sessions List
                        </button>

                        <div className="dashboard-title-row" style={{ marginBottom: '24px' }}>
                          <div>
                            <h1 className="dashboard-title">{session.sessionType}: {session.cohortName}</h1>
                            <p className="dashboard-subtitle">Manage breakout rooms, adjust assignments, and record attendance in real time.</p>
                          </div>
                        </div>

                        {/* Breakout Rooms */}
                        <h2 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--primary-navy)', marginBottom: '16px' }}>
                          Breakout Room Auditor Allocation
                        </h2>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
                          {Object.keys(roomAssignments).map(roomNum => {
                            const audId = roomAssignments[roomNum];
                            const aud = activeAuditors.find(a => a.id === audId);
                            const isNoShow = sessionNoShows[session.id]?.[audId];
                            
                            return (
                              <div
                                key={roomNum}
                                className="room-card"
                                style={{
                                  background: '#ffffff',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: '8px',
                                  padding: '20px',
                                  boxShadow: 'var(--shadow-sm)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  minHeight: '200px'
                                }}
                              >
                                <div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ fontSize: '16px', fontWeight: 800, color: 'var(--primary-navy)', margin: 0 }}>
                                      Room {roomNum}
                                    </h3>
                                    {audId ? (
                                      <span className={`status-pill ${isNoShow ? 'declined' : 'active'}`} style={{
                                        background: isNoShow ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                                        color: isNoShow ? '#ef4444' : '#10b981',
                                        fontWeight: 700
                                      }}>
                                        {isNoShow ? 'No-show' : 'Accepted'}
                                      </span>
                                    ) : (
                                      <span className="status-pill pending" style={{ background: 'var(--neutral-100)', color: 'var(--text-muted)' }}>
                                        Unassigned
                                      </span>
                                    )}
                                  </div>

                                  {aud ? (
                                    <div style={{ marginBottom: '24px' }}>
                                      <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--neutral-800)' }}>
                                        {aud.name}
                                      </div>
                                      <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                        {aud.email}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ color: 'var(--text-muted)', fontSize: '14px', fontStyle: 'italic', marginBottom: '24px' }}>
                                      No auditor assigned to this room.
                                    </div>
                                  )}
                                </div>

                                <div>
                                  {/* Manual Reassign dropdown */}
                                  {audId && (
                                    <div style={{ marginBottom: '16px' }}>
                                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', marginBottom: '6px' }}>
                                        Move to Breakout Room
                                      </label>
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <select
                                          className="styled-select-input"
                                          value={roomNum}
                                          onChange={(e) => handleReassignRoom(session.id, Number(roomNum), Number(e.target.value))}
                                          style={{ flex: 1, padding: '8px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid var(--border-color)', outline: 'none' }}
                                        >
                                          {Object.keys(roomAssignments).map(rNum => (
                                            <option key={rNum} value={rNum}>
                                              Room {rNum}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  )}

                                  {/* Mark No-Show button */}
                                  {audId && !isNoShow && (
                                    <button
                                      type="button"
                                      className="btn-secondary-action"
                                      style={{
                                        width: '100%',
                                        padding: '10px',
                                        fontSize: '13px',
                                        borderColor: '#ef4444',
                                        color: '#ef4444',
                                        fontWeight: 600
                                      }}
                                      onClick={() => setShowNoShowConfirm({ sessionId: session.id, auditorId: audId, auditorName: aud?.name || audId })}
                                    >
                                      Mark No-show
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Confirm No-Show Confirmation Modal */}
          {showNoShowConfirm && (
            <div className="modal-overlay animate-fade-in" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
              <div className="modal-card" style={{ background: '#ffffff', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '100%', boxShadow: 'var(--shadow-lg)', animation: 'scaleIn 0.3s ease-out' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--neutral-900)', marginBottom: '12px' }}>Confirm Attendance Audit</h3>
                <p style={{ fontSize: '14.5px', color: 'var(--neutral-600)', lineHeight: 1.5, marginBottom: '24px' }}>
                  Mark <strong>{showNoShowConfirm.auditorName}</strong> as no-show? This will be logged against their record.
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    className="btn-secondary-action"
                    style={{ flex: 1, padding: '12px' }}
                    onClick={() => setShowNoShowConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-submit-session"
                    style={{ flex: 1, padding: '12px', background: '#ef4444', borderColor: '#ef4444', color: '#ffffff' }}
                    onClick={() => {
                      handleMarkNoShow(showNoShowConfirm.sessionId, showNoShowConfirm.auditorId);
                      setShowNoShowConfirm(null);
                    }}
                  >
                    Confirm No-show
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* GENERAL DASHBOARDS FOR OTHER PERSONAS PLACEHOLDERS */}
          {/* ========================================================= */}
          {activeTab === 'dashboard' && currentUser?.role !== 'program_administrator' && currentUser?.role !== 'spoc' && currentUser?.role !== 'auditor' && currentUser?.role !== 'counselor' && !outcomeFormSessionId && (
            <div className="placeholder-card animate-fade-in">
              <div className="placeholder-icon">🛠️</div>
              <h2 className="placeholder-title">{currentUser?.roleLabel} Dashboard</h2>
              <p className="placeholder-desc">
                Welcome to the console, {currentUser?.name}. The operational workflows and reports queue for {currentUser?.roleLabel} are currently under construction and will be deployed in a future phase.
              </p>
              <div style={{ marginTop: '24px', fontSize: '12px', color: 'var(--text-muted)' }}>
                Target Phase: {currentUser?.role === 'auditor' ? 'Phase 4' : 'Phase 5'}
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* WORKSPACE PAGES FOR OTHER PERSONAS TAB PLACEHOLDERS */}
          {/* ========================================================= */}
          {activeTab !== 'dashboard' && currentUser?.role !== 'program_administrator' && currentUser?.role !== 'spoc' && currentUser?.role !== 'auditor' && currentUser?.role !== 'counselor' && !selectedCohort && !wizardState && !sessionFormState && !outcomeFormSessionId && (
            <div className="placeholder-card animate-fade-in">
              <div className="placeholder-icon">🚧</div>
              <h2 className="placeholder-title">Section Under Development</h2>
              <p className="placeholder-desc">
                The "{navItems.find(item => item.id === activeTab)?.label}" workspace view is scheduled for development in a future phase of the GlosCo redesign sequence.
              </p>
              <div style={{ marginTop: '24px', fontSize: '12px', color: 'var(--text-muted)' }}>
                Active Session Role: <strong>{currentUser?.roleLabel}</strong>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <div className="mobile-nav-bar">
        <div className="mobile-nav-items">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`mobile-nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => (item.children ? handleNavigateToChild(item.id, item.children[0].id) : handleNavigate(item.id))}
            >
              <span className="mobile-nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* STUDENT DETAIL DRAWER */}
      {selectedStudentForDrawer && (
        <div
          className="drawer-overlay animate-fade-in"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(2px)',
            zIndex: 1100,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setSelectedStudentForDrawer(null)}
        >
          <div
            className="drawer-card"
            style={{
              background: 'var(--card-bg, #ffffff)',
              width: '100%',
              maxWidth: '700px',
              height: '100%',
              boxShadow: '-4px 0 24px rgba(15, 23, 42, 0.15)',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #e2e8f0)', paddingBottom: '16px', marginBottom: '24px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {selectedStudentForDrawer.name}
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--text-muted)' }}>
                  Grade: {selectedStudentForDrawer.grade || '—'}
                </p>
              </div>
              <button
                onClick={() => setSelectedStudentForDrawer(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  lineHeight: 1,
                  padding: '4px'
                }}
              >
                &times;
              </button>
            </div>

            {/* Evaluation History Section */}
            <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--text-primary)' }}>
              Evaluation History
            </h3>

            {loadingStudentDrawerEvaluations ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                <span className="spinner" style={{ display: 'inline-block', width: '24px', height: '24px', border: '3px solid #e2e8f0', borderTopColor: '#0284c7', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '12px' }}></span>
                <div>Loading evaluations…</div>
              </div>
            ) : studentDrawerEvaluations.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '48px 24px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px dashed #cbd5e1',
                color: 'var(--text-muted)',
                fontStyle: 'italic'
              }}>
                No approved evaluations recorded for this student.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {studentDrawerEvaluations.map(ev => {
                  const roleClass = (ev.roleBadge || ev.role || 'delegate').toLowerCase();
                  const approvedDate = ev.approvedAt || ev.submittedAt;
                  const sessionLabel = [ev.sessionType, ev.cohortName].filter(Boolean).join(' · ');
                  return (
                    <div
                      key={ev.id}
                      style={{
                        background: '#ffffff',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span className={`role-badge ${roleClass}`}>
                              {ev.roleBadge || ev.role}
                            </span>
                            {ev.rating && (
                              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-warning, #eab308)' }}>
                                ★ {ev.rating}/10
                              </span>
                            )}
                            {ev.isRevision && (
                              <span style={{ fontSize: '11px', background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '12px', fontWeight: 600 }}>
                                Revision {ev.revisionNumber}
                              </span>
                            )}
                          </div>
                          {sessionLabel && (
                            <div style={{ fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                              {sessionLabel}
                            </div>
                          )}
                          {ev.auditorName && (
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                              Auditor: {ev.auditorName}
                            </div>
                          )}
                          <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                            {approvedDate
                              ? `Approved: ${new Date(approvedDate).toLocaleDateString()}${ev.approvedByName ? ` by ${ev.approvedByName}` : ''}`
                              : 'Date: —'}
                          </span>
                        </div>
                        <span className="approval-status-badge approved">
                          approved
                        </span>
                      </div>

                      {/* Comments */}
                      {ev.comment && (
                        <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-primary)', fontStyle: 'italic', background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', borderLeft: '3px solid #cbd5e1' }}>
                          "{ev.comment}"
                        </p>
                      )}

                      {/* Keywords */}
                      {((ev.positiveKeywords && ev.positiveKeywords.length > 0) || (ev.negativeKeywords && ev.negativeKeywords.length > 0)) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {(ev.positiveKeywords || []).map((k, i) => (
                            <span key={`pos-${i}`} style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', padding: '3px 8px', borderRadius: '12px', fontWeight: 500 }}>
                              + {k}
                            </span>
                          ))}
                          {(ev.negativeKeywords || []).map((k, i) => (
                            <span key={`neg-${i}`} style={{ fontSize: '11px', background: '#fee2e2', color: '#b91c1c', padding: '3px 8px', borderRadius: '12px', fontWeight: 500 }}>
                              - {k}
                            </span>
                          ))}
                        </div>
                      )}


                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
